import React from 'react';

/**
 * Occasion motif packs — the little objects that say what kind of party it is at
 * a glance: rings on a wedding, party hats on a birthday, pool noodles on a pool
 * party.
 *
 * This is a separate layer from the illustrated scenes in InviteArt.js. A scene
 * belongs to a DESIGN SYSTEM (the look), a motif belongs to an OCCASION (the
 * event). The same Black-Tie Deco card therefore gets champagne coupes for New
 * Year's and a mortarboard for a graduation.
 *
 * Because one pack has to sit on top of all 38 design systems — cream, navy,
 * gold, blush, near-black — every pack:
 *   • takes its colours from props and hard-codes none,
 *   • stays restrained in opacity so it accents rather than shouts,
 *   • keeps the centre of the card completely clear for the text.
 *
 * Props are shared so packs are interchangeable:
 *   color    main line colour        accent   accent colour
 *   accent2  secondary accent        scale    card width in px
 */

// Translucent variant of a hex colour. Shared by every pack below.
export function hexA(hex, a = 1) {
  const h = String(hex || '#888').replace('#', '');
  const f = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(f, 16);
  if (Number.isNaN(n)) return `rgba(136,136,136,${a})`;
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

// ─── Motif pack components ───

// ─── WeddingMotif — interlocking wedding bands with solitaire diamond, pair of church bells hung on a ribbon s
function WeddingMotif({ color, accent, accent2, scale = 300 }) {
  const k = 300 / Math.max(140, Math.min(640, scale));
  const sw = (w) => +(w * (0.72 + 0.28 * k)).toFixed(2);
  const spark = (x, y, s) =>
    `M${x} ${y - s} Q${x + s * 0.2} ${y - s * 0.2} ${x + s} ${y} Q${x + s * 0.2} ${y + s * 0.2} ${x} ${y + s} Q${x - s * 0.2} ${y + s * 0.2} ${x - s} ${y} Q${x - s * 0.2} ${y - s * 0.2} ${x} ${y - s} Z`;

  const sprigLeaves = [
    { y: 8, a: -34, s: -1 },
    { y: 20, a: -26, s: 1 },
    { y: 33, a: -40, s: -1 },
    { y: 46, a: -30, s: 1 },
    { y: 60, a: -36, s: -1 },
    { y: 74, a: -24, s: 1 },
  ];

  const sprigs = [
    { id: "sprig-l", t: "translate(12 148) scale(0.96)", c: color, o: 0.36 },
    { id: "sprig-r", t: "translate(288 156) scale(-0.92 0.92)", c: color, o: 0.32 },
    { id: "sprig-t", t: "translate(84 14) rotate(14) scale(0.44)", c: accent, o: 0.44 },
  ];

  const doves = [
    { id: "dove-a", t: "translate(250 338) rotate(-10) scale(0.84)", c: color, o: 0.5 },
    { id: "dove-b", t: "translate(274 370) rotate(7) scale(0.6)", c: accent, o: 0.4 },
  ];

  const dots = [
    { id: "d1", x: 18, y: 26, r: 1.5 },
    { id: "d3", x: 292, y: 88, r: 1.3 },
    { id: "d4", x: 8, y: 104, r: 1.2 },
    { id: "d6", x: 16, y: 272, r: 1.2 },
    { id: "d7", x: 292, y: 258, r: 1.1 },
    { id: "d8", x: 104, y: 382, r: 1.3 },
  ];

  const sparks = [
    { id: "s1", x: 58, y: 16, s: 4.4 },
    { id: "s2", x: 24, y: 96, s: 3.4 },
    { id: "s3", x: 204, y: 58, s: 3.6 },
    { id: "s4", x: 192, y: 348, s: 3.2 },
  ];

  const petals = [0, 1, 2, 3, 4];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id="wedm-band" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={hexA(accent2, 0.62)} />
          <stop offset="55%" stopColor={hexA(accent, 0.55)} />
          <stop offset="100%" stopColor={hexA(color, 0.6)} />
        </linearGradient>
        <linearGradient id="wedm-bellface" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={hexA(color, 0.3)} />
          <stop offset="100%" stopColor={hexA(accent, 0.1)} />
        </linearGradient>
      </defs>

      <g fill="none" strokeLinecap="round">
        <circle cx="38" cy="52" r="15.5" stroke="url(#wedm-band)" strokeWidth={sw(2.3)} />
        <circle cx="38" cy="52" r="11.6" stroke={hexA(color, 0.22)} strokeWidth={sw(0.7)} />
        <circle cx="60" cy="58" r="13" stroke="url(#wedm-band)" strokeWidth={sw(2.1)} />
        <circle cx="60" cy="58" r="9.6" stroke={hexA(color, 0.2)} strokeWidth={sw(0.7)} />
        <path
          d="M38 30.4 L42.4 34 L38 40.2 L33.6 34 Z"
          fill={hexA(accent2, 0.45)}
          stroke={hexA(accent2, 0.55)}
          strokeWidth={sw(0.8)}
          strokeLinejoin="round"
        />
        <path d="M33.6 34 H42.4 M38 30.4 V40.2" stroke={hexA(accent2, 0.4)} strokeWidth={sw(0.55)} />
      </g>

      <g strokeLinecap="round" strokeLinejoin="round">
        <path
          d="M204 46 C214 32 220 32 226 31.5 C238 30 245 27 250 24.5 C262 29 276 36 288 48"
          fill="none"
          stroke={hexA(accent, 0.3)}
          strokeWidth={sw(1.1)}
        />
        <g transform="translate(226 46) scale(0.66) translate(-250 -46)">
          <path
            d="M250 27 C241 31 237 43 235.6 56.5 C234.7 57.8 234.3 59.4 234.6 60.3 C242.4 63.8 257.6 63.8 265.4 60.3 C265.7 59.4 265.3 57.8 264.4 56.5 C263 43 259 31 250 27 Z"
            fill="url(#wedm-bellface)"
            stroke={hexA(color, 0.42)}
            strokeWidth={sw(2.4)}
          />
          <path d="M240 57 C244 59.6 256 59.6 260 57" fill="none" stroke={hexA(color, 0.26)} strokeWidth={sw(1.4)} />
          <path d="M250 62.6 V64.4" stroke={hexA(color, 0.4)} strokeWidth={sw(1.8)} />
          <circle cx="250" cy="66.6" r="2.6" fill={hexA(color, 0.4)} />
          <circle cx="250" cy="24.4" r="2.5" fill="none" stroke={hexA(color, 0.4)} strokeWidth={sw(2)} />
        </g>
        <g>
          <path
            d="M250 27 C241 31 237 43 235.6 56.5 C234.7 57.8 234.3 59.4 234.6 60.3 C242.4 63.8 257.6 63.8 265.4 60.3 C265.7 59.4 265.3 57.8 264.4 56.5 C263 43 259 31 250 27 Z"
            fill="url(#wedm-bellface)"
            stroke={hexA(color, 0.5)}
            strokeWidth={sw(1.9)}
          />
          <path d="M240.5 56.6 C244.5 59.4 255.5 59.4 259.5 56.6" fill="none" stroke={hexA(color, 0.28)} strokeWidth={sw(1)} />
          <path d="M246.5 36 C245 42 244.2 49 244.4 55" fill="none" stroke={hexA(accent2, 0.28)} strokeWidth={sw(0.8)} />
          <path d="M250 62.8 V64.8" stroke={hexA(color, 0.46)} strokeWidth={sw(1.4)} />
          <circle cx="250" cy="67" r="2.7" fill={hexA(color, 0.46)} />
          <circle cx="250" cy="24.2" r="2.6" fill="none" stroke={hexA(color, 0.46)} strokeWidth={sw(1.5)} />
        </g>
      </g>

      {sprigs.map((sp) => (
        <g key={"wedm-" + sp.id} transform={sp.t}>
          <path d="M20 0 C12 24 12 62 20 92" fill="none" stroke={hexA(sp.c, sp.o + 0.06)} strokeWidth={sw(1.2)} strokeLinecap="round" />
          {sprigLeaves.map((lf, i) => {
            const sx = 20 - 7 * Math.sin((lf.y / 92) * Math.PI);
            return (
              <ellipse
                key={"wedm-" + sp.id + "-lf" + i}
                cx={lf.s * 6.4}
                cy="0"
                rx="6.4"
                ry="2.8"
                fill={hexA(sp.c, sp.o - (i % 2) * 0.07)}
                transform={`translate(${sx.toFixed(2)} ${lf.y}) rotate(${lf.a})`}
              />
            );
          })}
        </g>
      ))}

      <g strokeLinecap="round">
        <g fill="none" stroke={hexA(color, 0.4)} strokeWidth={sw(1.2)}>
          <path d="M54 372 C50 362 44 352 39 345" />
          <path d="M54 372 C54 360 54 350 53 341" />
          <path d="M54 372 C58 362 64 355 70 349" />
        </g>
        <ellipse cx="-6" cy="0" rx="6.2" ry="2.7" fill={hexA(color, 0.34)} transform="translate(45 356) rotate(-42)" />
        <ellipse cx="6" cy="0" rx="6" ry="2.6" fill={hexA(color, 0.3)} transform="translate(63 354) rotate(-28)" />
        <g transform="translate(53 334)">
          {petals.map((p) => (
            <ellipse
              key={"wedm-petal-" + p}
              cx="0"
              cy="-5.4"
              rx="3.1"
              ry="5.4"
              fill={hexA(accent, 0.3)}
              stroke={hexA(accent, 0.42)}
              strokeWidth={sw(0.7)}
              transform={`rotate(${p * 72})`}
            />
          ))}
          <circle cx="0" cy="0" r="1.9" fill={hexA(accent2, 0.55)} />
        </g>
        <circle cx="37" cy="341" r="6.2" fill={hexA(accent2, 0.26)} stroke={hexA(accent2, 0.48)} strokeWidth={sw(1)} />
        <path
          d="M33.4 341 A3.6 3.6 0 1 1 37.4 344.4 A2 2 0 1 1 37 340.2"
          fill="none"
          stroke={hexA(accent2, 0.45)}
          strokeWidth={sw(0.8)}
        />
        <ellipse cx="71" cy="346" rx="3.4" ry="5.2" fill={hexA(accent, 0.28)} stroke={hexA(accent, 0.42)} strokeWidth={sw(0.8)} transform="rotate(24 71 346)" />
        <circle cx="44" cy="329" r="1.3" fill={hexA(color, 0.3)} />
        <circle cx="65" cy="336" r="1.1" fill={hexA(color, 0.28)} />
        <circle cx="30" cy="352" r="1.1" fill={hexA(color, 0.26)} />
        <path d="M47 366 C51 369.5 57 369.5 61 366" fill="none" stroke={hexA(accent2, 0.5)} strokeWidth={sw(2)} />
        <path d="M52 372 C47 380 44 386 42 392" fill="none" stroke={hexA(accent2, 0.38)} strokeWidth={sw(1.4)} />
        <path d="M56 372 C60 380 62 386 63 392" fill="none" stroke={hexA(accent2, 0.34)} strokeWidth={sw(1.4)} />
      </g>

      <g fill="none" stroke={hexA(accent, 0.42)} strokeWidth={sw(1.4)} strokeLinecap="round" strokeLinejoin="round">
        <path d="M150 354 C139 342 125 345 128 354 C130 361 143 360 150 354 Z" fill={hexA(accent, 0.14)} />
        <path d="M150 354 C161 342 175 345 172 354 C170 361 157 360 150 354 Z" fill={hexA(accent, 0.14)} />
        <ellipse cx="150" cy="354" rx="3.4" ry="2.8" fill={hexA(accent, 0.22)} />
        <path d="M147.5 357 C143 367 139 377 133.5 386" strokeWidth={sw(1.9)} />
        <path d="M152.5 357 C156 367 159 378 163.5 387" strokeWidth={sw(1.9)} />
        <path d="M130.6 382.6 L133.5 386 L137 383.4" strokeWidth={sw(1.1)} />
        <path d="M160.4 383.6 L163.5 387 L166.4 383.2" strokeWidth={sw(1.1)} />
      </g>

      {doves.map((dv) => (
        <g key={"wedm-" + dv.id} transform={dv.t}>
          <path
            d="M-19 0 C-21.5 -2.6 -18 -6.4 -13.5 -5.4 C-5.5 -8 4.5 -5.8 11.5 -1.8 C17.5 1.6 22 6.6 25 12.4 C17 10.4 8 8.2 1 5.2 C-6 2.2 -13.5 2.2 -17.5 3.2 C-20.5 3.8 -21.5 1.4 -19 0 Z"
            fill={hexA(dv.c, dv.o - 0.14)}
            stroke={hexA(dv.c, dv.o)}
            strokeWidth={sw(1.2)}
            strokeLinejoin="round"
          />
          <path d="M-19.8 -0.4 L-25.5 0.8 L-19.6 2.4 Z" fill={hexA(accent2, dv.o)} />
          <path
            d="M17 7.5 C23 6.5 29.5 3.5 33.5 -0.5 C32.5 5.5 29.5 11.5 25.5 14.5 Z"
            fill={hexA(dv.c, dv.o - 0.2)}
            stroke={hexA(dv.c, dv.o - 0.06)}
            strokeWidth={sw(1)}
            strokeLinejoin="round"
          />
          <path
            d="M-8 -4 C-4.5 -13.5 5 -19.5 15.5 -19.5 C11.5 -11.5 5.5 -5.5 -1.5 -1.5 Z"
            fill={hexA(dv.c, dv.o - 0.16)}
            stroke={hexA(dv.c, dv.o + 0.04)}
            strokeWidth={sw(1.2)}
            strokeLinejoin="round"
          />
          <path d="M-4 -4.6 C1 -9.2 7 -13.6 13 -16.6" fill="none" stroke={hexA(dv.c, dv.o - 0.18)} strokeWidth={sw(0.8)} strokeLinecap="round" />
          <circle cx="-15.6" cy="-3.1" r="0.8" fill={hexA(color, 0.55)} />
        </g>
      ))}

      {sparks.map((s, i) => (
        <path key={"wedm-" + s.id} d={spark(s.x, s.y, s.s)} fill={hexA(i % 2 ? accent2 : accent, 0.34)} />
      ))}
      {dots.map((d, i) => (
        <circle key={"wedm-" + d.id} cx={d.x} cy={d.y} r={d.r} fill={hexA(i % 3 === 0 ? accent2 : color, 0.3)} />
      ))}
    </svg>
  );
}

// ─── BridalShowerMotif — Bridal veil on a jewelled comb (top-left), Two clinking champagne coupes with bubbles (top
function BridalShowerMotif({ color, accent, accent2, scale = 300 }) {
  const k = Math.max(0.72, Math.min(1.3, 300 / scale));
  const sw = 1.1 * k;
  const thin = 0.7 * k;

  const line = hexA(color, 0.52);
  const soft = hexA(color, 0.33);
  const petal = hexA(accent, 0.32);
  const petalLine = hexA(accent, 0.55);
  const pearlFill = hexA(accent2, 0.3);
  const pearlLine = hexA(accent2, 0.5);
  const glass = hexA(accent2, 0.16);

  const pearls = [0, 1, 2, 3, 4, 5, 6].map(function (i) {
    const t = i / 6;
    return {
      id: 'p' + i,
      x: 96 + 116 * t,
      y: 376 + 40 * t * (1 - t),
      r: 2.4 - 0.5 * Math.abs(t - 0.5)
    };
  });

  const sparks = [
    { id: 'sp0', x: 96, y: 102, s: 4.6 },
    { id: 'sp1', x: 272, y: 24, s: 3.6 },
    { id: 'sp2', x: 251, y: 151, s: 4.2 },
    { id: 'sp3', x: 46, y: 117, s: 3.1 },
    { id: 'sp4', x: 285, y: 197, s: 2.7 }
  ];
  const sparkD =
    'M 0 -1 C 0.14 -0.32 0.32 -0.14 1 0 C 0.32 0.14 0.14 0.32 0 1 C -0.14 0.32 -0.32 0.14 -1 0 C -0.32 -0.14 -0.14 -0.32 0 -1 Z';

  const teeth = [
    { id: 't0', d: 'M 36 23 L 35 30' },
    { id: 't1', d: 'M 42 21 L 42 29' },
    { id: 't2', d: 'M 50 21 L 51 29' },
    { id: 't3', d: 'M 56 23 L 57 30' }
  ];

  const coupes = [
    { id: 'c0', t: 'translate(228 44) rotate(-15)' },
    { id: 'c1', t: 'translate(258 44) rotate(13)' }
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id="brid-shwr-veil-grad" x1="0" y1="0" x2="0.35" y2="1">
          <stop offset="0%" stopColor={hexA(accent2, 0.22)} />
          <stop offset="100%" stopColor={hexA(color, 0.03)} />
        </linearGradient>
      </defs>

      <g
        fill="none"
        stroke={line}
        strokeWidth={sw}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* ---- top-left: bridal veil on a comb ---- */}
        <path
          d="M 38 26 C 26 44 16 68 17 90 C 20 96 28 100 40 101 C 56 102 70 97 76 90 C 76 66 66 42 55 26 Z"
          fill="url(#brid-shwr-veil-grad)"
        />
        <path
          d="M 41 27 C 32 42 27 58 29 72 C 40 79 55 77 64 70 C 63 51 55 36 50 27 Z"
          stroke={soft}
          strokeWidth={thin}
        />
        <path d="M 45 30 C 39 50 35 72 36 96" stroke={soft} strokeWidth={thin} />
        <path d="M 52 30 C 55 52 58 74 57 95" stroke={soft} strokeWidth={thin} />
        <path d="M 17 90 C 34 98 60 97 76 90" stroke={soft} strokeWidth={thin} />
        <path d="M 34 24 C 38 13 54 13 58 24" />
        {teeth.map(function (t) {
          return <path key={t.id} d={t.d} strokeWidth={thin} />;
        })}
        <circle cx="46" cy="16" r="2.6" fill={petal} stroke={petalLine} strokeWidth={thin} />
        <circle cx="84" cy="34" r="2.1" fill={pearlFill} stroke={pearlLine} strokeWidth={thin} />
        <circle cx="89" cy="49" r="1.7" fill={pearlFill} stroke={pearlLine} strokeWidth={thin} />
        <circle cx="82" cy="61" r="1.3" fill={pearlFill} stroke="none" />

        {/* ---- top-right: clinking champagne coupes ---- */}
        {coupes.map(function (c) {
          return (
            <g key={c.id} transform={c.t}>
              <path
                d="M -13 0 L 13 0 C 12 10 7 15 0 15 C -7 15 -12 10 -13 0 Z"
                fill={glass}
              />
              <ellipse cx="0" cy="0" rx="13" ry="2.4" />
              <path d="M -11 3 C -6 6.5 6 6.5 11 3" stroke={soft} strokeWidth={thin} />
              <path d="M 0 15 L 0 31" />
              <path d="M -8 31.5 C -5 29.5 5 29.5 8 31.5" />
            </g>
          );
        })}
        <circle cx="240" cy="25" r="1.7" fill={pearlFill} stroke="none" />
        <circle cx="247" cy="18" r="1.2" fill={pearlFill} stroke="none" />

        {/* ---- right edge: solitaire ring ---- */}
        <circle cx="268" cy="180" r="12.5" strokeWidth={sw * 1.5} />
        <path d="M 262 164 L 263 158" strokeWidth={thin} />
        <path d="M 274 164 L 273 158" strokeWidth={thin} />
        <path
          d="M 258 156 L 278 156 L 273.5 148 L 262.5 148 Z"
          fill={hexA(accent, 0.2)}
        />
        <path d="M 264 148 L 261.5 156" strokeWidth={thin} stroke={soft} />
        <path d="M 272 148 L 274.5 156" strokeWidth={thin} stroke={soft} />
        <path d="M 258 156 L 268 168 L 278 156 Z" fill={hexA(accent2, 0.14)} />
        <path d="M 263 156 L 268 168" strokeWidth={thin} stroke={soft} />
        <path d="M 273 156 L 268 168" strokeWidth={thin} stroke={soft} />

        {/* ---- left edge: floral sprig ---- */}
        <path d="M 36 240 C 30 214 26 186 30 152" />
        <path
          d="M 29 206 C 20 204 13 196 14 188 C 23 189 29 197 29 206 Z"
          fill={hexA(accent2, 0.16)}
          strokeWidth={thin}
        />
        <path
          d="M 28 192 C 36 189 42 182 41 175 C 33 177 28 184 28 192 Z"
          fill={hexA(accent2, 0.16)}
          strokeWidth={thin}
        />
        <path
          d="M 28 176 C 21 173 16 166 18 159 C 25 162 29 169 28 176 Z"
          fill={hexA(accent2, 0.16)}
          strokeWidth={thin}
        />
        <path d="M 31 167 L 38 162" strokeWidth={thin} />
        <path
          d="M 38 162 C 34 158 34 152 38 149 C 42 152 42 158 38 162 Z"
          fill={petal}
          strokeWidth={thin}
          stroke={petalLine}
        />
        <path
          d="M 30 152 C 26 148 26 141 30 138 C 34 141 34 148 30 152 Z"
          fill={petal}
          strokeWidth={thin}
          stroke={petalLine}
        />
        <circle cx="30" cy="137" r="2.4" fill={petalLine} stroke="none" />

        {/* ---- bottom-left: tiered cake ---- */}
        <rect x="26" y="362" width="52" height="28" rx="2" fill={hexA(color, 0.06)} />
        <rect x="33" y="340" width="38" height="22" rx="2" fill={hexA(color, 0.06)} />
        <rect x="39" y="322" width="26" height="18" rx="2" fill={hexA(color, 0.06)} />
        <path
          d="M 26 369 q 6.5 7 13 0 q 6.5 7 13 0 q 6.5 7 13 0 q 6.5 7 13 0"
          stroke={soft}
          strokeWidth={thin}
        />
        <path
          d="M 33 346 q 6.33 6 12.66 0 q 6.34 6 12.67 0 q 6.33 6 12.67 0"
          stroke={soft}
          strokeWidth={thin}
        />
        <path d="M 39 328 q 6.5 5.5 13 0 q 6.5 5.5 13 0" stroke={soft} strokeWidth={thin} />
        <path d="M 20 391 C 26 394 78 394 84 391" />
        <path d="M 52 322 L 52 316" strokeWidth={thin} />
        <path d="M 52 318 C 48 318 45 316 45 313" strokeWidth={thin} stroke={soft} />
        <path d="M 52 318 C 56 318 59 316 59 313" strokeWidth={thin} stroke={soft} />
        <circle cx="52" cy="313" r="3.1" fill={petal} stroke={petalLine} strokeWidth={thin} />

        {/* ---- bottom-right: tied bouquet ---- */}
        <path d="M 254 380 C 250 366 244 352 240 341" />
        <path d="M 254 380 C 254 364 254 348 254 335" />
        <path d="M 254 380 C 258 366 265 352 269 343" />
        <path
          d="M 246 356 C 236 352 228 354 224 360 C 232 364 242 362 246 356 Z"
          fill={hexA(accent2, 0.16)}
          strokeWidth={thin}
        />
        <path
          d="M 262 352 C 272 348 280 350 284 356 C 276 360 266 358 262 352 Z"
          fill={hexA(accent2, 0.16)}
          strokeWidth={thin}
        />
        <circle cx="238" cy="334" r="7.6" fill={petal} stroke={petalLine} strokeWidth={thin} />
        <circle cx="254" cy="326" r="8.8" fill={petal} stroke={petalLine} strokeWidth={thin} />
        <circle cx="270" cy="336" r="6.8" fill={petal} stroke={petalLine} strokeWidth={thin} />
        <path d="M 233 336 C 236 329 241 329 243 334" stroke={petalLine} strokeWidth={thin} />
        <path d="M 248 328 C 251 320 258 320 260 327" stroke={petalLine} strokeWidth={thin} />
        <path d="M 266 338 C 268 332 273 332 274 337" stroke={petalLine} strokeWidth={thin} />
        <path d="M 248 371 C 252 373.5 257 373.5 261 371" />
        <path d="M 247 377 C 252 379.5 257 379.5 262 377" />
        <path d="M 250 380 C 248 386 246 389 243 392" strokeWidth={thin} />
        <path d="M 258 380 C 260 386 262 389 265 392" strokeWidth={thin} />

        {/* ---- bottom centre: pearl swag with ribbon bow ---- */}
        <path d="M 96 376 Q 154 396 212 376" stroke={pearlLine} strokeWidth={thin} />
        {pearls.map(function (p) {
          return (
            <circle
              key={p.id}
              cx={p.x}
              cy={p.y}
              r={p.r}
              fill={pearlFill}
              stroke={pearlLine}
              strokeWidth={thin}
            />
          );
        })}
        <path
          d="M 154 386 C 145 378 136 377 134 382 C 132 387 143 389 154 386 Z"
          fill={hexA(accent, 0.18)}
        />
        <path
          d="M 154 386 C 163 378 172 377 174 382 C 176 387 165 389 154 386 Z"
          fill={hexA(accent, 0.18)}
        />
        <path d="M 151 388 C 148 392 147 395 145 397" strokeWidth={thin} />
        <path d="M 157 388 C 160 392 161 395 163 397" strokeWidth={thin} />
        <ellipse cx="154" cy="386" rx="2.6" ry="2.4" fill={petalLine} stroke="none" />

        {/* ---- edge sparkle punctuation ---- */}
        {sparks.map(function (s) {
          return (
            <path
              key={s.id}
              d={sparkD}
              transform={'translate(' + s.x + ' ' + s.y + ') scale(' + s.s + ')'}
              fill={hexA(accent, 0.42)}
              stroke="none"
            />
          );
        })}
      </g>
    </svg>
  );
}

// ─── BachelorMotif — whiskey tumbler with ice cube and poured measure (top-left), angled cigar with band, lit e
function BachelorMotif({ color, accent, accent2, scale = 300 }) {
  const a2 = accent2 || accent;
  const sw = Math.max(0.7, Math.min(1.7, 1.3 * Math.sqrt(300 / Math.max(60, scale))));

  const SPADE =
    "M0,-6.6 C2.2,-3.1 6,-0.7 6,2.1 C6,4.5 4.1,5.9 2.3,5.9 C1.2,5.9 0.4,5.4 0,4.6 C-0.4,5.4 -1.2,5.9 -2.3,5.9 C-4.1,5.9 -6,4.5 -6,2.1 C-6,-0.7 -2.2,-3.1 0,-6.6 Z M-2.7,8.5 C-1.1,7.5 -0.5,5.8 -0.35,4.1 L0.35,4.1 C0.5,5.8 1.1,7.5 2.7,8.5 Z";
  const HEART =
    "M0,7.8 C-1.7,5.4 -6,2.3 -6,-1.6 C-6,-4.5 -3.9,-6.1 -2,-6.1 C-0.9,-6.1 -0.25,-5.4 0,-4.7 C0.25,-5.4 0.9,-6.1 2,-6.1 C3.9,-6.1 6,-4.5 6,-1.6 C6,2.3 1.7,5.4 0,7.8 Z";
  const DIAMOND = "M0,-7.8 L5.2,0 L0,7.8 L-5.2,0 Z";

  const topSuits = [
    { id: "a", x: 116, d: SPADE, c: color, o: 0.52 },
    { id: "b", x: 139, d: HEART, c: a2, o: 0.46 },
    { id: "c", x: 185, d: DIAMOND, c: a2, o: 0.46 }
  ];

  const railSegs = [[92, 118], [142, 188], [212, 258], [282, 308]];
  const rails = [17, 283];

  const edgePips = [
    { id: "l1", x: 17, y: 130, d: SPADE, c: color, o: 0.42 },
    { id: "l2", x: 17, y: 200, d: DIAMOND, c: a2, o: 0.4 },
    { id: "l3", x: 17, y: 270, d: HEART, c: a2, o: 0.4 },
    { id: "r1", x: 283, y: 130, d: HEART, c: a2, o: 0.4 },
    { id: "r2", x: 283, y: 200, d: SPADE, c: color, o: 0.42 },
    { id: "r3", x: 283, y: 270, d: DIAMOND, c: a2, o: 0.4 }
  ];

  const dieAPips = [[-6, -6], [6, -6], [0, 0], [-6, 6], [6, 6]];
  const dieBPips = [[-4.6, -4.6], [0, 0], [4.6, 4.6]];

  const wing =
    "M-7.5,-4.2 C-14,-8.2 -24,-13.2 -30,-13.2 C-34.2,-13.2 -35.8,-9.4 -35.8,0 C-35.8,9.4 -34.2,13.2 -30,13.2 C-24,13.2 -14,8.2 -7.5,4.2 Z";

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id="bachMotifPour" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={hexA(accent, 0.5)} />
          <stop offset="1" stopColor={hexA(accent, 0.26)} />
        </linearGradient>
        <linearGradient
          id="bachMotifSmoke"
          gradientUnits="userSpaceOnUse"
          x1="277"
          y1="36"
          x2="277"
          y2="4"
        >
          <stop offset="0" stopColor={hexA(color, 0.42)} />
          <stop offset="1" stopColor={hexA(color, 0)} />
        </linearGradient>
      </defs>

      <g strokeLinecap="round" strokeLinejoin="round">
        {/* ---- top left: whiskey tumbler ---- */}
        <path
          d="M21.4,41 C21.4,44.4 46.6,44.4 46.6,41 L45.4,56.5 Q45,59.5 42,59.5 L26,59.5 Q23,59.5 22.6,56.5 Z"
          fill="url(#bachMotifPour)"
        />
        <path
          d="M19,26 L22.5,57 Q23,60 26,60 L42,60 Q45,60 45.5,57 L49,26"
          fill="none"
          stroke={hexA(color, 0.55)}
          strokeWidth={sw}
        />
        <ellipse
          cx="34"
          cy="26"
          rx="15"
          ry="3.2"
          fill="none"
          stroke={hexA(color, 0.5)}
          strokeWidth={sw}
        />
        <ellipse
          cx="34"
          cy="41"
          rx="12.6"
          ry="2.4"
          fill="none"
          stroke={hexA(accent, 0.4)}
          strokeWidth={sw * 0.8}
        />
        <line
          x1="23.4"
          y1="54.5"
          x2="44.6"
          y2="54.5"
          stroke={hexA(color, 0.32)}
          strokeWidth={sw}
        />
        <g transform="translate(34 45) rotate(-14)">
          <rect
            x="-5.5"
            y="-5.5"
            width="11"
            height="11"
            rx="1.8"
            fill={hexA(color, 0.08)}
            stroke={hexA(color, 0.4)}
            strokeWidth={sw * 0.85}
          />
          <line
            x1="-3.2"
            y1="-2.4"
            x2="0.6"
            y2="-2.4"
            stroke={hexA(color, 0.3)}
            strokeWidth={sw * 0.7}
          />
        </g>

        {/* ---- top right: cigar + smoke ---- */}
        <g transform="rotate(-24 256 46)">
          <rect
            x="230"
            y="41.5"
            width="52"
            height="9"
            rx="4.5"
            fill={hexA(color, 0.18)}
            stroke={hexA(color, 0.52)}
            strokeWidth={sw}
          />
          <rect x="238.5" y="41.8" width="7.5" height="8.4" fill={hexA(accent, 0.5)} />
          <path
            d="M274,41.8 L277.5,41.8 A4.3,4.3 0 0 1 277.5,50.2 L274,50.2 Z"
            fill={hexA(accent, 0.55)}
          />
          <line
            x1="270"
            y1="42.2"
            x2="270"
            y2="49.8"
            stroke={hexA(color, 0.38)}
            strokeWidth={sw * 0.8}
          />
        </g>
        <path
          d="M277,34 C283,28 273,24 279,17 C283,12.5 279.5,9 277.5,6"
          fill="none"
          stroke="url(#bachMotifSmoke)"
          strokeWidth={sw * 1.05}
        />

        {/* ---- top centre: card suits between deco rules ---- */}
        <line x1="62" y1="22" x2="100" y2="22" stroke={hexA(color, 0.34)} strokeWidth={sw * 0.8} />
        <line x1="200" y1="22" x2="238" y2="22" stroke={hexA(color, 0.34)} strokeWidth={sw * 0.8} />
        {topSuits.map((s) => (
          <path
            key={`bach-top-suit-${s.id}`}
            d={s.d}
            fill={hexA(s.c, s.o)}
            transform={`translate(${s.x} 22) scale(0.92)`}
          />
        ))}
        <g transform="translate(162 22) scale(0.92)" fill={hexA(color, 0.46)}>
          <circle cx="0" cy="-3.1" r="3.3" />
          <circle cx="-3.8" cy="2.3" r="3.3" />
          <circle cx="3.8" cy="2.3" r="3.3" />
          <path d="M-2.6,8.4 C-1.1,7.4 -0.5,5.6 -0.4,2.6 L0.4,2.6 C0.5,5.6 1.1,7.4 2.6,8.4 Z" />
        </g>

        {/* ---- side rails with suit pips ---- */}
        {rails.map((rx, ri) => (
          <g key={`bach-rail-${ri}`}>
            {railSegs.map((seg, si) => (
              <line
                key={`bach-rail-seg-${ri}-${si}`}
                x1={rx}
                y1={seg[0]}
                x2={rx}
                y2={seg[1]}
                stroke={hexA(color, 0.3)}
                strokeWidth={sw * 0.75}
              />
            ))}
          </g>
        ))}
        {edgePips.map((p) => (
          <path
            key={`bach-edge-pip-${p.id}`}
            d={p.d}
            fill={hexA(p.c, p.o)}
            transform={`translate(${p.x} ${p.y}) scale(0.72)`}
          />
        ))}

        {/* ---- bottom left: dice ---- */}
        <g transform="translate(31 350) rotate(-8)">
          <rect
            x="-12"
            y="-12"
            width="24"
            height="24"
            rx="5"
            fill={hexA(color, 0.12)}
            stroke={hexA(color, 0.52)}
            strokeWidth={sw}
          />
          {dieAPips.map((p, i) => (
            <circle
              key={`bach-die-a-${i}`}
              cx={p[0]}
              cy={p[1]}
              r="2"
              fill={hexA(a2, 0.55)}
            />
          ))}
        </g>
        <g transform="translate(55 367) rotate(14)">
          <rect
            x="-9.5"
            y="-9.5"
            width="19"
            height="19"
            rx="4"
            fill={hexA(color, 0.12)}
            stroke={hexA(color, 0.48)}
            strokeWidth={sw}
          />
          {dieBPips.map((p, i) => (
            <circle
              key={`bach-die-b-${i}`}
              cx={p[0]}
              cy={p[1]}
              r="1.7"
              fill={hexA(a2, 0.5)}
            />
          ))}
        </g>

        {/* ---- bottom centre: bow tie ---- */}
        <g transform="translate(150 362)">
          <path
            d={wing}
            fill={hexA(color, 0.16)}
            stroke={hexA(color, 0.52)}
            strokeWidth={sw}
          />
          <path
            d={wing}
            transform="scale(-1 1)"
            fill={hexA(color, 0.16)}
            stroke={hexA(color, 0.52)}
            strokeWidth={sw}
          />
          <path
            d="M-10,-3 C-17,-5.6 -25,-9 -29.5,-10"
            fill="none"
            stroke={hexA(color, 0.3)}
            strokeWidth={sw * 0.75}
          />
          <path
            d="M-10,3 C-17,5.6 -25,9 -29.5,10"
            fill="none"
            stroke={hexA(color, 0.3)}
            strokeWidth={sw * 0.75}
          />
          <path
            d="M-10,-3 C-17,-5.6 -25,-9 -29.5,-10"
            transform="scale(-1 1)"
            fill="none"
            stroke={hexA(color, 0.3)}
            strokeWidth={sw * 0.75}
          />
          <path
            d="M-10,3 C-17,5.6 -25,9 -29.5,10"
            transform="scale(-1 1)"
            fill="none"
            stroke={hexA(color, 0.3)}
            strokeWidth={sw * 0.75}
          />
          <rect
            x="-8"
            y="-6.8"
            width="16"
            height="13.6"
            rx="3.2"
            fill={hexA(accent, 0.3)}
            stroke={hexA(color, 0.52)}
            strokeWidth={sw}
          />
        </g>

        {/* ---- bottom right: poker chip ---- */}
        <g transform="translate(264 355)">
          <circle
            r="17"
            fill={hexA(color, 0.1)}
            stroke={hexA(color, 0.48)}
            strokeWidth={sw}
          />
          <circle
            r="10.5"
            fill="none"
            stroke={hexA(color, 0.38)}
            strokeWidth={sw * 0.8}
          />
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <rect
              key={`bach-chip-spot-${i}`}
              x="-3"
              y="-17.4"
              width="6"
              height="6.9"
              rx="1.5"
              fill={hexA(a2, 0.42)}
              transform={`rotate(${i * 60})`}
            />
          ))}
          <path d={SPADE} fill={hexA(accent, 0.5)} transform="scale(0.62)" />
        </g>
      </g>
    </svg>
  );
}

// ─── BirthdayMotif — balloon bunch of three on curling strings (top-left), striped party hat with a pom-pom (to
function BirthdayMotif({ color, accent, accent2, scale = 300 }) {
  const k = Math.max(0.6, Math.min(1.6, Math.sqrt(300 / scale)));
  const sw = 1.45 * k;
  const sf = 0.85 * k;

  const balloons = [
    { cx: 24, cy: 38, r: 12.4, tint: accent, tail: 9 },
    { cx: 50.5, cy: 27, r: 10.8, tint: color, tail: -7 },
    { cx: 37.5, cy: 61, r: 9.4, tint: accent2, tail: 4 }
  ];

  const confetti = [
    { x: 96, y: 34, a: -24, t: 0 },
    { x: 118, y: 53, a: 0, t: 1 },
    { x: 140, y: 26, a: 38, t: 2 },
    { x: 164, y: 47, a: -12, t: 0 },
    { x: 187, y: 30, a: 20, t: 1 },
    { x: 207, y: 56, a: -34, t: 0 },
    { x: 100, y: 366, a: 16, t: 0 },
    { x: 125, y: 350, a: -30, t: 1 },
    { x: 150, y: 373, a: 0, t: 2 },
    { x: 176, y: 352, a: 26, t: 0 },
    { x: 199, y: 369, a: -16, t: 1 }
  ];
  const cTints = [accent, color, accent2];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <radialGradient id="bdayMotif-flameGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={hexA(accent, 0.3)} />
          <stop offset="55%" stopColor={hexA(accent, 0.1)} />
          <stop offset="100%" stopColor={hexA(accent, 0)} />
        </radialGradient>
      </defs>

      <g fill="none" strokeLinecap="round" strokeLinejoin="round">
        {/* ---- top-left : balloon bunch ---- */}
        {balloons.map((b, i) => {
          const ry = b.r * 1.16;
          const ky = b.cy + ry;
          return (
            <g key={"bday-balloon-" + i}>
              <path
                d={`M ${b.cx} ${b.cy - ry} C ${b.cx + b.r * 1.02} ${b.cy - ry * 0.72} ${b.cx + b.r * 1.06} ${b.cy + ry * 0.42} ${b.cx} ${ky} C ${b.cx - b.r * 1.06} ${b.cy + ry * 0.42} ${b.cx - b.r * 1.02} ${b.cy - ry * 0.72} ${b.cx} ${b.cy - ry} Z`}
                fill={hexA(b.tint, 0.14)}
                stroke={hexA(b.tint, 0.58)}
                strokeWidth={sw}
              />
              <path
                d={`M ${b.cx - 2.4} ${ky - 0.8} L ${b.cx + 2.4} ${ky - 0.8} L ${b.cx} ${ky + 3.6} Z`}
                fill={hexA(b.tint, 0.4)}
                stroke={hexA(b.tint, 0.5)}
                strokeWidth={sf}
              />
              <path
                d={`M ${b.cx - b.r * 0.52} ${b.cy - b.r * 0.44} Q ${b.cx - b.r * 0.8} ${b.cy + b.r * 0.1} ${b.cx - b.r * 0.32} ${b.cy + b.r * 0.62}`}
                stroke={hexA(b.tint, 0.33)}
                strokeWidth={sf}
              />
              <path
                d={`M ${b.cx} ${ky + 3.6} C ${b.cx + b.tail} ${ky + (104 - ky) * 0.42} ${40 - b.tail * 0.55} ${ky + (104 - ky) * 0.76} 40 104`}
                stroke={hexA(color, 0.34)}
                strokeWidth={sf}
              />
            </g>
          );
        })}

        {/* ---- top-right : striped party hat with pom-pom ---- */}
        <g transform="rotate(-9 258 44)">
          <path
            d="M 258 24 L 275 64 Q 258 70 241 64 Z"
            fill={hexA(accent2, 0.13)}
            stroke={hexA(color, 0.58)}
            strokeWidth={sw}
          />
          <path d="M 250.8 41 Q 258 43.6 265.2 41" stroke={hexA(accent, 0.42)} strokeWidth={sf} />
          <path d="M 246.5 51 Q 258 54 269.5 51" stroke={hexA(accent, 0.42)} strokeWidth={sf} />
          <path d="M 242.7 60 Q 258 63.4 273.3 60" stroke={hexA(accent, 0.42)} strokeWidth={sf} />
          <circle cx="258" cy="20.6" r="4.6" fill={hexA(accent, 0.24)} stroke={hexA(color, 0.55)} strokeWidth={sf} />
          <path d="M 252.6 15.4 L 250.7 12.6" stroke={hexA(accent, 0.34)} strokeWidth={sf} />
          <path d="M 263.4 15.4 L 265.3 12.6" stroke={hexA(accent, 0.34)} strokeWidth={sf} />
        </g>

        {/* ---- confetti / sparkle scatter (top + bottom bands) ---- */}
        {confetti.map((c, i) => {
          const tint = cTints[i % 3];
          if (c.t === 1) {
            return <circle key={"bday-conf-" + i} cx={c.x} cy={c.y} r="1.7" fill={hexA(tint, 0.4)} />;
          }
          if (c.t === 2) {
            return (
              <path
                key={"bday-conf-" + i}
                d={`M ${c.x} ${c.y - 4.4} Q ${c.x} ${c.y - 1.1} ${c.x + 4.4} ${c.y} Q ${c.x} ${c.y + 1.1} ${c.x} ${c.y + 4.4} Q ${c.x} ${c.y + 1.1} ${c.x - 4.4} ${c.y} Q ${c.x} ${c.y - 1.1} ${c.x} ${c.y - 4.4} Z`}
                fill={hexA(tint, 0.38)}
              />
            );
          }
          return (
            <rect
              key={"bday-conf-" + i}
              x={c.x - 3}
              y={c.y - 1.1}
              width="6"
              height="2.2"
              rx="1.1"
              fill={hexA(tint, 0.36)}
              transform={`rotate(${c.a} ${c.x} ${c.y})`}
            />
          );
        })}

        {/* ---- left margin : lit taper candle ---- */}
        <g>
          <circle cx="29.3" cy="152" r="11.5" fill="url(#bdayMotif-flameGlow)" />
          <rect x="24" y="166" width="10.6" height="72" rx="2.6" fill={hexA(accent2, 0.12)} stroke={hexA(color, 0.55)} strokeWidth={sw} />
          <path d="M 24.4 190 L 34.2 184" stroke={hexA(accent, 0.4)} strokeWidth={sf} />
          <path d="M 24.4 206 L 34.2 200" stroke={hexA(accent, 0.4)} strokeWidth={sf} />
          <path d="M 24.4 222 L 34.2 216" stroke={hexA(accent, 0.4)} strokeWidth={sf} />
          <path d="M 34.4 172 C 37.2 178 36.2 186 34.4 190" stroke={hexA(color, 0.3)} strokeWidth={sf} />
          <path d="M 29.3 166 L 29.3 160" stroke={hexA(color, 0.5)} strokeWidth={sf} />
          <path
            d="M 29.3 159.4 C 25.4 153.6 26.5 147.4 29.3 142.6 C 32.1 147.4 33.2 153.6 29.3 159.4 Z"
            fill={hexA(accent, 0.22)}
            stroke={hexA(accent, 0.55)}
            strokeWidth={sf}
          />
          <path d="M 29.3 156.6 C 27.7 153.4 28.3 150.4 29.3 147.8 C 30.3 150.4 30.9 153.4 29.3 156.6 Z" fill={hexA(accent, 0.3)} />
        </g>

        {/* ---- right margin : curling streamers ---- */}
        <path
          d="M 274 148 C 288 163 261 176 271.5 192 C 282 208 260.5 220 270.5 236 C 276.5 245 274.5 250 270 254"
          stroke={hexA(accent, 0.45)}
          strokeWidth={sw}
        />
        <path
          d="M 284 158 C 292 172 272.5 182 280 198 C 287.5 214 270.5 224 277.5 239"
          stroke={hexA(accent2, 0.3)}
          strokeWidth={sf}
        />
        <circle cx="265.5" cy="145" r="1.5" fill={hexA(accent, 0.36)} />
        <circle cx="284" cy="250" r="1.7" fill={hexA(accent2, 0.32)} />

        {/* ---- bottom-left : wrapped gift ---- */}
        <g>
          <rect x="27" y="351" width="46" height="31" rx="2" fill={hexA(accent2, 0.12)} stroke={hexA(color, 0.56)} strokeWidth={sw} />
          <rect x="23" y="340" width="54" height="11" rx="2" fill={hexA(accent2, 0.16)} stroke={hexA(color, 0.56)} strokeWidth={sw} />
          <path d="M 50 351 L 50 382" stroke={hexA(accent, 0.46)} strokeWidth={sw} />
          <path d="M 50 340 L 50 351" stroke={hexA(accent, 0.46)} strokeWidth={sw} />
          <path d="M 50 339.5 C 41.5 331 33.8 333.2 37.4 338.8 C 39.6 342.2 45 341 50 339.5 Z" fill={hexA(accent, 0.16)} stroke={hexA(accent, 0.5)} strokeWidth={sf} />
          <path d="M 50 339.5 C 58.5 331 66.2 333.2 62.6 338.8 C 60.4 342.2 55 341 50 339.5 Z" fill={hexA(accent, 0.16)} stroke={hexA(accent, 0.5)} strokeWidth={sf} />
          <circle cx="50" cy="340.4" r="2.2" fill={hexA(accent, 0.34)} />
          <circle cx="37" cy="364" r="1.4" fill={hexA(color, 0.3)} />
          <circle cx="63" cy="371" r="1.4" fill={hexA(color, 0.3)} />
        </g>

        {/* ---- bottom-right : slice of layered cake ---- */}
        <g>
          <circle cx="265.5" cy="303.5" r="9.5" fill="url(#bdayMotif-flameGlow)" />
          <path d="M 220 382 L 286 382 L 286 338 L 244 338 Z" fill={hexA(accent2, 0.11)} stroke={hexA(color, 0.58)} strokeWidth={sw} />
          <path d="M 236.4 352 L 286 352 L 286 366 L 228.7 366 Z" fill={hexA(accent, 0.16)} stroke="none" />
          <path d="M 236.4 352 L 286 352" stroke={hexA(color, 0.4)} strokeWidth={sf} />
          <path d="M 228.7 366 L 286 366" stroke={hexA(color, 0.4)} strokeWidth={sf} />
          <path
            d="M 244 338 Q 251 329.4 258 338 Q 265 329.4 272 338 Q 279 329.4 286 338"
            fill={hexA(accent, 0.14)}
            stroke={hexA(color, 0.5)}
            strokeWidth={sf}
          />
          <rect x="263.5" y="314" width="4.2" height="20" rx="1.4" fill={hexA(accent2, 0.18)} stroke={hexA(color, 0.5)} strokeWidth={sf} />
          <path d="M 263.7 322 L 267.5 318.6" stroke={hexA(accent, 0.42)} strokeWidth={sf} />
          <path d="M 265.6 314 L 265.6 310" stroke={hexA(color, 0.48)} strokeWidth={sf} />
          <path
            d="M 265.6 309.6 C 262.7 305.2 263.5 300.8 265.6 297.2 C 267.7 300.8 268.5 305.2 265.6 309.6 Z"
            fill={hexA(accent, 0.24)}
            stroke={hexA(accent, 0.55)}
            strokeWidth={sf}
          />
          <path d="M 210 383.4 Q 252 390.6 296 383.4" stroke={hexA(color, 0.34)} strokeWidth={sf} />
          <circle cx="211" cy="379" r="1.5" fill={hexA(accent2, 0.3)} />
        </g>
      </g>
    </svg>
  );
}

// ─── FirstBirthdayMotif — triangle bunting garland strung across the top edge, smash cake on a pedestal cake stand w
function FirstBirthdayMotif({ color, accent, accent2, scale = 300 }) {
  const a1 = accent || color;
  const a2 = accent2 || accent || color;
  const sw = Math.max(1, Math.min(2.1, Math.round(1.35 * (300 / (scale || 300)) * 100) / 100));

  const ink = hexA(color, 0.5);
  const inkSoft = hexA(color, 0.32);
  const tint1 = hexA(a1, 0.22);
  const tint2 = hexA(a2, 0.2);
  const tint3 = hexA(color, 0.09);
  const dotA = hexA(a1, 0.42);
  const dotB = hexA(a2, 0.42);

  // --- bunting: pennants sampled along a quadratic sag -------------------
  const B0 = [14, 18];
  const B1 = [150, 50];
  const B2 = [286, 18];
  const flags = [];
  for (let i = 0; i < 11; i++) {
    const t = 0.05 + i * 0.09;
    const mt = 1 - t;
    flags.push({
      i,
      x: mt * mt * B0[0] + 2 * mt * t * B1[0] + t * t * B2[0],
      y: mt * mt * B0[1] + 2 * mt * t * B1[1] + t * t * B2[1],
      a:
        (Math.atan2(
          2 * mt * (B1[1] - B0[1]) + 2 * t * (B2[1] - B1[1]),
          2 * mt * (B1[0] - B0[0]) + 2 * t * (B2[0] - B1[0])
        ) *
          180) /
        Math.PI
    });
  }

  // --- scalloped icing drip across the top of the cake -------------------
  let icing = 'M -19 -25 H 19 V -16.6';
  for (let i = 0; i < 8; i++) icing += ' a 2.375 2.375 0 0 1 -4.75 0';
  icing += ' Z';

  // --- confetti sparkles, edges only ------------------------------------
  const sparks = [
    [26, 158, 0.9, 0],
    [38, 206, 0.62, 1],
    [22, 254, 0.78, 2],
    [276, 150, 0.7, 0],
    [262, 198, 0.95, 1],
    [280, 252, 0.6, 2],
    [150, 372, 0.72, 0],
    [172, 344, 0.55, 1],
    [86, 338, 0.6, 2],
    [236, 384, 0.66, 0],
    [58, 104, 0.6, 1],
    [212, 92, 0.7, 2]
  ];
  const sparkFill = [hexA(a1, 0.4), hexA(a2, 0.36), hexA(color, 0.3)];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <radialGradient id="firstbday-flameglow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={hexA(a2, 0.3)} />
          <stop offset="100%" stopColor={hexA(a2, 0)} />
        </radialGradient>
      </defs>

      {/* bunting */}
      <path
        d="M 14 18 Q 150 50 286 18"
        fill="none"
        stroke={ink}
        strokeWidth={sw}
        strokeLinecap="round"
      />
      {flags.map((f) => (
        <path
          key={`firstbday-flag-${f.i}`}
          d="M -5.1 0 L 5.1 0 L 0 12.6 Z"
          transform={`translate(${f.x.toFixed(2)} ${f.y.toFixed(2)}) rotate(${f.a.toFixed(2)})`}
          fill={f.i % 3 === 0 ? tint1 : f.i % 3 === 1 ? tint2 : 'none'}
          stroke={ink}
          strokeWidth={sw * 0.85}
          strokeLinejoin="round"
        />
      ))}

      {/* smash cake on a stand, with the number-one candle */}
      <g transform="translate(44 100)">
        <ellipse cx="0" cy="0" rx="26" ry="3.6" fill={tint3} stroke={ink} strokeWidth={sw} />
        <path
          d="M -5.6 2.9 c 0 4.6 -3.8 6.2 -6.2 7.4 h 23.6 c -2.4 -1.2 -6.2 -2.8 -6.2 -7.4"
          fill="none"
          stroke={ink}
          strokeWidth={sw}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <rect x="-19" y="-25" width="38" height="25" rx="2" fill={tint3} stroke={ink} strokeWidth={sw} />
        <path d={icing} fill={tint1} stroke={ink} strokeWidth={sw} strokeLinejoin="round" />
        <path d="M -19 -7.4 H 19" fill="none" stroke={inkSoft} strokeWidth={sw * 0.7} />
        <circle cx="-10.5" cy="-3.6" r="1.25" fill={dotB} />
        <circle cx="0" cy="-2.4" r="1.25" fill={dotA} />
        <circle cx="9.6" cy="-4.2" r="1.25" fill={dotB} />
        <g transform="translate(-1.7 0)">
          <circle cx="2" cy="-45.6" r="7.6" fill="url(#firstbday-flameglow)" />
          <path
            d="M 3.4 -40.5 L 3.4 -27 L 6.4 -27 L 6.4 -25 L -3 -25 L -3 -27 L 0.6 -27 L 0.6 -37.2 L -2.4 -34.8 L -3.7 -36.5 L 0.9 -40.5 Z"
            fill={tint2}
            stroke={ink}
            strokeWidth={sw}
            strokeLinejoin="round"
          />
          <path d="M 2 -40.6 v -2.4" fill="none" stroke={ink} strokeWidth={sw * 0.8} strokeLinecap="round" />
          <path
            d="M 2 -49.8 c 2.7 2.8 3.4 4.6 2 6.1 c -1.4 1.5 -4.1 1.2 -4.9 -0.5 c -0.7 -1.5 0.3 -3.3 2.9 -5.6 Z"
            fill={hexA(a2, 0.45)}
            stroke={ink}
            strokeWidth={sw * 0.8}
            strokeLinejoin="round"
          />
        </g>
      </g>

      {/* little crown */}
      <g transform="translate(252 86)">
        <path
          d="M -18.5 4 L -16 -13.5 L -8.4 -4.4 L 0 -17.6 L 8.4 -4.4 L 16 -13.5 L 18.5 4 Z"
          fill={tint2}
          stroke={ink}
          strokeWidth={sw}
          strokeLinejoin="round"
        />
        <rect x="-19.6" y="3.4" width="39.2" height="6.4" rx="2.2" fill={tint1} stroke={ink} strokeWidth={sw} />
        <circle cx="-16" cy="-15.4" r="2.1" fill={tint1} stroke={ink} strokeWidth={sw * 0.8} />
        <circle cx="0" cy="-19.6" r="2.5" fill={tint1} stroke={ink} strokeWidth={sw * 0.8} />
        <circle cx="16" cy="-15.4" r="2.1" fill={tint1} stroke={ink} strokeWidth={sw * 0.8} />
        <circle cx="-9" cy="6.6" r="1.3" fill={dotB} />
        <circle cx="0" cy="6.6" r="1.3" fill={dotB} />
        <circle cx="9" cy="6.6" r="1.3" fill={dotB} />
      </g>

      {/* baby rattle */}
      <g transform="translate(36 342) rotate(-12)">
        <rect x="-3.2" y="9" width="6.4" height="18" rx="3.2" fill={tint2} stroke={ink} strokeWidth={sw} />
        <circle cx="0" cy="31.4" r="6" fill="none" stroke={ink} strokeWidth={sw * 1.25} />
        <circle cx="0" cy="0" r="13" fill={tint1} stroke={ink} strokeWidth={sw} />
        <circle cx="0" cy="-0.6" r="4.4" fill="none" stroke={inkSoft} strokeWidth={sw * 0.7} />
        <circle cx="7.4" cy="-4.4" r="1.35" fill={hexA(a2, 0.4)} />
        <circle cx="-6.8" cy="4.8" r="1.35" fill={hexA(a2, 0.4)} />
        <circle cx="4.8" cy="7.4" r="1.35" fill={hexA(a2, 0.4)} />
        <circle cx="-6.6" cy="-6.4" r="1.2" fill={hexA(a1, 0.4)} />
      </g>

      {/* party hat */}
      <g transform="translate(112 374)">
        <path
          d="M 0 -34 L 14.4 -1.2 A 15 4.6 0 0 1 -14.4 -1.2 Z"
          fill={tint1}
          stroke={ink}
          strokeWidth={sw}
          strokeLinejoin="round"
        />
        <circle cx="0" cy="-36.4" r="3.9" fill={tint2} stroke={ink} strokeWidth={sw} />
        <circle cx="0" cy="-24" r="1.5" fill={dotB} />
        <circle cx="-4.8" cy="-13.6" r="1.5" fill={dotB} />
        <circle cx="5.2" cy="-14.6" r="1.5" fill={dotB} />
        <circle cx="0" cy="-6.4" r="1.4" fill={dotA} />
        <circle cx="-9.6" cy="-5" r="1.4" fill={dotA} />
        <circle cx="9.8" cy="-5.6" r="1.4" fill={dotA} />
      </g>

      {/* balloon pair */}
      <g transform="translate(266 346)">
        <ellipse cx="0" cy="-17" rx="9" ry="11" fill={tint1} stroke={ink} strokeWidth={sw} />
        <path
          d="M -3.4 -22.6 a 5.4 6.6 0 0 1 3 -3.4"
          fill="none"
          stroke={inkSoft}
          strokeWidth={sw * 0.7}
          strokeLinecap="round"
        />
        <path d="M 0 -6 l -2.3 3.3 h 4.6 Z" fill={tint1} stroke={ink} strokeWidth={sw * 0.8} strokeLinejoin="round" />
        <path
          d="M 0 -2.4 c 3.4 4.8 -3.2 8.4 -0.4 13.4 c 1.6 2.8 1 5.2 -1.6 6.8"
          fill="none"
          stroke={ink}
          strokeWidth={sw * 0.7}
          strokeLinecap="round"
        />
        <ellipse cx="-16.6" cy="-3" rx="7.4" ry="9.2" fill={tint2} stroke={ink} strokeWidth={sw} />
        <path
          d="M -16.6 6.4 l -2 2.9 h 4 Z"
          fill={tint2}
          stroke={ink}
          strokeWidth={sw * 0.8}
          strokeLinejoin="round"
        />
        <path
          d="M -16.6 9.6 c -3 4.2 1.8 7.4 -0.6 11.8"
          fill="none"
          stroke={ink}
          strokeWidth={sw * 0.7}
          strokeLinecap="round"
        />
      </g>

      {/* confetti sparkles */}
      {sparks.map((s, i) => (
        <path
          key={`firstbday-spark-${i}`}
          d="M 0 -5.4 C 0.7 -2.1 1.6 -1.2 4.9 -0.5 C 1.6 0.2 0.7 1.1 0 4.4 C -0.7 1.1 -1.6 0.2 -4.9 -0.5 C -1.6 -1.2 -0.7 -2.1 0 -5.4 Z"
          transform={`translate(${s[0]} ${s[1]}) scale(${s[2]})`}
          fill={sparkFill[s[3]]}
        />
      ))}
    </svg>
  );
}

// ─── BabyShowerMotif — pram / bassinet stroller (top-left), onesie pegged to a clothesline swag (top-right), baby
function BabyShowerMotif({ color, accent, accent2, scale = 300 }) {
  const c0 = color;
  const c1 = accent || color;
  const c2 = accent2 || accent || color;

  const k = Math.max(0.8, Math.min(1.3, 300 / scale));
  const sw = 1.45 * k;
  const swThin = 0.95 * k;

  const ink = hexA(c0, 0.5);
  const inkSoft = hexA(c0, 0.34);
  const wash = hexA(c1, 0.15);
  const washWarm = hexA(c2, 0.15);
  const tint = hexA(c2, 0.42);

  const CLOUD =
    "M -15 4 C -20.5 4 -21.5 -3.4 -16 -4.6 C -16.6 -11.6 -6.8 -13.8 -4 -8.4 C -1.6 -14.6 8.4 -15 10.2 -8.2 C 16.2 -8.2 17.4 1.8 11.4 4 Z";
  const SPARK =
    "M 0 -5 C 0.5 -1.7 1.7 -0.5 5 0 C 1.7 0.5 0.5 1.7 0 5 C -0.5 1.7 -1.7 0.5 -5 0 C -1.7 -0.5 -0.5 -1.7 0 -5 Z";
  const DROP =
    "M 0 -3 C 1.8 -0.9 2.1 0.7 1 1.7 C 0.2 2.4 -0.8 2.3 -1.4 1.5 C -2.2 0.5 -1.6 -1.1 0 -3 Z";

  const clouds = [
    { id: "cl-a", x: 82, y: 30, s: 1 },
    { id: "cl-b", x: 252, y: 334, s: 0.92 },
    { id: "cl-c", x: 30, y: 292, s: 0.6 }
  ];
  const drops = [
    { id: "dr-a", x: 76, y: 42, s: 1 },
    { id: "dr-b", x: 84, y: 48, s: 0.85 },
    { id: "dr-c", x: 91, y: 41, s: 0.72 }
  ];
  const booties = [
    { id: "bo-a", x: 42, y: 346, r: -10, s: 0.95 },
    { id: "bo-b", x: 69, y: 355, r: 9, s: 0.84 }
  ];
  const sparks = [
    { id: "sp-a", x: 22, y: 100, s: 1 },
    { id: "sp-b", x: 56, y: 16, s: 0.7 },
    { id: "sp-c", x: 120, y: 24, s: 0.85 },
    { id: "sp-d", x: 286, y: 66, s: 0.9 },
    { id: "sp-e", x: 44, y: 162, s: 0.65 },
    { id: "sp-f", x: 274, y: 238, s: 0.75 },
    { id: "sp-g", x: 110, y: 372, s: 0.8 },
    { id: "sp-h", x: 188, y: 352, s: 0.62 }
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <g
        fill="none"
        stroke={ink}
        strokeWidth={sw}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <g transform="translate(44 64) scale(0.72)">
          <path d="M -21 -13 L 15 -13 C 14 -3 8 0 0 0 C -11 0 -20 -6 -21 -13 Z" fill={wash} />
          <path d="M -20 -13 C -21 -28 -7 -34 1 -27 L 1 -13 Z" fill={washWarm} />
          <path d="M -14 -13 C -14 -23.5 -7 -27.5 0.6 -24.4" stroke={inkSoft} strokeWidth={swThin} />
          <path d="M -7.5 -13 C -7.5 -19 -3.5 -22 0.8 -20.4" stroke={inkSoft} strokeWidth={swThin} />
          <path d="M -19 -9.6 L 13.6 -9.6" stroke={inkSoft} strokeWidth={swThin} />
          <path d="M 15 -13 C 22.5 -15 26.5 -20.5 25 -26.5" />
          <path d="M 20.5 -28.6 L 29 -24.8" />
          <path d="M -9.5 -1.2 L -10.5 3" />
          <path d="M 8 -0.6 L 10.5 3" />
          <circle cx="-10.5" cy="8" r="5.4" />
          <circle cx="10.5" cy="8" r="5.4" />
          <circle cx="-10.5" cy="8" r="1.5" fill={tint} stroke="none" />
          <circle cx="10.5" cy="8" r="1.5" fill={tint} stroke="none" />
        </g>

        <path d="M 190 21 Q 240 44 292 28" stroke={inkSoft} strokeWidth={swThin} />
        <g transform="translate(240 40) scale(0.95)">
          <rect x="-1.7" y="-7" width="3.4" height="6" rx="1.4" fill={washWarm} />
          <path d="M 0 -6.4 L 0 -1.6" stroke={inkSoft} strokeWidth={swThin} />
          <path
            d="M -6 0 C -3 3.2 3 3.2 6 0 L 9 1 L 12 6.2 L 8.6 8.8 L 6.5 6.2 L 6.5 14 C 6.5 17.6 4.6 19.2 1.6 19.2 L -1.6 19.2 C -4.6 19.2 -6.5 17.6 -6.5 14 L -6.5 6.2 L -8.6 8.8 L -12 6.2 L -9 1 Z"
            fill={wash}
          />
          <path d="M -6 0 C -3 3.2 3 3.2 6 0" stroke={inkSoft} strokeWidth={swThin} />
          <circle cx="-2.6" cy="16.4" r="0.9" fill={tint} stroke="none" />
          <circle cx="0" cy="17.2" r="0.9" fill={tint} stroke="none" />
          <circle cx="2.6" cy="16.4" r="0.9" fill={tint} stroke="none" />
        </g>

        <g transform="translate(266 186) rotate(-16) scale(0.8)">
          <circle cx="0" cy="0" r="9.5" fill={washWarm} />
          <circle cx="-3.4" cy="-3" r="1.3" fill={tint} stroke="none" />
          <circle cx="3" cy="0.6" r="1.3" fill={tint} stroke="none" />
          <circle cx="-1.4" cy="4.4" r="1.3" fill={tint} stroke="none" />
          <rect x="-3" y="8.2" width="6" height="3.4" rx="1.5" fill={wash} />
          <rect x="-2.3" y="11.4" width="4.6" height="12.8" rx="2.3" fill={wash} />
          <circle cx="0" cy="27.2" r="3.2" />
        </g>

        <g transform="translate(31 224) rotate(8) scale(0.78)">
          <path
            d="M -3 -20.5 C -3 -25.5 3 -25.5 3 -20.5 C 3 -18.4 2.4 -17.2 2.4 -16.2 L -2.4 -16.2 C -2.4 -17.2 -3 -18.4 -3 -20.5 Z"
            fill={washWarm}
          />
          <rect x="-5.2" y="-16.2" width="10.4" height="4.8" rx="1.3" fill={wash} />
          <path d="M -5 -11.4 L 5 -11.4 C 7.5 -9 8 -6 8 -3 L 8 14 C 8 17.5 6 19 3 19 L -3 19 C -6 19 -8 17.5 -8 14 L -8 -3 C -8 -6 -7.5 -9 -5 -11.4 Z" />
          <path
            d="M -8 2 L 8 2 L 8 14 C 8 17.5 6 19 3 19 L -3 19 C -6 19 -8 17.5 -8 14 Z"
            fill={wash}
            stroke="none"
          />
          <path d="M -8 2 L 8 2" stroke={inkSoft} strokeWidth={swThin} />
          <path d="M 3.6 -5.4 L 6.4 -5.4" stroke={inkSoft} strokeWidth={swThin} />
          <path d="M 3.6 -1.2 L 6.4 -1.2" stroke={inkSoft} strokeWidth={swThin} />
        </g>

        {booties.map((b) => (
          <g key={b.id} transform={"translate(" + b.x + " " + b.y + ") rotate(" + b.r + ") scale(" + b.s + ")"}>
            <path
              d="M -6.2 -14 C -6.2 -15.3 -5.3 -15.9 -4 -15.9 L 3 -15.9 C 4.4 -15.9 5.1 -15.2 5.1 -14 L 5.1 -5.6 C 10 -5 12.9 -2 12.9 1.6 C 12.9 4.5 11 6 8 6 L -3.6 6 C -6.2 6 -7.3 4.6 -7.3 1.8 C -7.3 -3 -6.4 -8.6 -6.2 -14 Z"
              fill={wash}
            />
            <path d="M -6.6 -8.2 L 5.1 -8.2" stroke={inkSoft} strokeWidth={swThin} />
            <path d="M -6.4 -11.7 L 5.1 -11.7" stroke={inkSoft} strokeWidth={swThin} />
            <circle cx="-0.7" cy="-11.7" r="1.1" fill={tint} stroke="none" />
            <path d="M -6.9 3.2 C -2 4.4 5.6 4.2 11.9 2.8" stroke={inkSoft} strokeWidth={swThin} />
          </g>
        ))}

        {clouds.map((c) => (
          <path
            key={c.id}
            d={CLOUD}
            transform={"translate(" + c.x + " " + c.y + ") scale(" + c.s + ")"}
            fill={wash}
          />
        ))}

        {drops.map((d) => (
          <path
            key={d.id}
            d={DROP}
            transform={"translate(" + d.x + " " + d.y + ") scale(" + d.s + ")"}
            fill={tint}
            stroke="none"
          />
        ))}

        {sparks.map((p) => (
          <path
            key={p.id}
            d={SPARK}
            transform={"translate(" + p.x + " " + p.y + ") scale(" + p.s + ")"}
            fill={tint}
            stroke="none"
          />
        ))}
      </g>
    </svg>
  );
}

// ─── GenderRevealMotif — Pair of tied balloons, one in accent and one in accent2, with knots, curling strings and h
function GenderRevealMotif({ color, accent, accent2, scale = 300 }) {
  const ink = color;
  const pink = accent || color;
  const blue = accent2 || accent || color;
  const k = Math.max(0.7, Math.min(1.5, 300 / (scale || 300)));
  const sw = (w) => Math.round(w * k * 100) / 100;

  const balloon = (cx, cy, rx, ry) =>
    "M " + cx + " " + (cy - ry) +
    " C " + (cx + rx) + " " + (cy - ry) +
    " " + (cx + rx) + " " + (cy + ry * 0.58) +
    " " + cx + " " + (cy + ry) +
    " C " + (cx - rx) + " " + (cy + ry * 0.58) +
    " " + (cx - rx) + " " + (cy - ry) +
    " " + cx + " " + (cy - ry) + " Z";

  const bootiePath =
    "M 0 0 L 8.5 0 L 8.5 6.4 C 8.5 8.2 10.6 8.7 13.6 9.7 C 16.9 10.8 17.7 13.5 14.6 14.5 C 12 15.4 3 15.5 1.2 14.7 C -0.4 14 -0.6 11.5 0 9.6 Z";

  const burst = [
    [44.6, 343.0, 45.6, 335.1],
    [46.1, 343.5, 51.2, 332.6],
    [47.4, 344.3, 57.6, 333.4],
    [48.4, 345.6, 58.0, 340.2],
    [48.9, 347.0, 55.8, 345.7]
  ];

  const pops = [[59, 327], [52, 320], [65, 335]];

  const confetti = [
    [84, 24, 0], [112, 54, 1], [140, 18, 2], [168, 44, 0], [196, 14, 1],
    [216, 64, 0], [68, 86, 2], [150, 90, 1],
    [18, 140, 0], [28, 192, 1], [16, 250, 2],
    [284, 212, 1], [270, 252, 0], [288, 290, 2],
    [92, 364, 1], [122, 338, 0], [156, 384, 2], [186, 346, 1], [214, 374, 0]
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id="grev-bal-pink" x1="0.15" y1="0" x2="0.6" y2="1">
          <stop offset="0" stopColor={hexA(pink, 0.26)} />
          <stop offset="1" stopColor={hexA(pink, 0.07)} />
        </linearGradient>
        <linearGradient id="grev-bal-blue" x1="0.15" y1="0" x2="0.6" y2="1">
          <stop offset="0" stopColor={hexA(blue, 0.26)} />
          <stop offset="1" stopColor={hexA(blue, 0.07)} />
        </linearGradient>
      </defs>

      {/* ---- top-left: the pair of tied balloons ---- */}
      <g>
        <path
          d="M 30 56.5 C 31.5 68 42 80 46 96"
          fill="none"
          stroke={hexA(ink, 0.34)}
          strokeWidth={sw(0.9)}
          strokeLinecap="round"
        />
        <path
          d="M 57 45 C 56 58 48.5 78 46 96"
          fill="none"
          stroke={hexA(ink, 0.34)}
          strokeWidth={sw(0.9)}
          strokeLinecap="round"
        />
        <path
          d="M 46 96 C 43.5 104 50 110 47 117"
          fill="none"
          stroke={hexA(ink, 0.26)}
          strokeWidth={sw(0.8)}
          strokeLinecap="round"
        />
        <path d={balloon(30, 36, 13.5, 16.5)} fill="url(#grev-bal-pink)" stroke={hexA(pink, 0.52)} strokeWidth={sw(1.1)} />
        <path d="M 27.4 52 L 32.6 52 L 30 57 Z" fill={hexA(pink, 0.4)} stroke={hexA(pink, 0.45)} strokeWidth={sw(0.7)} strokeLinejoin="round" />
        <path d="M 21 30 C 19.4 24 23 19.4 28.2 18.8" fill="none" stroke={hexA(pink, 0.4)} strokeWidth={sw(1)} strokeLinecap="round" />
        <path d={balloon(57, 27, 11.5, 14)} fill="url(#grev-bal-blue)" stroke={hexA(blue, 0.52)} strokeWidth={sw(1.1)} />
        <path d="M 54.6 40.6 L 59.4 40.6 L 57 45 Z" fill={hexA(blue, 0.4)} stroke={hexA(blue, 0.45)} strokeWidth={sw(0.7)} strokeLinejoin="round" />
        <path d="M 50 22 C 48.8 17.4 51.4 14.6 55 14.2" fill="none" stroke={hexA(blue, 0.4)} strokeWidth={sw(1)} strokeLinecap="round" />
      </g>

      {/* ---- top-right: question mark cradled in a heart ---- */}
      <g>
        <path
          d="M 258 58.4 C 236.4 45.2 243.6 27.2 258 39.2 C 272.4 27.2 279.6 45.2 258 58.4 Z"
          fill={hexA(pink, 0.12)}
          stroke={hexA(ink, 0.5)}
          strokeWidth={sw(1.2)}
          strokeLinejoin="round"
        />
        <path
          d="M 254.2 40 C 254.2 35 261.8 34.8 261.8 39.4 C 261.8 42.6 258 43.2 258 46.4"
          fill="none"
          stroke={hexA(blue, 0.6)}
          strokeWidth={sw(1.5)}
          strokeLinecap="round"
        />
        <circle cx="258" cy="50.6" r={1.6} fill={hexA(blue, 0.6)} />
      </g>

      {/* ---- right margin: a single balloon drifting ---- */}
      <g>
        <path
          d="M 277 181 C 279.5 192 271.5 202 274 216"
          fill="none"
          stroke={hexA(ink, 0.3)}
          strokeWidth={sw(0.85)}
          strokeLinecap="round"
        />
        <path d={balloon(277, 162, 12, 14.5)} fill="url(#grev-bal-blue)" stroke={hexA(blue, 0.46)} strokeWidth={sw(1.05)} />
        <path d="M 274.6 176 L 279.4 176 L 277 181 Z" fill={hexA(blue, 0.36)} stroke={hexA(blue, 0.4)} strokeWidth={sw(0.7)} strokeLinejoin="round" />
        <path d="M 269 156 C 267.6 150.6 270.6 146.8 275 146.2" fill="none" stroke={hexA(blue, 0.36)} strokeWidth={sw(0.95)} strokeLinecap="round" />
      </g>

      {/* ---- left margin: curling streamer ---- */}
      <path
        d="M 22 166 C 12 178 30 188 20 200 C 10 212 28 222 18 234 C 12 241 14 246.5 18.5 250"
        fill="none"
        stroke={hexA(pink, 0.38)}
        strokeWidth={sw(1.15)}
        strokeLinecap="round"
      />

      {/* ---- bottom-left: the popped party popper ---- */}
      <g>
        <path
          d="M 12.3 374.6 L 37.4 341.9 Q 47.1 344.7 50.6 354.1 L 19.7 381.4 Z"
          fill={hexA(ink, 0.1)}
          stroke={hexA(ink, 0.48)}
          strokeWidth={sw(1.15)}
          strokeLinejoin="round"
        />
        <path d="M 21.1 363.1 L 30.5 371.9" fill="none" stroke={hexA(pink, 0.42)} strokeWidth={sw(1.1)} strokeLinecap="round" />
        <path d="M 27.9 354.3 L 38.9 364.5" fill="none" stroke={hexA(blue, 0.42)} strokeWidth={sw(1.1)} strokeLinecap="round" />
        {burst.map((b, i) => (
          <path
            key={"grev-burst-" + i}
            d={"M " + b[0] + " " + b[1] + " L " + b[2] + " " + b[3]}
            fill="none"
            stroke={hexA(i % 2 === 0 ? pink : blue, 0.44)}
            strokeWidth={sw(1.1)}
            strokeLinecap="round"
          />
        ))}
        {pops.map((p, i) => (
          <circle
            key={"grev-pop-" + i}
            cx={p[0]}
            cy={p[1]}
            r={1.5 + (i % 2) * 0.4}
            fill={hexA(i % 2 === 0 ? blue : pink, 0.4)}
          />
        ))}
      </g>

      {/* ---- bottom-right: a pink bootie and a blue bootie ---- */}
      <g transform="translate(236 346) scale(1.2)">
        <path d={bootiePath} fill={hexA(pink, 0.13)} stroke={hexA(ink, 0.5)} strokeWidth={sw(1)} strokeLinejoin="round" />
        <path d="M 0 4.6 L 8.5 4.6" fill="none" stroke={hexA(ink, 0.4)} strokeWidth={sw(0.85)} strokeLinecap="round" />
        <path d="M 2 13.4 C 6 14.2 12 14 15.2 13" fill="none" stroke={hexA(ink, 0.28)} strokeWidth={sw(0.75)} strokeLinecap="round" />
        <circle cx="4.3" cy="4.6" r={1.05} fill={hexA(pink, 0.5)} />
      </g>
      <g transform="translate(292 364) scale(-1.1 1.1)">
        <path d={bootiePath} fill={hexA(blue, 0.13)} stroke={hexA(ink, 0.46)} strokeWidth={sw(1)} strokeLinejoin="round" />
        <path d="M 0 4.6 L 8.5 4.6" fill="none" stroke={hexA(ink, 0.38)} strokeWidth={sw(0.85)} strokeLinecap="round" />
        <path d="M 2 13.4 C 6 14.2 12 14 15.2 13" fill="none" stroke={hexA(ink, 0.26)} strokeWidth={sw(0.75)} strokeLinecap="round" />
        <circle cx="4.3" cy="4.6" r={1.05} fill={hexA(blue, 0.5)} />
      </g>

      {/* ---- confetti scattered along the edges ---- */}
      {confetti.map((c, i) => {
        const cx = c[0];
        const cy = c[1];
        const hue = i % 3 === 0 ? pink : i % 3 === 1 ? blue : ink;
        const op = 0.3 + ((i * 5) % 4) * 0.055;
        const rot = ((i * 53) % 160) - 80;
        if (c[2] === 1) {
          return <circle key={"grev-conf-" + i} cx={cx} cy={cy} r={1.5} fill={hexA(hue, op + 0.08)} />;
        }
        if (c[2] === 2) {
          return (
            <path
              key={"grev-conf-" + i}
              d={"M " + (cx - 3.2) + " " + (cy + 2) + " Q " + cx + " " + (cy - 3.2) + " " + (cx + 3.2) + " " + (cy + 1)}
              fill="none"
              stroke={hexA(hue, op)}
              strokeWidth={sw(1.05)}
              strokeLinecap="round"
            />
          );
        }
        return (
          <rect
            key={"grev-conf-" + i}
            x={cx - 2.2}
            y={cy - 1}
            width={4.4}
            height={2}
            rx={0.8}
            fill={hexA(hue, op)}
            transform={"rotate(" + rot + " " + cx + " " + cy + ")"}
          />
        );
      })}
    </svg>
  );
}

// ─── PoolPartyMotif — Ring float (inner tube) with tinted panels, Sunglasses, Flamingo ring float, Pool noodle, 
function PoolPartyMotif({ color, accent, accent2, scale = 300 }) {
  const u = Math.max(0.75, Math.min(1.5, 300 / scale));

  // ---- ring float spokes (top-left) ----
  const ringSpokes = [45, 135, 225, 315].map((deg, i) => {
    const r = (deg * Math.PI) / 180;
    return {
      i,
      x1: 48 + 15 * Math.cos(r),
      y1: 50 + 15 * Math.sin(r),
      x2: 48 + 30 * Math.cos(r),
      y2: 50 + 30 * Math.sin(r),
    };
  });

  // ---- flamingo float tube segments (top-right) ----
  const floatTicks = [30, 150, 210, 330].map((deg, i) => {
    const r = (deg * Math.PI) / 180;
    return {
      i,
      x1: 250 + 12.5 * Math.cos(r),
      y1: 74 + 5 * Math.sin(r),
      x2: 250 + 27 * Math.cos(r),
      y2: 74 + 12 * Math.sin(r),
    };
  });

  // ---- palm frond leaflets (bottom-left) ----
  const frond = [0, 1, 2, 3, 4, 5, 6].map((i) => {
    const t = i / 6;
    const px = 15 + 33 * t;
    const py = 393 - 58 * t;
    const L = 9 + 11 * Math.sin(Math.PI * (0.22 + 0.66 * t));
    return {
      i,
      left: `M ${px} ${py} Q ${px - L * 0.9} ${py - L * 0.1} ${px - L * 1.05} ${py - L * 0.7}`,
      right: `M ${px} ${py} Q ${px + L * 0.5} ${py - L * 0.7} ${px + L * 0.45} ${py - L * 1.15}`,
    };
  });

  // ---- water lines (bottom band) ----
  const waves = [0, 1, 2].map((i) => ({
    i,
    d:
      `M ${-10 - (i % 2) * 16} ${356 + i * 14}` +
      " q 17 -6.5 34 0 t 34 0 t 34 0 t 34 0 t 34 0 t 34 0 t 34 0 t 34 0 t 34 0 t 34 0",
    w: (1.7 - i * 0.3) * u,
  }));

  // ---- small ripple rings ----
  const ripples = [
    { i: 0, cx: 104, cy: 92, rx: 13, ry: 4.4 },
    { i: 1, cx: 104, cy: 92, rx: 7, ry: 2.4 },
    { i: 2, cx: 206, cy: 100, rx: 10, ry: 3.4 },
    { i: 3, cx: 206, cy: 100, rx: 5, ry: 1.7 },
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id="poolm-water-fade" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={hexA(color, 0.04)} />
          <stop offset="24%" stopColor={hexA(color, 0.4)} />
          <stop offset="76%" stopColor={hexA(color, 0.4)} />
          <stop offset="100%" stopColor={hexA(color, 0.05)} />
        </linearGradient>
      </defs>

      <g fill="none" strokeLinecap="round" strokeLinejoin="round">
        {/* ================= RING FLOAT — top left ================= */}
        <g>
          <path
            d="M 32.09 34.09 A 22.5 22.5 0 0 1 63.91 34.09"
            stroke={hexA(accent, 0.3)}
            strokeWidth={15 * u}
            strokeLinecap="butt"
          />
          <path
            d="M 63.91 65.91 A 22.5 22.5 0 0 1 32.09 65.91"
            stroke={hexA(accent, 0.3)}
            strokeWidth={15 * u}
            strokeLinecap="butt"
          />
          <circle cx="48" cy="50" r="30" stroke={hexA(color, 0.5)} strokeWidth={1.7 * u} />
          <circle cx="48" cy="50" r="15" stroke={hexA(color, 0.5)} strokeWidth={1.7 * u} />
          {ringSpokes.map((s) => (
            <line
              key={`poolm-spoke-${s.i}`}
              x1={s.x1}
              y1={s.y1}
              x2={s.x2}
              y2={s.y2}
              stroke={hexA(color, 0.42)}
              strokeWidth={1.4 * u}
            />
          ))}
          <path
            d="M 27.5 39.5 A 24 24 0 0 1 40.5 27.8"
            stroke={hexA(accent2, 0.4)}
            strokeWidth={1.2 * u}
          />
        </g>

        {/* ================= RIPPLE RINGS — top ================= */}
        {ripples.map((r) => (
          <ellipse
            key={`poolm-ripple-${r.i}`}
            cx={r.cx}
            cy={r.cy}
            rx={r.rx}
            ry={r.ry}
            stroke={hexA(color, r.i % 2 === 0 ? 0.3 : 0.22)}
            strokeWidth={1.1 * u}
          />
        ))}

        {/* ================= SUNGLASSES — top centre-right ================= */}
        <g transform="rotate(-7 173 33)">
          <rect
            x="143"
            y="23"
            width="29"
            height="21"
            rx="9.5"
            ry="9"
            fill={hexA(accent2, 0.2)}
            stroke={hexA(color, 0.52)}
            strokeWidth={1.7 * u}
          />
          <rect
            x="177"
            y="23"
            width="29"
            height="21"
            rx="9.5"
            ry="9"
            fill={hexA(accent2, 0.2)}
            stroke={hexA(color, 0.52)}
            strokeWidth={1.7 * u}
          />
          <path d="M 172 28.5 Q 174.5 24.5 177 28.5" stroke={hexA(color, 0.5)} strokeWidth={1.6 * u} />
          <path d="M 143.5 27 L 131 20.5" stroke={hexA(color, 0.5)} strokeWidth={1.6 * u} />
          <path d="M 205.5 27 L 218 20.5" stroke={hexA(color, 0.5)} strokeWidth={1.6 * u} />
          <path d="M 150 39 L 157.5 28.5" stroke={hexA(color, 0.26)} strokeWidth={1.2 * u} />
          <path d="M 184 39 L 191.5 28.5" stroke={hexA(color, 0.26)} strokeWidth={1.2 * u} />
        </g>

        {/* ================= FLAMINGO FLOAT — top right ================= */}
        <g>
          <ellipse
            cx="250"
            cy="74"
            rx="27"
            ry="12"
            fill={hexA(accent, 0.14)}
            stroke={hexA(color, 0.5)}
            strokeWidth={1.7 * u}
          />
          <ellipse cx="250" cy="74" rx="12.5" ry="5" stroke={hexA(color, 0.44)} strokeWidth={1.4 * u} />
          {floatTicks.map((t) => (
            <line
              key={`poolm-tick-${t.i}`}
              x1={t.x1}
              y1={t.y1}
              x2={t.x2}
              y2={t.y2}
              stroke={hexA(color, 0.3)}
              strokeWidth={1.1 * u}
            />
          ))}
          <path
            d="M 243 68 C 233.5 56 232 38 242.5 33"
            stroke={hexA(color, 0.52)}
            strokeWidth={2.1 * u}
          />
          <ellipse
            cx="246"
            cy="31"
            rx="6"
            ry="5"
            fill={hexA(accent, 0.22)}
            stroke={hexA(color, 0.52)}
            strokeWidth={1.6 * u}
          />
          <path d="M 241.5 32 L 233.5 36.5 L 242 38.5 Z" fill={hexA(accent, 0.4)} stroke={hexA(color, 0.42)} strokeWidth={1.1 * u} />
          <circle cx="248.2" cy="29.6" r="1.2" fill={hexA(color, 0.5)} />
          <path d="M 272 68 C 279 64 281.5 58 279.5 52" stroke={hexA(color, 0.4)} strokeWidth={1.5 * u} />
          <path d="M 268.5 70.5 C 274.5 68 277.5 63.5 277 58.5" stroke={hexA(color, 0.28)} strokeWidth={1.3 * u} />
        </g>

        {/* ================= POOL NOODLE — left margin ================= */}
        <g>
          <path
            d="M 26 148 C 12 178 40 202 27 232 C 15 260 34 282 25 298"
            stroke={hexA(accent2, 0.4)}
            strokeWidth={12.5 * u}
          />
          <path
            d="M 26 148 C 12 178 40 202 27 232 C 15 260 34 282 25 298"
            stroke={hexA(color, 0.24)}
            strokeWidth={1.2 * u}
            fill="none"
            opacity="0.9"
          />
          <ellipse cx="26" cy="148" rx="4.2" ry="2.4" stroke={hexA(color, 0.45)} strokeWidth={1.2 * u} />
          <path d="M 16 168 Q 22 172.5 28.5 168.5" stroke={hexA(color, 0.26)} strokeWidth={1.2 * u} />
          <path d="M 24 208 Q 30 212.5 36.5 208.5" stroke={hexA(color, 0.26)} strokeWidth={1.2 * u} />
          <path d="M 19 267.5 Q 25 272 31.5 268" stroke={hexA(color, 0.26)} strokeWidth={1.2 * u} />
        </g>

        {/* ================= BEACH BALL — right margin ================= */}
        <g transform="rotate(-16 268 206)">
          <circle
            cx="268"
            cy="206"
            r="17"
            fill={hexA(accent, 0.1)}
            stroke={hexA(color, 0.5)}
            strokeWidth={1.6 * u}
          />
          <path
            d="M 268 189 C 261.5 197 261.5 215 268 223 C 274.5 215 274.5 197 268 189 Z"
            fill={hexA(accent, 0.28)}
          />
          <path d="M 268 189 C 255.5 197 255.5 215 268 223" stroke={hexA(color, 0.38)} strokeWidth={1.3 * u} />
          <path d="M 268 189 C 280.5 197 280.5 215 268 223" stroke={hexA(color, 0.38)} strokeWidth={1.3 * u} />
          <path d="M 268 189 L 268 223" stroke={hexA(color, 0.3)} strokeWidth={1.2 * u} />
          <ellipse cx="268" cy="189.4" rx="4" ry="2" stroke={hexA(color, 0.42)} strokeWidth={1.2 * u} />
        </g>

        {/* ================= PALM FROND — bottom left ================= */}
        <g>
          <path
            d="M 10 398 Q 22 372 49 334"
            stroke={hexA(accent2, 0.48)}
            strokeWidth={1.9 * u}
          />
          {frond.map((f) => (
            <g key={`poolm-frond-${f.i}`}>
              <path d={f.left} stroke={hexA(accent2, 0.42)} strokeWidth={1.5 * u} />
              <path d={f.right} stroke={hexA(accent2, 0.34)} strokeWidth={1.4 * u} />
            </g>
          ))}
          <path d="M 49 334 Q 51 328 49.5 321" stroke={hexA(accent2, 0.4)} strokeWidth={1.4 * u} />
        </g>

        {/* ================= POOL LADDER — bottom right ================= */}
        <g>
          <path d="M 252 392 L 252 340 Q 252 324 264.5 322" stroke={hexA(color, 0.5)} strokeWidth={2 * u} />
          <path d="M 276 392 L 276 340 Q 276 324 288.5 322" stroke={hexA(color, 0.5)} strokeWidth={2 * u} />
          <line x1="252" y1="348" x2="276" y2="348" stroke={hexA(color, 0.42)} strokeWidth={1.7 * u} />
          <line x1="252" y1="364" x2="276" y2="364" stroke={hexA(color, 0.32)} strokeWidth={1.7 * u} />
          <ellipse cx="264.5" cy="322" rx="2.2" ry="2.2" fill={hexA(accent2, 0.35)} />
          <ellipse cx="288.5" cy="322" rx="2.2" ry="2.2" fill={hexA(accent2, 0.35)} />
        </g>

        {/* ================= WATER — bottom band ================= */}
        {waves.map((w) => (
          <path
            key={`poolm-wave-${w.i}`}
            d={w.d}
            stroke="url(#poolm-water-fade)"
            strokeWidth={w.w}
          />
        ))}
        <path
          d="M -6 392 q 20 -6 40 0 t 40 0 t 40 0 t 40 0 t 40 0 t 40 0 t 40 0 t 40 0"
          stroke={hexA(accent2, 0.22)}
          strokeWidth={1.3 * u}
        />
      </g>
    </svg>
  );
}

// ─── BbqCookoutMotif — kettle grill with lid, vents, wheels and three rising smoke curls (top-left), crossed spat
function BbqCookoutMotif({ color, accent, accent2, scale = 300 }) {
  const k = Math.max(0.8, Math.min(1.25, 300 / scale));
  const ink = hexA(color, 0.5);
  const inkSoft = hexA(color, 0.32);
  const emb = hexA(accent, 0.55);
  const warm = hexA(accent, 0.26);
  const warm2 = hexA(accent2, 0.3);
  const neutral = hexA(color, 0.11);
  const sw = 1.5 * k;
  const thin = 1 * k;

  const smoke = [
    { x: 36, y: 50, a: 5.5, h: 32 },
    { x: 62, y: 51, a: 6.5, h: 30 },
    { x: 71, y: 57, a: 4.2, h: 21 }
  ];

  const kernelRows = [
    { y: -20, w: 7 },
    { y: -10, w: 8.9 },
    { y: 0, w: 9.6 },
    { y: 10, w: 9.1 },
    { y: 20, w: 7 }
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <pattern id="bbq-cookout-check" width="14" height="14" patternUnits="userSpaceOnUse">
          <rect width="7" height="7" fill={hexA(accent, 0.3)} />
          <rect x="7" y="7" width="7" height="7" fill={hexA(accent, 0.3)} />
          <rect x="7" width="7" height="7" fill={hexA(color, 0.12)} />
          <rect y="7" width="7" height="7" fill={hexA(color, 0.12)} />
        </pattern>
      </defs>

      {/* picnic-check bands, top + bottom edge */}
      <rect x="0" y="0" width="300" height="14" fill="url(#bbq-cookout-check)" />
      <rect x="0" y="14.6" width="300" height="0.9" fill={inkSoft} />
      <rect x="0" y="386" width="300" height="14" fill="url(#bbq-cookout-check)" />
      <rect x="0" y="384.5" width="300" height="0.9" fill={inkSoft} />

      {/* ---- top-left: kettle grill + smoke ---- */}
      <g fill="none" stroke={ink} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
        {smoke.map((s, i) => (
          <path
            key={`bbq-smoke-${i}`}
            d={`M${s.x},${s.y} C${s.x - s.a},${s.y - s.h * 0.28} ${s.x + s.a},${s.y - s.h * 0.5} ${s.x - s.a * 0.5},${s.y - s.h * 0.72} C${s.x - s.a * 1.4},${s.y - s.h * 0.86} ${s.x + s.a * 0.35},${s.y - s.h * 0.95} ${s.x - s.a * 0.15},${s.y - s.h}`}
            strokeWidth={thin * 1.2}
            opacity={0.72 - i * 0.14}
          />
        ))}
        <path d="M23,72 A26,24 0 0 1 75,72 Z" fill={neutral} />
        <rect x="44" y="40.5" width="10" height="4.5" rx="2.2" fill={warm} />
        <path d="M49,45 L49,48" strokeWidth={thin} />
        <path d="M19,73.6 L79,73.6" strokeWidth={sw * 1.25} />
        <path d="M25,75.5 A24,19 0 0 0 73,75.5 Z" fill={warm} />
        <path d="M32,90 L23,110" />
        <path d="M49,94 L49,112" />
        <path d="M66,90 L75,110" />
        <circle cx="23" cy="111" r="3.2" fill={neutral} />
        <circle cx="75" cy="111" r="3.2" fill={neutral} />
      </g>
      <g fill={emb} stroke="none">
        <circle cx="42" cy="57" r="1.3" />
        <circle cx="49" cy="54" r="1.3" />
        <circle cx="56" cy="57" r="1.3" />
      </g>

      {/* ---- top-right: crossed tongs + spatula crest ---- */}
      <g transform="translate(252,66)" fill="none" stroke={ink} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
        <g transform="rotate(20)">
          <rect x="-2.2" y="0" width="4.4" height="42" rx="2.2" fill={neutral} />
          <circle cx="0" cy="37" r="1.5" strokeWidth={thin} />
          <rect x="-2.2" y="-12" width="4.4" height="14" fill={neutral} />
          <rect x="-10" y="-38" width="20" height="28" rx="4" fill={warm} />
          <path d="M-6,-31.5 L6,-31.5" strokeWidth={thin} />
          <path d="M-6,-24 L6,-24" strokeWidth={thin} />
          <path d="M-6,-16.5 L6,-16.5" strokeWidth={thin} />
        </g>
        <g transform="rotate(-20)">
          <path d="M-3.6,-32.5 A4.4,4.8 0 0 1 3.6,-32.5" strokeWidth={thin * 1.3} />
          <path d="M0,-33 C-5,-21 -7,-4 -5.2,20" />
          <path d="M0,-33 C5,-21 7,-4 5.2,20" />
          <path d="M-5.2,20 C-6.4,25 -6.6,28 -5.4,30.5" strokeWidth={sw * 1.7} />
          <path d="M5.2,20 C6.4,25 6.6,28 5.4,30.5" strokeWidth={sw * 1.7} />
        </g>
      </g>

      {/* ---- left margin: corn on the cob ---- */}
      <g transform="translate(30,214) rotate(-8)" fill="none" stroke={ink} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
        <path d="M-3,27 C-13,33 -17,45 -11,52 C-5,45 -3,34 -3,27 Z" fill={warm2} />
        <path d="M3,27 C12,34 15,46 9,52 C4,45 3,34 3,27 Z" fill={warm2} />
        <path d="M0,-32 C7,-26 10,-12 10,2 C10,18 6,30 0,30 C-6,30 -10,18 -10,2 C-10,-12 -7,-26 0,-32 Z" fill={warm} />
        {kernelRows.map((r, i) => (
          <path
            key={`bbq-corn-row-${i}`}
            d={`M${-r.w},${r.y} Q0,${r.y + 4.5} ${r.w},${r.y}`}
            strokeWidth={thin * 0.9}
            opacity="0.75"
          />
        ))}
        <path d="M-4.6,-27 C-6.4,-12 -6.4,12 -4.2,27" strokeWidth={thin * 0.9} opacity="0.75" />
        <path d="M4.6,-27 C6.4,-12 6.4,12 4.2,27" strokeWidth={thin * 0.9} opacity="0.75" />
      </g>

      {/* ---- right margin: barbecue sauce bottle ---- */}
      <g transform="translate(266,212) rotate(6)" fill="none" stroke={ink} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
        <rect x="-6" y="-36.5" width="12" height="9" rx="2.5" fill={neutral} />
        <path d="M-11,-4 C-11,-12 -5,-15 -5,-21 L-5,-28 L5,-28 L5,-21 C5,-15 11,-12 11,-4 L11,30 C11,34 8,36 4,36 L-4,36 C-8,36 -11,34 -11,30 Z" fill={warm} />
        <rect x="-8" y="4" width="16" height="20" rx="2.5" fill={hexA(accent2, 0.24)} />
        <path d="M-4.5,11 L4.5,11" strokeWidth={thin * 0.9} />
        <path d="M-4.5,17 L2,17" strokeWidth={thin * 0.9} />
      </g>

      {/* ---- bottom-left: burger ---- */}
      <g transform="translate(50,344)" fill="none" stroke={ink} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
        <path d="M-25,0 C-25,-14 -14,-23 0,-23 C14,-23 25,-14 25,0 Z" fill={warm} />
        <ellipse cx="-11" cy="-11" rx="2.4" ry="1.4" transform="rotate(-22 -11 -11)" fill={neutral} strokeWidth={thin * 0.8} />
        <ellipse cx="1" cy="-16.5" rx="2.4" ry="1.4" fill={neutral} strokeWidth={thin * 0.8} />
        <ellipse cx="12" cy="-10" rx="2.4" ry="1.4" transform="rotate(20 12 -10)" fill={neutral} strokeWidth={thin * 0.8} />
        <path d="M-26,1.5 q5,6 9.5,0 q5,6 9.5,0 q5,6 9.5,0 q5,6 9.5,0" strokeWidth={thin * 1.3} stroke={hexA(accent2, 0.5)} />
        <rect x="-23" y="7" width="46" height="10.5" rx="5.2" fill={hexA(accent, 0.34)} />
        <path d="M-22.5,20 L22.5,20 C22.5,28.5 16,33 8,33 L-8,33 C-16,33 -22.5,28.5 -22.5,20 Z" fill={warm} />
      </g>

      {/* ---- bottom-right: hot dog ---- */}
      <g transform="translate(250,344) rotate(-8)" fill="none" stroke={ink} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
        <path d="M-23,-4 C-23,-10 -16,-12.5 0,-12.5 C16,-12.5 23,-10 23,-4 C23,0.5 16,2 0,2 C-16,2 -23,0.5 -23,-4 Z" fill={hexA(accent, 0.34)} />
        <path d="M-27,-2 C-27,-6.5 -24,-8.5 -20,-7 C-12,-4 12,-4 20,-7 C24,-8.5 27,-6.5 27,-2 C27,8 20,14 0,14 C-20,14 -27,8 -27,-2 Z" fill={warm} />
        <path d="M-14.5,-7 l5,-4 l5,4 l5,-4 l5,4 l4,-3" strokeWidth={thin * 1.5} stroke={hexA(accent2, 0.55)} />
      </g>
    </svg>
  );
}

// ─── PicnicMotif — woven picnic basket with lid, handle and weave texture, baguette angled out of the basket 
function PicnicMotif({ color, accent, accent2, scale = 300 }) {
  const k = Math.max(0.8, Math.min(1.3, 300 / (scale || 300)));
  const sw = 1.15 * k;
  const ink = (a) => hexA(color, a);
  const warm = (a) => hexA(accent, a);
  const herb = (a) => hexA(accent2, a);

  const weave = [34, 42.5, 51, 59.5, 68, 76];
  const ginghamV = [-12, 0, 12, 24, 36, 48, 60, 72, 84];
  const ginghamH = [334, 346, 358, 370, 382, 394];
  const fringe = [[4.8, 340.8], [18, 339], [31.2, 340.8], [53.4, 348.4], [67.1, 349.5], [81.2, 347.4]];
  const ants = [
    { x: 104, y: 388, r: -9 },
    { x: 136, y: 381, r: -13 },
    { x: 168, y: 377, r: -5 },
    { x: 200, y: 379.5, r: 9 },
    { x: 229, y: 371, r: -14 }
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <clipPath id="picnic-blanket-clip">
          <path d="M-6 344 Q18 334 40 344 Q62 354 86 346 L94 406 L-6 406 Z" />
        </clipPath>
      </defs>

      {/* ---- top left: picnic basket, baguette, herb sprig ---- */}
      <g strokeLinecap="round" strokeLinejoin="round">
        <path d="M37 56 Q31 46 28.5 36" fill="none" stroke={herb(0.42)} strokeWidth={sw * 0.7} />
        <path d="M28.5 40.5 Q22.5 38 23.8 43.4 Q28.2 44.6 28.5 40.5 Z" fill={herb(0.2)} stroke={herb(0.4)} strokeWidth={sw * 0.55} />
        <path d="M31.5 47.5 Q37.4 44.6 36.2 50 Q31.8 51.2 31.5 47.5 Z" fill={herb(0.2)} stroke={herb(0.4)} strokeWidth={sw * 0.55} />

        <g transform="rotate(-40 78 44)">
          <rect x={64} y={39} width={30} height={10} rx={5} fill={warm(0.16)} stroke={ink(0.5)} strokeWidth={sw * 0.85} />
          <path d="M71 41.6 L69.2 46.4" fill="none" stroke={ink(0.32)} strokeWidth={sw * 0.6} />
          <path d="M78 41.4 L76.2 46.6" fill="none" stroke={ink(0.32)} strokeWidth={sw * 0.6} />
          <path d="M85 41.6 L83.2 46.4" fill="none" stroke={ink(0.32)} strokeWidth={sw * 0.6} />
        </g>

        <path d="M40 55 Q55 27 70 55" fill="none" stroke={ink(0.5)} strokeWidth={sw} />
        <path d="M27 62 H83 L76.5 96 Q55 101.5 33.5 96 Z" fill={warm(0.13)} stroke={ink(0.5)} strokeWidth={sw * 0.9} />
        {weave.map((x, i) => (
          <path
            key={`picnic-weave-${i}`}
            d={`M${x} 63.5 L${(55 + (x - 55) * 0.78).toFixed(1)} 95.5`}
            fill="none"
            stroke={ink(0.3)}
            strokeWidth={sw * 0.6}
          />
        ))}
        <path d="M29.5 74 Q55 78.5 80.5 74" fill="none" stroke={ink(0.34)} strokeWidth={sw * 0.6} />
        <path d="M31.5 86 Q55 90.5 78.5 86" fill="none" stroke={ink(0.34)} strokeWidth={sw * 0.6} />
        <rect x={24.5} y={54.5} width={61} height={9} rx={4} fill={warm(0.2)} stroke={ink(0.52)} strokeWidth={sw * 0.9} />
      </g>

      {/* ---- top right: thermos ---- */}
      <g strokeLinecap="round" strokeLinejoin="round">
        <rect x={248} y={48} width={27} height={50} rx={6} fill={herb(0.11)} stroke={ink(0.5)} strokeWidth={sw * 0.9} />
        <rect x={248} y={62} width={27} height={11} fill={warm(0.18)} stroke={ink(0.3)} strokeWidth={sw * 0.55} />
        <path d="M251.5 91.5 H271.5" fill="none" stroke={ink(0.24)} strokeWidth={sw * 0.55} />
        <rect x={252.5} y={41.5} width={18} height={7} rx={2} fill={ink(0.1)} stroke={ink(0.45)} strokeWidth={sw * 0.7} />
        <rect x={250.5} y={27} width={22} height={15} rx={3.5} fill={warm(0.16)} stroke={ink(0.5)} strokeWidth={sw * 0.85} />
      </g>

      {/* ---- right margin: cherries ---- */}
      <g strokeLinecap="round" strokeLinejoin="round">
        <path d="M262 179 Q266 169 273.5 165.5" fill="none" stroke={herb(0.45)} strokeWidth={sw * 0.65} />
        <path d="M277 182 Q276 172 273.8 165.8" fill="none" stroke={herb(0.45)} strokeWidth={sw * 0.65} />
        <path d="M273.8 166 Q281 159.5 284.5 165.6 Q278.5 170.4 273.8 166 Z" fill={herb(0.22)} stroke={herb(0.42)} strokeWidth={sw * 0.55} />
        <circle cx={262} cy={184.5} r={5.6} fill={warm(0.2)} stroke={ink(0.46)} strokeWidth={sw * 0.75} />
        <circle cx={277} cy={187.5} r={5} fill={warm(0.16)} stroke={ink(0.46)} strokeWidth={sw * 0.75} />
      </g>

      {/* ---- left margin: meadow herb sprig ---- */}
      <g strokeLinecap="round" strokeLinejoin="round">
        <path d="M30.5 240 Q26 205 32 172" fill="none" stroke={ink(0.36)} strokeWidth={sw * 0.7} />
        <path d="M29.5 219 Q22.5 210 20.5 199" fill="none" stroke={ink(0.32)} strokeWidth={sw * 0.6} />
        <path d="M29 205 Q36.5 196.5 40 187" fill="none" stroke={ink(0.32)} strokeWidth={sw * 0.6} />
        <ellipse cx={32} cy={171} rx={2.6} ry={4.4} fill={herb(0.22)} stroke={herb(0.42)} strokeWidth={sw * 0.5} />
        <ellipse cx={20} cy={197} rx={2.2} ry={3.4} fill={herb(0.2)} stroke={herb(0.4)} strokeWidth={sw * 0.5} />
        <ellipse cx={40.5} cy={185.5} rx={2.2} ry={3.4} fill={herb(0.2)} stroke={herb(0.4)} strokeWidth={sw * 0.5} />
        <ellipse cx={27.6} cy={212} rx={1.8} ry={2.6} fill={herb(0.18)} stroke={herb(0.36)} strokeWidth={sw * 0.45} />
        <ellipse cx={31.8} cy={190} rx={1.8} ry={2.6} fill={herb(0.18)} stroke={herb(0.36)} strokeWidth={sw * 0.45} />
      </g>

      {/* ---- bottom left: checked blanket corner ---- */}
      <g>
        <path d="M-6 344 Q18 334 40 344 Q62 354 86 346 L94 406 L-6 406 Z" fill={warm(0.1)} />
        <g clipPath="url(#picnic-blanket-clip)" transform="rotate(-9 40 372)">
          {ginghamV.map((x, i) => (
            <path
              key={`picnic-gv-${i}`}
              d={`M${x} 318 L${x} 412`}
              stroke={ink(i % 2 === 0 ? 0.3 : 0.16)}
              strokeWidth={i % 2 === 0 ? sw * 1.1 : sw * 0.55}
            />
          ))}
          {ginghamH.map((y, i) => (
            <path
              key={`picnic-gh-${i}`}
              d={`M-24 ${y} L112 ${y}`}
              stroke={ink(i % 2 === 0 ? 0.3 : 0.16)}
              strokeWidth={i % 2 === 0 ? sw * 1.1 : sw * 0.55}
            />
          ))}
        </g>
        <path d="M-6 344 Q18 334 40 344 Q62 354 86 346" fill="none" stroke={ink(0.5)} strokeWidth={sw} strokeLinecap="round" />
        {fringe.map((p, i) => (
          <path
            key={`picnic-fringe-${i}`}
            d={`M${p[0]} ${p[1]} L${(p[0] + (i % 2 === 0 ? 0.8 : -0.8)).toFixed(1)} ${p[1] - 5}`}
            stroke={ink(0.34)}
            strokeWidth={sw * 0.6}
            strokeLinecap="round"
          />
        ))}
      </g>

      {/* ---- bottom right: wedge of cheese ---- */}
      <g strokeLinejoin="round">
        <path d="M247 361.5 L287 338 L287 361.5 Z" fill={warm(0.17)} stroke={ink(0.5)} strokeWidth={sw * 0.9} />
        <path d="M287 338 L287 361.5 L283 361.5 L283 340.3 Z" fill={warm(0.24)} stroke={ink(0.3)} strokeWidth={sw * 0.5} />
        <circle cx={270} cy={352.5} r={2.9} fill="none" stroke={ink(0.38)} strokeWidth={sw * 0.6} />
        <circle cx={261} cy={357.4} r={2.1} fill="none" stroke={ink(0.34)} strokeWidth={sw * 0.55} />
        <circle cx={276.5} cy={349.5} r={1.7} fill="none" stroke={ink(0.32)} strokeWidth={sw * 0.5} />
      </g>

      {/* ---- bottom edge: the ant trail ---- */}
      {ants.map((a, i) => (
        <g key={`picnic-ant-${i}`} transform={`translate(${a.x} ${a.y}) rotate(${a.r})`}>
          <ellipse cx={2.6} cy={0} rx={2.3} ry={1.6} fill={ink(0.42)} />
          <circle cx={-1.9} cy={-0.2} r={1.25} fill={ink(0.42)} />
          <path
            d="M0.2 -0.7 L-0.9 -2.4 M0.6 0.7 L-0.1 2.5 M2.4 -1.4 L2.9 -3.2 M3.2 1.3 L4.2 2.9 M-2.7 -1.1 L-4.1 -2.4"
            fill="none"
            stroke={ink(0.36)}
            strokeWidth={0.55 * k}
            strokeLinecap="round"
          />
        </g>
      ))}
    </svg>
  );
}

// ─── GraduationLaurelMotif — mortarboard with swinging tassel (top-left hero), rolled diploma tied with a ribbon (top-r
function GraduationLaurelMotif({ color, accent, accent2, scale = 300 }) {
  const sw = Math.max(1, Math.min(1.7, 1.35 * (300 / (scale || 300))));
  const thin = sw * 0.72;

  const LEAF = "M0 0C3.6-4.2 8.8-5 12.4-2.2C8.4 0.8 3.6 2.6 0 0Z";

  // laurel sprig climbing the left margin (mirrored to the right margin)
  const sprig = [
    { x: 31, y: 295, r: -42, s: 1 },
    { x: 28.5, y: 281, r: -137, s: 0.94 },
    { x: 26, y: 266, r: -46, s: 0.92 },
    { x: 23.5, y: 250, r: -133, s: 0.88 },
    { x: 21, y: 233, r: -39, s: 0.84 },
    { x: 19, y: 216, r: -141, s: 0.8 },
    { x: 17.5, y: 199, r: -48, s: 0.74 },
    { x: 16.5, y: 183, r: -130, s: 0.68 },
    { x: 15.5, y: 172, r: -88, s: 0.58 }
  ];

  // half of an open laurel crown along the bottom edge (mirrored)
  const crown = [
    { x: 141, y: 379, r: -108, s: 0.9 },
    { x: 131, y: 375, r: -186, s: 0.86 },
    { x: 122, y: 369, r: -116, s: 0.86 },
    { x: 114, y: 362, r: -192, s: 0.8 },
    { x: 107, y: 354, r: -124, s: 0.76 },
    { x: 104, y: 348, r: -152, s: 0.6 }
  ];

  const confetti = [
    { x: 104, y: 30, t: "dot", r: 0, s: 1, c: 0 },
    { x: 126, y: 52, t: "bar", r: 28, s: 1, c: 1 },
    { x: 150, y: 24, t: "tri", r: -14, s: 1, c: 2 },
    { x: 168, y: 62, t: "ring", r: 0, s: 1, c: 0 },
    { x: 186, y: 34, t: "bar", r: -38, s: 0.9, c: 2 },
    { x: 112, y: 92, t: "dot", r: 0, s: 0.8, c: 1 },
    { x: 196, y: 96, t: "tri", r: 22, s: 0.8, c: 1 },
    { x: 146, y: 84, t: "ring", r: 0, s: 0.75, c: 2 },
    { x: 62, y: 100, t: "dot", r: 0, s: 0.85, c: 2 },
    { x: 246, y: 108, t: "bar", r: 52, s: 0.85, c: 0 },
    { x: 62, y: 336, t: "dot", r: 0, s: 0.9, c: 1 },
    { x: 84, y: 372, t: "bar", r: -24, s: 0.9, c: 0 },
    { x: 210, y: 342, t: "ring", r: 0, s: 0.85, c: 2 },
    { x: 236, y: 370, t: "tri", r: 16, s: 0.85, c: 1 },
    { x: 258, y: 330, t: "dot", r: 0, s: 1, c: 0 },
    { x: 46, y: 356, t: "tri", r: -30, s: 0.8, c: 2 }
  ];

  const sparkles = [
    { x: 150, y: 367, s: 1.05, c: 1 },
    { x: 92, y: 20, s: 0.8, c: 2 },
    { x: 262, y: 116, s: 0.72, c: 0 }
  ];

  const palette = [color, accent, accent2];

  const leaves = (list, tag) =>
    list.map((l, i) => (
      <path
        key={`gradpk-${tag}-${i}`}
        d={LEAF}
        fill={hexA(i % 2 === 0 ? color : accent2, 0.2)}
        stroke={hexA(color, 0.46)}
        strokeWidth={thin}
        strokeLinejoin="round"
        transform={`translate(${l.x} ${l.y}) rotate(${l.r}) scale(${l.s})`}
      />
    ));

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id="gradpk-board-face" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={hexA(color, 0.28)} />
          <stop offset="100%" stopColor={hexA(accent, 0.1)} />
        </linearGradient>
      </defs>

      {/* left margin laurel */}
      <g>
        <path
          d="M34 308C28 288 21 258 17 228C14 206 14 190 15.5 173"
          fill="none"
          stroke={hexA(color, 0.4)}
          strokeWidth={thin}
          strokeLinecap="round"
        />
        {leaves(sprig, "sl")}
      </g>

      {/* right margin laurel (mirrored) */}
      <g transform="translate(300 0) scale(-1 1)">
        <path
          d="M34 308C28 288 21 258 17 228C14 206 14 190 15.5 173"
          fill="none"
          stroke={hexA(color, 0.4)}
          strokeWidth={thin}
          strokeLinecap="round"
        />
        {leaves(sprig, "sr")}
      </g>

      {/* bottom laurel crown */}
      <g>
        <path
          d="M150 381C134 379 118 371 106 356"
          fill="none"
          stroke={hexA(color, 0.4)}
          strokeWidth={thin}
          strokeLinecap="round"
        />
        {leaves(crown, "cl")}
      </g>
      <g transform="translate(300 0) scale(-1 1)">
        <path
          d="M150 381C134 379 118 371 106 356"
          fill="none"
          stroke={hexA(color, 0.4)}
          strokeWidth={thin}
          strokeLinecap="round"
        />
        {leaves(crown, "cr")}
      </g>

      {/* confetti drift */}
      {confetti.map((c, i) => {
        const col = palette[c.c];
        const key = `gradpk-cf-${i}`;
        if (c.t === "dot")
          return <circle key={key} cx={c.x} cy={c.y} r={2.3 * c.s} fill={hexA(col, 0.42)} />;
        if (c.t === "ring")
          return (
            <circle
              key={key}
              cx={c.x}
              cy={c.y}
              r={2.8 * c.s}
              fill="none"
              stroke={hexA(col, 0.45)}
              strokeWidth={thin}
            />
          );
        if (c.t === "bar")
          return (
            <rect
              key={key}
              x={-3.6 * c.s}
              y={-1.1 * c.s}
              width={7.2 * c.s}
              height={2.2 * c.s}
              rx={1.1 * c.s}
              fill={hexA(col, 0.4)}
              transform={`translate(${c.x} ${c.y}) rotate(${c.r})`}
            />
          );
        return (
          <path
            key={key}
            d="M0-3.2L3 2.4L-3 2.4Z"
            fill={hexA(col, 0.36)}
            transform={`translate(${c.x} ${c.y}) rotate(${c.r}) scale(${c.s})`}
          />
        );
      })}

      {/* sparkles */}
      {sparkles.map((p, i) => (
        <path
          key={`gradpk-sp-${i}`}
          d="M0-7C1-2 2-1 7 0C2 1 1 2 0 7C-1 2-2 1-7 0C-2-1-1-2 0-7Z"
          fill={hexA(palette[p.c], 0.42)}
          transform={`translate(${p.x} ${p.y}) scale(${p.s})`}
        />
      ))}

      {/* mortarboard with tassel — top left */}
      <g transform="translate(52 54) rotate(-7)">
        <path
          d="M-12.5 11C-12.5 27 12.5 27 12.5 11"
          fill={hexA(accent, 0.14)}
          stroke={hexA(color, 0.45)}
          strokeWidth={thin}
        />
        <path
          d="M-36 0L-36 3.4L0 17L36 3.4L36 0L0 13.5Z"
          fill={hexA(color, 0.24)}
          stroke={hexA(color, 0.42)}
          strokeWidth={thin}
          strokeLinejoin="round"
        />
        <path
          d="M0-13.5L36 0L0 13.5L-36 0Z"
          fill="url(#gradpk-board-face)"
          stroke={hexA(color, 0.55)}
          strokeWidth={sw}
          strokeLinejoin="round"
        />
        <path d="M-19 3.6L19 3.6" stroke={hexA(color, 0.16)} strokeWidth={thin} fill="none" />
        <circle cx="0" cy="0" r="2.7" fill={hexA(accent, 0.5)} stroke={hexA(color, 0.4)} strokeWidth={thin} />
        <path
          d="M0 0C12 3 24 4 32.5-1.5C34.6 4 34.2 9 33.2 13"
          fill="none"
          stroke={hexA(accent, 0.55)}
          strokeWidth={sw}
          strokeLinecap="round"
        />
        <circle cx="33.2" cy="14.6" r="2.2" fill={hexA(accent, 0.45)} />
        <path
          d="M29.8 15.6L36.6 15.6L34.8 27.6L31.6 27.6Z"
          fill={hexA(accent, 0.3)}
          stroke={hexA(accent, 0.42)}
          strokeWidth={thin}
          strokeLinejoin="round"
        />
        <path d="M32.5 17.2L32.1 26.6" stroke={hexA(accent, 0.34)} strokeWidth={thin} fill="none" />
        <path d="M34.2 17.2L34.4 26.6" stroke={hexA(accent, 0.34)} strokeWidth={thin} fill="none" />
      </g>

      {/* rolled diploma with ribbon — top right */}
      <g transform="translate(240 62) rotate(13)">
        <path
          d="M-27-7.5L27-7.5A3.6 7.5 0 0 1 27 7.5L-27 7.5A3.6 7.5 0 0 0 -27-7.5Z"
          fill={hexA(color, 0.14)}
          stroke={hexA(color, 0.5)}
          strokeWidth={sw}
          strokeLinejoin="round"
        />
        <path d="M-23-5.4L19-5.4" stroke={hexA(color, 0.2)} strokeWidth={thin} fill="none" />
        <ellipse cx="27" cy="0" rx="3.6" ry="7.5" fill={hexA(color, 0.2)} stroke={hexA(color, 0.45)} strokeWidth={thin} />
        <path d="M27-3.6A1.9 3.6 0 1 1 27 3.6" fill="none" stroke={hexA(color, 0.4)} strokeWidth={thin} />
        <path
          d="M-3.6 8.4C-6 13-8 16-9.6 20L-3 16.4Z"
          fill={hexA(accent, 0.26)}
          stroke={hexA(accent, 0.38)}
          strokeWidth={thin}
          strokeLinejoin="round"
        />
        <path
          d="M3.6 8.4C5.6 13 7 15.6 8.6 19.6L2.6 16.4Z"
          fill={hexA(accent, 0.26)}
          stroke={hexA(accent, 0.38)}
          strokeWidth={thin}
          strokeLinejoin="round"
        />
        <rect
          x="-6.5"
          y="-9"
          width="13"
          height="18"
          rx="1.6"
          fill={hexA(accent, 0.32)}
          stroke={hexA(accent, 0.46)}
          strokeWidth={thin}
        />
      </g>
    </svg>
  );
}

// ─── ChristmasMotif — fir sprig cluster, top-left (a long sprig crossed by a shorter twig, straight swept-back n
function ChristmasMotif({ color, accent, accent2, scale = 300 }) {
  const a1 = accent || color;
  const c2 = accent2 || accent || color;
  const k = Math.max(0.75, Math.min(1.3, 300 / scale));

  // fir sprigs: straight needles swept back 150deg off the stem
  const SWEEP = 2.62;
  const sprigs = [
    { id: "tl-a", x: -6, y: 8, a: 36, len: 88, nl: 15, w: 1.1, o: 0.5, n: 16 },
    { id: "tl-b", x: -6, y: 44, a: 10, len: 58, nl: 10.5, w: 0.8, o: 0.32, n: 11 },
    { id: "br-a", x: 306, y: 396, a: 207, len: 90, nl: 15, w: 1.1, o: 0.48, n: 16 }
  ];

  const baubles = [
    { id: "bb-1", x: 206, cy: 60, r: 9.5, o: 0.5, zig: true },
    { id: "bb-2", x: 234, cy: 87, r: 7, o: 0.4, zig: false },
    { id: "bb-3", x: 260, cy: 42, r: 11, o: 0.55, zig: true }
  ];

  const flakes = [
    { id: "fl-1", x: 26, y: 192, r: 8, rot: 0, o: 0.42 },
    { id: "fl-2", x: 36, y: 256, r: 5.5, rot: 20, o: 0.32 },
    { id: "fl-3", x: 80, y: 74, r: 6.5, rot: 12, o: 0.36 },
    { id: "fl-4", x: 270, y: 182, r: 7.5, rot: 8, o: 0.42 },
    { id: "fl-5", x: 88, y: 322, r: 5.5, rot: 25, o: 0.3 },
    { id: "fl-6", x: 196, y: 372, r: 6, rot: 14, o: 0.34 }
  ];

  const sparks = [
    { id: "sp-1", x: 96, y: 28, r: 5.5, o: 0.42 },
    { id: "sp-2", x: 248, y: 104, r: 4, o: 0.32 },
    { id: "sp-3", x: 30, y: 308, r: 4.5, o: 0.34 }
  ];

  // spiked holly leaf, drawn once and reused
  const leaf =
    "M0 0Q3.8 -1.9 7.5 -10.6Q11.3 -4 15 -13.2Q18.8 -3.9 22.5 -9.3Q26.3 -1.7 30 0" +
    "Q26.3 0.2 22.5 6.5Q18.8 0.2 15 9.2Q11.3 0.3 7.5 7.7Q3.8 0.4 0 0Z";
  const vein = "M2.5 -0.4Q15 -2.4 27.5 -0.6";
  const cane = "M278 313L278 272C278 258 256 256 253 268C251.5 274 255 279 258 280";
  const spark = (s) =>
    "M" + s.x + " " + (s.y - s.r) +
    "Q" + s.x + " " + s.y + " " + (s.x + s.r) + " " + s.y +
    "Q" + s.x + " " + s.y + " " + s.x + " " + (s.y + s.r) +
    "Q" + s.x + " " + s.y + " " + (s.x - s.r) + " " + s.y +
    "Q" + s.x + " " + s.y + " " + s.x + " " + (s.y - s.r) + "Z";
  const zigzag = (b) => {
    const w = b.r * 0.88;
    const y = b.cy - b.r * 0.18;
    const h = b.r * 0.26;
    let d = "M" + (b.x - w).toFixed(1) + " " + y.toFixed(1);
    for (let i = 1; i <= 4; i++) {
      d += "L" + (b.x - w + (2 * w * i) / 4).toFixed(1) + " " + (y + (i % 2 ? h : -h)).toFixed(1);
    }
    return d;
  };

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      {/* ---------- fir sprigs: top-left pair + bottom-right ---------- */}
      {sprigs.map((s) => {
        const r = (s.a * Math.PI) / 180;
        const dx = Math.cos(r);
        const dy = Math.sin(r);
        const px = -dy;
        const py = dx;
        const ex = s.x + dx * s.len;
        const ey = s.y + dy * s.len;
        const sw = Math.sin(SWEEP);
        const cw = Math.cos(SWEEP);
        const needles = Array.from({ length: s.n }, (_, i) => {
          const t = (i + 0.5) / (s.n + 0.25);
          const bx = s.x + dx * s.len * t;
          const by = s.y + dy * s.len * t;
          const L = s.nl * (1 - 0.3 * t);
          const b = "M" + bx.toFixed(1) + " " + by.toFixed(1);
          return (
            b + "L" + (bx + (px * sw + dx * cw) * L).toFixed(1) + " " +
            (by + (py * sw + dy * cw) * L).toFixed(1) +
            b + "L" + (bx + (-px * sw + dx * cw) * L).toFixed(1) + " " +
            (by + (-py * sw + dy * cw) * L).toFixed(1)
          );
        }).join("");
        return (
          <g key={s.id} fill="none" strokeLinecap="round">
            <path
              d={"M" + s.x + " " + s.y + "L" + ex.toFixed(1) + " " + ey.toFixed(1)}
              stroke={hexA(color, s.o)}
              strokeWidth={1.4 * k * s.w}
            />
            <path d={needles} stroke={hexA(color, s.o * 0.8)} strokeWidth={0.8 * k * s.w} />
            <circle cx={ex.toFixed(1)} cy={ey.toFixed(1)} r={1.4 * s.w} fill={hexA(a1, s.o * 0.7)} />
          </g>
        );
      })}

      {/* ---------- baubles hanging from the top edge ---------- */}
      {baubles.map((b) => (
        <g key={b.id} fill="none" strokeLinecap="round">
          <path
            d={"M" + b.x + " -2L" + b.x + " " + (b.cy - b.r - 5)}
            stroke={hexA(color, b.o * 0.55)}
            strokeWidth={0.75 * k}
          />
          <rect
            x={b.x - 2.3}
            y={b.cy - b.r - 5.4}
            width={4.6}
            height={5.2}
            rx={1.2}
            fill={hexA(a1, b.o * 0.45)}
            stroke={hexA(a1, b.o * 0.8)}
            strokeWidth={0.7 * k}
          />
          <circle
            cx={b.x}
            cy={b.cy}
            r={b.r}
            fill={hexA(a1, 0.09)}
            stroke={hexA(a1, b.o)}
            strokeWidth={1.1 * k}
          />
          <path
            d={
              b.zig
                ? zigzag(b)
                : "M" + (b.x - b.r * 0.86).toFixed(1) + " " + (b.cy - b.r * 0.3).toFixed(1) +
                  "L" + (b.x + b.r * 0.86).toFixed(1) + " " + (b.cy - b.r * 0.3).toFixed(1)
            }
            stroke={hexA(c2, b.o * 0.8)}
            strokeWidth={0.85 * k}
            strokeDasharray={b.zig ? undefined : "1.6 2.4"}
          />
          <path
            d={
              "M" + (b.x - b.r * 0.55).toFixed(1) + " " + (b.cy - b.r * 0.55).toFixed(1) +
              "A" + (b.r * 0.72).toFixed(1) + " " + (b.r * 0.72).toFixed(1) + " 0 0 1 " +
              (b.x - b.r * 0.02).toFixed(1) + " " + (b.cy - b.r * 0.82).toFixed(1)
            }
            stroke={hexA(a1, b.o * 0.6)}
            strokeWidth={0.75 * k}
          />
        </g>
      ))}

      {/* ---------- holly sprig with berries, bottom-left ---------- */}
      <g transform="translate(10 352) scale(0.86)" strokeLinejoin="round">
        <path
          d="M-5 1Q-2.5 0 0 0"
          fill="none"
          stroke={hexA(color, 0.4)}
          strokeWidth={1 * k}
          strokeLinecap="round"
        />
        <path d={leaf} fill={hexA(color, 0.07)} stroke={hexA(color, 0.5)} strokeWidth={1.15 * k} />
        <path d={vein} fill="none" stroke={hexA(color, 0.3)} strokeWidth={0.65 * k} />
        <g transform="translate(36 4) rotate(26)">
          <path d={leaf} fill={hexA(color, 0.06)} stroke={hexA(color, 0.44)} strokeWidth={1.15 * k} />
          <path d={vein} fill="none" stroke={hexA(color, 0.26)} strokeWidth={0.65 * k} />
        </g>
        <circle cx={34} cy={-6} r={3.2} fill={hexA(c2, 0.5)} stroke={hexA(c2, 0.62)} strokeWidth={0.6 * k} />
        <circle cx={40.5} cy={-1.6} r={2.6} fill={hexA(c2, 0.42)} stroke={hexA(c2, 0.55)} strokeWidth={0.6 * k} />
        <circle cx={33} cy={1} r={2.2} fill={hexA(c2, 0.34)} stroke={hexA(c2, 0.48)} strokeWidth={0.6 * k} />
      </g>

      {/* ---------- wrapped gift, bottom edge ---------- */}
      <g fill="none" strokeLinecap="round" strokeLinejoin="round">
        <rect
          x={112}
          y={349}
          width={44}
          height={35}
          rx={1.6}
          fill={hexA(color, 0.06)}
          stroke={hexA(color, 0.46)}
          strokeWidth={1.15 * k}
        />
        <rect
          x={107}
          y={338}
          width={54}
          height={11}
          rx={1.6}
          fill={hexA(color, 0.09)}
          stroke={hexA(color, 0.48)}
          strokeWidth={1.15 * k}
        />
        <path d="M134 349L134 384" stroke={hexA(a1, 0.46)} strokeWidth={2.2 * k} />
        <path d="M107 343.6L161 343.6" stroke={hexA(a1, 0.4)} strokeWidth={1.5 * k} />
        <path
          d="M134 338C126 335 119.5 327 125 323C130 319.5 133.5 329.5 134 338C134.5 329.5 138 319.5 143 323C148.5 327 142 335 134 338Z"
          fill={hexA(a1, 0.1)}
          stroke={hexA(a1, 0.52)}
          strokeWidth={1.05 * k}
        />
        <path d="M134 338L128.5 331M134 338L139.5 331" stroke={hexA(a1, 0.34)} strokeWidth={0.8 * k} />
        <circle cx={134} cy={338} r={1.7} fill={hexA(a1, 0.5)} stroke="none" />
      </g>

      {/* ---------- candy cane, lower right margin ---------- */}
      <g fill="none">
        <path d={cane} stroke={hexA(color, 0.3)} strokeWidth={4.6 * k} strokeLinecap="round" />
        <path
          d={cane}
          stroke={hexA(c2, 0.42)}
          strokeWidth={4.6 * k}
          strokeLinecap="butt"
          strokeDasharray="2.6 6.6"
        />
      </g>

      {/* ---------- snowflakes ---------- */}
      {flakes.map((f) => {
        const arms = Array.from({ length: 6 }, (_, i) => {
          const a = ((i * 60 + f.rot) * Math.PI) / 180;
          const ca = Math.cos(a);
          const sa = Math.sin(a);
          let d =
            "M" + f.x + " " + f.y +
            "L" + (f.x + ca * f.r).toFixed(1) + " " + (f.y + sa * f.r).toFixed(1);
          const barbs = [[0.55, 0.46], [0.82, 0.28]];
          for (let j = 0; j < barbs.length; j++) {
            const mx = f.x + ca * f.r * barbs[j][0];
            const my = f.y + sa * f.r * barbs[j][0];
            const L = f.r * barbs[j][1];
            const m = "M" + mx.toFixed(1) + " " + my.toFixed(1);
            d +=
              m + "L" + (mx + Math.cos(a - 0.7) * L).toFixed(1) + " " + (my + Math.sin(a - 0.7) * L).toFixed(1) +
              m + "L" + (mx + Math.cos(a + 0.7) * L).toFixed(1) + " " + (my + Math.sin(a + 0.7) * L).toFixed(1);
          }
          return d;
        }).join("");
        return (
          <g key={f.id} fill="none" strokeLinecap="round">
            <path d={arms} stroke={hexA(color, f.o)} strokeWidth={0.8 * k} />
            <circle cx={f.x} cy={f.y} r={1} fill={hexA(a1, f.o * 0.85)} />
          </g>
        );
      })}

      {/* ---------- sparkles ---------- */}
      {sparks.map((s) => (
        <path key={s.id} d={spark(s)} fill={hexA(a1, s.o)} />
      ))}
    </svg>
  );
}

// ─── HalloweenMotif — cobweb corner (radial spokes + sagging ring threads, top-left), spider descending on a thr
function HalloweenMotif({ color, accent, accent2, scale = 300 }) {
  const ink = color;
  const warm = accent || color;
  const glim = accent2 || accent || color;

  // stroke weights nudge up when the card renders small so hairlines survive
  const k = Math.max(0.7, Math.min(1.6, 300 / (scale || 300)));
  const hair = 0.85 * k;
  const line = 1.35 * k;

  const rad = (d) => (d * Math.PI) / 180;
  const px = (r, d) => (r * Math.cos(rad(d))).toFixed(2);
  const py = (r, d) => (r * Math.sin(rad(d))).toFixed(2);

  const webAngles = [7, 25, 45, 65, 83];
  const webRings = [27, 46, 65, 84];
  const spokeD = webAngles.map((a) => `M2 2L${px(88, a)} ${py(88, a)}`).join("");
  const webRingPaths = webRings.map((r, i) => {
    const d = webAngles
      .map((a, j) => {
        if (j === 0) return `M${px(r, a)} ${py(r, a)}`;
        const m = (a + webAngles[j - 1]) / 2;
        return `Q${px(r * 0.82, m)} ${py(r * 0.82, m)} ${px(r, a)} ${py(r, a)}`;
      })
      .join("");
    return { id: "hwn-ring-" + i, d, o: 0.4 - i * 0.035 };
  });

  const batD =
    "M-2.2 -3C-4 -4.2 -5.8 -5.8 -8.2 -6.6C-11.2 -7.6 -14.2 -6.8 -17 -4.4C-14.4 -4 -12.8 -2.4 -12.2 -0.2C-11.6 1.8 -11.2 3.6 -10 5.2C-9.2 3 -7.8 1.8 -5.8 1.7C-4 1.6 -2.8 2.7 -2 4.3C-1.4 5.5 -0.8 6.3 0 6.6C0.8 6.3 1.4 5.5 2 4.3C2.8 2.7 4 1.6 5.8 1.7C7.8 1.8 9.2 3 10 5.2C11.2 3.6 11.6 1.8 12.2 -0.2C12.8 -2.4 14.4 -4 17 -4.4C14.2 -6.8 11.2 -7.6 8.2 -6.6C5.8 -5.8 4 -4.2 2.2 -3L1.9 -7.2L0.7 -4.4C0.2 -4.9 -0.2 -4.9 -0.7 -4.4L-1.9 -7.2Z";

  const bats = [
    { id: "hwn-bat-a", x: 158, y: 52, s: 0.8, r: -13, o: 0.44 },
    { id: "hwn-bat-b", x: 196, y: 29, s: 1.05, r: 4, o: 0.56 },
    { id: "hwn-bat-c", x: 228, y: 56, s: 0.7, r: 15, o: 0.4 }
  ];

  const starD =
    "M0 -4C0.5 -1.4 1.4 -0.5 4 0C1.4 0.5 0.5 1.4 0 4C-0.5 1.4 -1.4 0.5 -4 0C-1.4 -0.5 -0.5 -1.4 0 -4Z";

  const stars = [
    { id: "hwn-star-a", x: 239, y: 21, s: 1, o: 0.5 },
    { id: "hwn-star-b", x: 285, y: 76, s: 0.72, o: 0.4 },
    { id: "hwn-star-c", x: 250, y: 90, s: 0.55, o: 0.34 },
    { id: "hwn-star-d", x: 288, y: 152, s: 0.6, o: 0.36 },
    { id: "hwn-star-e", x: 112, y: 33, s: 0.58, o: 0.36 },
    { id: "hwn-star-f", x: 28, y: 302, s: 0.62, o: 0.34 }
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <radialGradient id="hwn-lantern-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={hexA(warm, 0.3)} />
          <stop offset="100%" stopColor={hexA(warm, 0)} />
        </radialGradient>
        <radialGradient id="hwn-moon-halo" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={hexA(glim, 0.22)} />
          <stop offset="100%" stopColor={hexA(glim, 0)} />
        </radialGradient>
      </defs>

      {/* cobweb, top-left corner */}
      <g fill="none" strokeLinecap="round">
        <path d={spokeD} stroke={hexA(ink, 0.4)} strokeWidth={hair} />
        {webRingPaths.map((r) => (
          <path key={r.id} d={r.d} stroke={hexA(ink, r.o)} strokeWidth={hair} />
        ))}
      </g>

      {/* spider on a dropped thread, left margin */}
      <path
        d="M12 87C16 108 22 122 22 147"
        fill="none"
        stroke={hexA(ink, 0.32)}
        strokeWidth={hair}
        strokeLinecap="round"
      />
      <g transform="translate(22 154)">
        <ellipse cx="0" cy="0" rx="3.4" ry="4.2" fill={hexA(ink, 0.5)} />
        <circle cx="0" cy="-4.7" r="2.3" fill={hexA(ink, 0.5)} />
        <path
          d="M-2.6 -2C-6 -3.8 -8.4 -2.6 -9.6 0.2M-3 0.2C-6.6 0.2 -9 1.8 -9.8 4.4M-2.6 2.4C-5.4 3.4 -7 5.4 -7.2 8"
          fill="none"
          stroke={hexA(ink, 0.44)}
          strokeWidth={hair}
          strokeLinecap="round"
        />
        <path
          d="M2.6 -2C6 -3.8 8.4 -2.6 9.6 0.2M3 0.2C6.6 0.2 9 1.8 9.8 4.4M2.6 2.4C5.4 3.4 7 5.4 7.2 8"
          fill="none"
          stroke={hexA(ink, 0.44)}
          strokeWidth={hair}
          strokeLinecap="round"
        />
      </g>

      {/* bat flight arc across the top */}
      {bats.map((b) => (
        <path
          key={b.id}
          d={batD}
          fill={hexA(ink, b.o)}
          transform={`translate(${b.x} ${b.y}) rotate(${b.r}) scale(${b.s})`}
        />
      ))}

      {/* crescent moon, top-right */}
      <g transform="translate(262 44)">
        <circle cx="0" cy="0" r="32" fill="url(#hwn-moon-halo)" />
        <g transform="rotate(-28)">
          <path
            d="M0 -18A18 18 0 1 0 0 18A24 24 0 0 1 0 -18Z"
            fill={hexA(warm, 0.5)}
          />
          <path
            d="M0 -18A18 18 0 1 0 0 18A24 24 0 0 1 0 -18Z"
            fill="none"
            stroke={hexA(warm, 0.55)}
            strokeWidth={hair}
          />
        </g>
      </g>

      {/* sparse stars */}
      {stars.map((s) => (
        <path
          key={s.id}
          d={starD}
          fill={hexA(glim, s.o)}
          transform={`translate(${s.x} ${s.y}) scale(${s.s})`}
        />
      ))}

      {/* sheet ghost, right margin */}
      <g transform="translate(274 206)">
        <path
          d="M0 -19C8.5 -19 13 -12.5 13 -4C13 3 13.2 10 13.6 15.4C13.8 18.2 11.8 19 10.4 17.2L8.2 14.4C7.4 13.4 6.2 13.4 5.4 14.5L2.8 18C1.8 19.4 0.4 19.4 -0.6 18L-3.2 14.5C-4 13.4 -5.2 13.4 -6 14.4L-8.2 17.2C-9.8 19 -13.8 18.2 -13.6 15.4C-13.2 10 -13 3 -13 -4C-13 -12.5 -8.5 -19 0 -19Z"
          fill={hexA(glim, 0.16)}
          stroke={hexA(glim, 0.5)}
          strokeWidth={hair}
          strokeLinejoin="round"
        />
        <ellipse cx="-4.4" cy="-7.6" rx="1.9" ry="2.6" fill={hexA(ink, 0.5)} />
        <ellipse cx="4.4" cy="-7.6" rx="1.9" ry="2.6" fill={hexA(ink, 0.5)} />
        <ellipse cx="0" cy="0.6" rx="2.1" ry="3.1" fill={hexA(ink, 0.36)} />
      </g>

      {/* jack-o-lantern, bottom-left */}
      <g transform="translate(54 360)">
        <ellipse cx="0" cy="2" rx="36" ry="31" fill="url(#hwn-lantern-glow)" />
        <path
          d="M0 -20.5C12 -20.5 25.5 -13 25.5 -1C25.5 11.5 13.5 20.5 0 20.5C-13.5 20.5 -25.5 11.5 -25.5 -1C-25.5 -13 -12 -20.5 0 -20.5Z"
          fill={hexA(warm, 0.26)}
          stroke={hexA(warm, 0.55)}
          strokeWidth={line}
        />
        <path
          d="M-13 -17.6C-19 -11.5 -19.5 8 -13 17.6"
          fill="none"
          stroke={hexA(warm, 0.38)}
          strokeWidth={hair}
        />
        <path
          d="M13 -17.6C19 -11.5 19.5 8 13 17.6"
          fill="none"
          stroke={hexA(warm, 0.38)}
          strokeWidth={hair}
        />
        <path
          d="M-3 -19.8C-4.2 -24.6 -3 -28.8 1.2 -30.8C-0.4 -27.4 0 -23.8 3.2 -21.4Z"
          fill={hexA(ink, 0.45)}
        />
        <path
          d="M3.6 -22.4C8.4 -26.2 13.4 -25.2 15.4 -21C16.2 -19.4 15.4 -18 14.2 -18.6"
          fill="none"
          stroke={hexA(ink, 0.32)}
          strokeWidth={hair}
          strokeLinecap="round"
        />
        <path d="M-15 -8L-6 -8L-10.5 -0.5Z" fill={hexA(ink, 0.5)} />
        <path d="M15 -8L6 -8L10.5 -0.5Z" fill={hexA(ink, 0.5)} />
        <path d="M0 -1.5L3.2 4L-3.2 4Z" fill={hexA(ink, 0.5)} />
        <path
          d="M-14 6.5L-8.5 6.5L-6 11L-3.5 6.5L3.5 6.5L6 11L8.5 6.5L14 6.5C12 14.5 6 17.5 0 17.5C-6 17.5 -12 14.5 -14 6.5Z"
          fill={hexA(ink, 0.5)}
        />
      </g>

      {/* little gourd beside it */}
      <g transform="translate(97 378)">
        <path
          d="M0 -9.5C5.5 -9.5 11 -5.5 11 0.5C11 6.5 6 10.5 0 10.5C-6 10.5 -11 6.5 -11 0.5C-11 -5.5 -5.5 -9.5 0 -9.5Z"
          fill={hexA(warm, 0.2)}
          stroke={hexA(warm, 0.45)}
          strokeWidth={hair}
        />
        <path
          d="M-5 -8.6C-7.4 -5 -7.6 4 -5 9.4M5 -8.6C7.4 -5 7.6 4 5 9.4"
          fill="none"
          stroke={hexA(warm, 0.3)}
          strokeWidth={hair}
        />
        <path
          d="M-1.4 -9.2C-1.8 -12.4 -0.6 -14.2 2.2 -15"
          fill="none"
          stroke={hexA(ink, 0.4)}
          strokeWidth={line}
          strokeLinecap="round"
        />
      </g>

      {/* seated black cat, bottom-right */}
      <g transform="translate(252 382)">
        <path
          d="M-9.5 -2C2 2 12 -1 14 -9C15.5 -15 11 -19 8 -16.5C6.5 -15.2 6.8 -13 8.5 -12.6"
          fill="none"
          stroke={hexA(ink, 0.5)}
          strokeWidth={2.4 * k}
          strokeLinecap="round"
        />
        <path
          d="M-9 0C-13 0 -14.5 -3 -13.5 -7C-12.5 -12 -10.5 -16 -8.5 -19C-8 -21 -8 -23 -8.2 -25L-8.8 -31.5L-10.4 -38.5L-4.6 -34.6C-3 -35.2 -1 -35.2 0.6 -34.6L5.6 -38.8L4.4 -31.6C6.4 -29.6 7 -26.4 5.6 -24C4.4 -22 3.4 -20 3.4 -17C3.4 -12 5 -6 6 -2C6.4 -0.6 5.6 0 4.2 0Z"
          fill={hexA(ink, 0.52)}
        />
        <path
          d="M-7.6 -27.6L-14.6 -29.6M-7.8 -26.2L-15 -25.2M-7.4 -24.8L-13.6 -21.6"
          fill="none"
          stroke={hexA(ink, 0.34)}
          strokeWidth={hair}
          strokeLinecap="round"
        />
        <path
          d="M-5.6 -30.6C-4.8 -31.8 -3.6 -31.8 -2.8 -30.6C-3.6 -29.4 -4.8 -29.4 -5.6 -30.6Z"
          fill={hexA(warm, 0.6)}
        />
        <path
          d="M0.4 -30.8C1.2 -32 2.4 -32 3.2 -30.8C2.4 -29.6 1.2 -29.6 0.4 -30.8Z"
          fill={hexA(warm, 0.6)}
        />
      </g>
    </svg>
  );
}

// ─── NyeMidnightMotif — champagne bottle tilted mid-pop, with foil collar and label, flying cork with spray arcs a
function NyeMidnightMotif({ color, accent, accent2, scale = 300 }) {
  const w = Math.min(1.6, Math.max(0.85, 300 / scale));
  const ink = (a) => hexA(color, a);
  const gold = (a) => hexA(accent, a);
  const warm = (a) => hexA(accent2 || accent, a);

  const burst = (cx, cy, count, inner, longR, shortR, tipped) =>
    Array.from({ length: count }, (_, i) => {
      const ang = ((i * 360) / count + 12) * (Math.PI / 180);
      const out = i % 2 === 0 ? longR : shortR;
      return {
        i,
        x1: cx + Math.cos(ang) * inner,
        y1: cy + Math.sin(ang) * inner,
        x2: cx + Math.cos(ang) * out,
        y2: cy + Math.sin(ang) * out,
        tx: cx + Math.cos(ang) * (out + 6),
        ty: cy + Math.sin(ang) * (out + 6),
        tip: tipped && i % 2 === 0,
      };
    });

  const bigRays = burst(44, 344, 8, 7, 27, 18, true);
  const smallRays = burst(274, 196, 5, 3.5, 13, 9, false);

  const ticks = Array.from({ length: 12 }, (_, i) => {
    const ang = (i * 30 - 90) * (Math.PI / 180);
    const major = i % 3 === 0;
    return {
      i,
      x1: 240 + Math.cos(ang) * 19.5,
      y1: 348 + Math.sin(ang) * 19.5,
      x2: 240 + Math.cos(ang) * (major ? 14.5 : 16.8),
      y2: 348 + Math.sin(ang) * (major ? 14.5 : 16.8),
      major,
    };
  });

  const flecks = [
    [92, 28], [124, 54], [152, 16], [186, 44], [46, 38], [16, 176],
    [38, 212], [284, 166], [262, 248], [168, 384], [286, 332], [110, 392],
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <radialGradient id="nyeMid-sparkGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={gold(0.42)} />
          <stop offset="100%" stopColor={gold(0)} />
        </radialGradient>
      </defs>

      {/* champagne bottle, cork popping — top-left */}
      <g transform="translate(24 96) rotate(26)">
        <path
          d="M -9.4 2 L -9.4 -34 C -9.4 -42 -3.4 -46 -3.4 -52 L -3.4 -58 L 3.4 -58 L 3.4 -52 C 3.4 -46 9.4 -42 9.4 -34 L 9.4 2 Q 9.4 5 6.4 5 L -6.4 5 Q -9.4 5 -9.4 2 Z"
          fill={ink(0.12)}
          stroke={ink(0.52)}
          strokeWidth={1.1 * w}
          strokeLinejoin="round"
        />
        <rect x="-4.2" y="-59.5" width="8.4" height="7.5" rx="1.3" fill={gold(0.38)} />
        <path d="M -3.5 -50.5 L 3.5 -50.5" stroke={gold(0.5)} strokeWidth={1 * w} strokeLinecap="round" />
        <rect
          x="-9.4"
          y="-27"
          width="18.8"
          height="15"
          rx="1.6"
          fill={gold(0.16)}
          stroke={gold(0.46)}
          strokeWidth={0.9 * w}
        />
        <path
          d="M -5.4 -22.5 L 5.4 -22.5 M -4 -18.5 L 4 -18.5"
          stroke={ink(0.4)}
          strokeWidth={0.85 * w}
          strokeLinecap="round"
        />
        <path d="M 1 -60 C 6 -69 12 -74 18 -76.5" fill="none" stroke={gold(0.5)} strokeWidth={0.9 * w} strokeLinecap="round" />
        <path d="M 2.4 -58.5 C 9 -62.5 15 -63 20.5 -61.5" fill="none" stroke={gold(0.4)} strokeWidth={0.85 * w} strokeLinecap="round" />
        <path d="M 0 -61 C 1.6 -70 3.4 -79 4.6 -86" fill="none" stroke={gold(0.44)} strokeWidth={0.85 * w} strokeLinecap="round" />
        <circle cx="9.5" cy="-71" r="1.4" fill={warm(0.42)} />
        <circle cx="15.5" cy="-67" r="1" fill={warm(0.34)} />
        <circle cx="6.5" cy="-81.5" r="1.1" fill={warm(0.38)} />
        <g transform="translate(13.5 -77) rotate(38)">
          <path
            d="M -3.2 -4.6 L 3.2 -4.6 L 4.2 2 Q 4.2 4.9 0 4.9 Q -4.2 4.9 -4.2 2 Z"
            fill={warm(0.22)}
            stroke={ink(0.5)}
            strokeWidth={0.95 * w}
            strokeLinejoin="round"
          />
          <path d="M -3.7 -1.2 L 3.9 -1.2" stroke={ink(0.42)} strokeWidth={0.85 * w} />
        </g>
      </g>

      {/* two coupe glasses clinking — top-right */}
      <g transform="translate(234 50) rotate(-11)">
        <path d="M -10.5 -17 Q -10 -6.6 0 -6 Q 10 -6.6 10.5 -17 Z" fill={gold(0.15)} stroke={ink(0.52)} strokeWidth={1.1 * w} strokeLinejoin="round" />
        <path d="M -10.5 -17 L 10.5 -17" stroke={ink(0.5)} strokeWidth={1.1 * w} strokeLinecap="round" />
        <path d="M 0 -6 L 0 4.4" stroke={ink(0.5)} strokeWidth={1.25 * w} strokeLinecap="round" />
        <path d="M -6 5 Q 0 2.3 6 5 Q 0 7.4 -6 5 Z" fill={ink(0.14)} stroke={ink(0.5)} strokeWidth={1 * w} strokeLinejoin="round" />
        <circle cx="-3.2" cy="-12" r="1.3" fill={warm(0.4)} />
        <circle cx="1.8" cy="-13.6" r="1" fill={warm(0.32)} />
        <circle cx="4.8" cy="-10.4" r="0.85" fill={warm(0.36)} />
      </g>
      <g transform="translate(266 44) rotate(12)">
        <path d="M -10.5 -17 Q -10 -6.6 0 -6 Q 10 -6.6 10.5 -17 Z" fill={gold(0.15)} stroke={ink(0.52)} strokeWidth={1.1 * w} strokeLinejoin="round" />
        <path d="M -10.5 -17 L 10.5 -17" stroke={ink(0.5)} strokeWidth={1.1 * w} strokeLinecap="round" />
        <path d="M 0 -6 L 0 4.4" stroke={ink(0.5)} strokeWidth={1.25 * w} strokeLinecap="round" />
        <path d="M -6 5 Q 0 2.3 6 5 Q 0 7.4 -6 5 Z" fill={ink(0.14)} stroke={ink(0.5)} strokeWidth={1 * w} strokeLinejoin="round" />
        <circle cx="3.2" cy="-12" r="1.3" fill={warm(0.4)} />
        <circle cx="-1.8" cy="-13.6" r="1" fill={warm(0.32)} />
        <circle cx="-4.8" cy="-10.4" r="0.85" fill={warm(0.36)} />
      </g>
      <path
        transform="translate(251 31) scale(0.95)"
        d="M 0 -6.5 Q 1 -1 6.5 0 Q 1 1 0 6.5 Q -1 1 -6.5 0 Q -1 -1 0 -6.5 Z"
        fill={gold(0.5)}
      />

      {/* firework starburst — bottom-left */}
      <circle cx="44" cy="344" r="17" fill="url(#nyeMid-sparkGlow)" />
      {bigRays.map((r) => (
        <line
          key={`nyeMid-ray-${r.i}`}
          x1={r.x1}
          y1={r.y1}
          x2={r.x2}
          y2={r.y2}
          stroke={r.i % 2 === 0 ? gold(0.55) : ink(0.4)}
          strokeWidth={r.i % 2 === 0 ? 1.15 * w : 0.95 * w}
          strokeLinecap="round"
        />
      ))}
      {bigRays.filter((r) => r.tip).map((r) => (
        <circle key={`nyeMid-tip-${r.i}`} cx={r.tx} cy={r.ty} r="1.6" fill={warm(0.45)} />
      ))}
      <circle cx="44" cy="344" r="3.2" fill={gold(0.5)} />

      {/* small spark — right margin */}
      {smallRays.map((r) => (
        <line
          key={`nyeMid-sray-${r.i}`}
          x1={r.x1}
          y1={r.y1}
          x2={r.x2}
          y2={r.y2}
          stroke={gold(0.42)}
          strokeWidth={0.95 * w}
          strokeLinecap="round"
        />
      ))}
      <circle cx="274" cy="196" r="1.9" fill={warm(0.42)} />

      {/* clock at midnight — bottom-right */}
      <g>
        <circle cx="240" cy="348" r="24" fill={ink(0.09)} stroke={ink(0.52)} strokeWidth={1.2 * w} />
        <circle cx="240" cy="348" r="20.5" fill="none" stroke={gold(0.4)} strokeWidth={0.8 * w} />
        {ticks.map((t) => (
          <line
            key={`nyeMid-tick-${t.i}`}
            x1={t.x1}
            y1={t.y1}
            x2={t.x2}
            y2={t.y2}
            stroke={t.major ? ink(0.55) : ink(0.36)}
            strokeWidth={t.major ? 1.4 * w : 0.9 * w}
            strokeLinecap="round"
          />
        ))}
        <path d="M 240 348 L 240 334.5" stroke={ink(0.58)} strokeWidth={1.6 * w} strokeLinecap="round" />
        <path d="M 240 348 L 238.4 331" stroke={gold(0.58)} strokeWidth={1.15 * w} strokeLinecap="round" />
        <circle cx="240" cy="348" r="1.7" fill={ink(0.6)} />
        <path d="M 236.6 323.4 Q 240 320.6 243.4 323.4" fill="none" stroke={ink(0.5)} strokeWidth={1.3 * w} strokeLinecap="round" />
      </g>

      {/* curling streamers — left, right, bottom */}
      <path
        d="M 22 104 C 36 124 10 140 24 162 C 38 184 12 200 26 224 C 38 244 16 258 24 278"
        fill="none"
        stroke={gold(0.38)}
        strokeWidth={2.6 * w}
        strokeLinecap="round"
      />
      <path
        d="M 278 116 C 264 136 290 152 276 174 C 262 196 288 212 274 236 C 266 250 280 262 274 276"
        fill="none"
        stroke={ink(0.32)}
        strokeWidth={2.2 * w}
        strokeLinecap="round"
      />
      <path
        d="M 92 390 C 104 372 118 388 130 370"
        fill="none"
        stroke={warm(0.34)}
        strokeWidth={2.2 * w}
        strokeLinecap="round"
      />

      {/* confetti */}
      {flecks.map(([x, y], i) => {
        const c = i % 3 === 0 ? gold(0.5) : i % 3 === 1 ? ink(0.36) : warm(0.42);
        if (i % 3 === 1) return <circle key={`nyeMid-fleck-${i}`} cx={x} cy={y} r="1.7" fill={c} />;
        if (i % 3 === 2)
          return (
            <line
              key={`nyeMid-fleck-${i}`}
              x1={x - 2.6}
              y1={y + 2}
              x2={x + 2.6}
              y2={y - 2}
              stroke={c}
              strokeWidth={1.1 * w}
              strokeLinecap="round"
            />
          );
        return (
          <rect
            key={`nyeMid-fleck-${i}`}
            x={x - 2}
            y={y - 1.1}
            width="4"
            height="2.2"
            rx="0.5"
            fill={c}
            transform={`rotate(${18 + i * 27} ${x} ${y})`}
          />
        );
      })}
    </svg>
  );
}

// ─── ThanksgivingHarvestMotif — wheat stalk (two crossed, top-left corner), wheat stalk (single, rising along bottom-right
function ThanksgivingHarvestMotif({ color, accent, accent2, scale = 300 }) {
  const k = Math.max(0.82, Math.min(1.28, 300 / scale));
  const sw = 1.05 * k;
  const hair = 0.72 * k;

  const KERNEL = "M0 0C3.3 -1.6 4.5 -5.4 0 -9.4C-4.5 -5.4 -3.3 -1.6 0 0Z";
  const KERNELS = [
    { id: "k0", x: -2.4, y: -20, r: -27 },
    { id: "k1", x: 2.4, y: -20, r: 27 },
    { id: "k2", x: -2.3, y: -27, r: -25 },
    { id: "k3", x: 2.3, y: -27, r: 25 },
    { id: "k4", x: -2.1, y: -34, r: -22 },
    { id: "k5", x: 2.1, y: -34, r: 22 },
    { id: "k6", x: -1.7, y: -41, r: -17 },
    { id: "k7", x: 1.7, y: -41, r: 17 },
    { id: "k8", x: 0, y: -48, r: 0 }
  ];
  const STALKS = [
    { id: "wheat-tl-a", t: "translate(24 100) rotate(-15)" },
    { id: "wheat-tl-b", t: "translate(39 105) rotate(8) scale(0.9)" },
    { id: "wheat-br", t: "translate(268 332) rotate(16) scale(0.94)" }
  ];

  const LEAF_BLADE = "M0 13C-6.4 7 -7.6 -2.6 0 -12C7.6 -2.6 6.4 7 0 13Z";
  const LEAF_VEINS =
    "M0 13L0 17.4M0 12C0.4 5 -0.4 -3 0 -11M0 7.2L-4.2 3M0 7.2L4.2 3M0 2.4L-4.8 -2.4M0 2.4L4.8 -2.4M0 -2.6L-3.8 -6.6M0 -2.6L3.8 -6.6";
  const LEAVES = [
    { id: "leaf-tr", t: "translate(222 30) rotate(-42) scale(0.95)" },
    { id: "leaf-bl", t: "translate(84 362) rotate(34) scale(0.9)" },
    { id: "leaf-edge-l", t: "translate(22 240) rotate(22) scale(0.8)" },
    { id: "leaf-edge-r", t: "translate(282 190) rotate(-24) scale(0.85)" }
  ];

  const ACORNS = [
    { id: "acorn-a", t: "translate(230 350) rotate(-10)" },
    { id: "acorn-b", t: "translate(245 359) rotate(15) scale(0.76)" }
  ];

  const GRAINS = [
    { id: "g0", x: 94, y: 72, r: 1.7 },
    { id: "g1", x: 201, y: 41, r: 1.4 },
    { id: "g2", x: 124, y: 377, r: 1.6 },
    { id: "g3", x: 178, y: 366, r: 1.25 },
    { id: "g4", x: 60, y: 112, r: 1.15 }
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id="tgvHarvestGourd" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={accent2} stopOpacity="0.34" />
          <stop offset="1" stopColor={accent2} stopOpacity="0.13" />
        </linearGradient>
        <linearGradient id="tgvHarvestPie" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={accent2} stopOpacity="0.3" />
          <stop offset="1" stopColor={accent} stopOpacity="0.15" />
        </linearGradient>
      </defs>

      {/* --- wheat stalks --- */}
      {STALKS.map((s) => (
        <g key={s.id} transform={s.t}>
          <path
            d="M2.4 30C-1.6 14 1.8 0 0 -46"
            fill="none"
            stroke={hexA(accent, 0.46)}
            strokeWidth={sw * 0.8}
            strokeLinecap="round"
          />
          <path
            d="M0 -56C-1.5 -62 -3 -66.4 -4.8 -70.4M0.4 -57C0.9 -63 1.2 -67 1.4 -71.4M0.8 -56C2.6 -61 4.2 -64.8 5.8 -68"
            fill="none"
            stroke={hexA(accent, 0.38)}
            strokeWidth={hair * 0.85}
            strokeLinecap="round"
          />
          {KERNELS.map((n) => (
            <path
              key={n.id}
              d={KERNEL}
              transform={`translate(${n.x} ${n.y}) rotate(${n.r})`}
              fill={hexA(accent, 0.28)}
              stroke={hexA(accent, 0.55)}
              strokeWidth={hair}
              strokeLinejoin="round"
            />
          ))}
        </g>
      ))}

      {/* --- oak leaf, top left --- */}
      <g transform="translate(70 32) rotate(26)">
        <path
          d="M0 12C-2 9 -3 7 -5.6 6.2C-9.4 5.2 -10 1 -6.8 -0.4C-9.6 -2.6 -8 -6.8 -4.6 -6C-6 -9.6 -2.6 -12 0 -16.5C2.6 -12 6 -9.6 4.6 -6C8 -6.8 9.6 -2.6 6.8 -0.4C10 1 9.4 5.2 5.6 6.2C3 7 2 9 0 12Z"
          fill={hexA(color, 0.16)}
          stroke={hexA(color, 0.5)}
          strokeWidth={sw * 0.85}
          strokeLinejoin="round"
        />
        <path
          d="M0 12L0 16.6M0 11C0.4 4 -0.4 -4 0 -14M0 4.4L-4.6 1.4M0 4.4L4.6 1.4M0 -2.4L-3.6 -5.4M0 -2.4L3.6 -5.4"
          fill="none"
          stroke={hexA(color, 0.38)}
          strokeWidth={hair}
          strokeLinecap="round"
        />
      </g>

      {/* --- autumn leaves, scattered --- */}
      {LEAVES.map((l) => (
        <g key={l.id} transform={l.t}>
          <path
            d={LEAF_BLADE}
            fill={hexA(accent2, 0.15)}
            stroke={hexA(color, 0.48)}
            strokeWidth={sw * 0.8}
            strokeLinejoin="round"
          />
          <path
            d={LEAF_VEINS}
            fill="none"
            stroke={hexA(color, 0.36)}
            strokeWidth={hair}
            strokeLinecap="round"
          />
        </g>
      ))}

      {/* --- heirloom pumpkin, top right --- */}
      <g transform="translate(256 60)">
        <path
          d="M0 -12.5C9.5 -12.5 17 -7 17 1C17 9.2 9.4 14.2 0 14.2C-9.4 14.2 -17 9.2 -17 1C-17 -7 -9.5 -12.5 0 -12.5Z"
          fill="url(#tgvHarvestGourd)"
          stroke={hexA(accent2, 0.58)}
          strokeWidth={sw}
          strokeLinejoin="round"
        />
        <path
          d="M-7.4 -11.6C-11.4 -7 -11.4 8 -7.4 13.2M7.4 -11.6C11.4 -7 11.4 8 7.4 13.2M-3.2 -12.3C-4.8 -6 -4.8 8 -3.2 13.9M3.2 -12.3C4.8 -6 4.8 8 3.2 13.9"
          fill="none"
          stroke={hexA(accent2, 0.36)}
          strokeWidth={hair}
          strokeLinecap="round"
        />
        <path
          d="M-1 -12.6C-1.4 -16.4 -2.6 -19.4 -4.6 -21.4C-1.6 -21 0.6 -19.6 1.6 -17.4C2.4 -15.6 2 -13.6 1 -12.7Z"
          fill={hexA(color, 0.32)}
          stroke={hexA(color, 0.5)}
          strokeWidth={hair}
          strokeLinejoin="round"
        />
        <path
          d="M1.8 -18.2C5.4 -21.6 9.8 -20.2 9.4 -16.6C9.1 -13.9 5.9 -13.6 6 -16C6.1 -17.7 7.8 -18.1 8.6 -17"
          fill="none"
          stroke={hexA(color, 0.42)}
          strokeWidth={hair}
          strokeLinecap="round"
        />
      </g>

      {/* --- slice of pie, bottom left --- */}
      <g transform="translate(40 346) rotate(-12) scale(1.05)">
        <path
          d="M0 -14L-12.6 7.6Q-10.6 13.4 -6.3 10.6Q-3.3 14.6 0 11.6Q3.3 14.6 6.3 10.6Q10.6 13.4 12.6 7.6Z"
          fill="url(#tgvHarvestPie)"
          stroke={hexA(color, 0.5)}
          strokeWidth={sw * 0.85}
          strokeLinejoin="round"
        />
        <path
          d="M-10.6 3.2C-6.2 6.8 6.2 6.8 10.6 3.2"
          fill="none"
          stroke={hexA(color, 0.44)}
          strokeWidth={sw * 0.7}
          strokeLinecap="round"
        />
        <path
          d="M-6.6 6.2L-6.4 10.4M0 7L0 11.2M6.6 6.2L6.4 10.4"
          fill="none"
          stroke={hexA(color, 0.3)}
          strokeWidth={hair}
          strokeLinecap="round"
        />
        <path
          d="M-2.2 -4.4L-4 -0.4M2.4 -5.2L0.6 -1.2"
          fill="none"
          stroke={hexA(accent, 0.42)}
          strokeWidth={hair}
          strokeLinecap="round"
        />
      </g>

      {/* --- acorns, bottom right --- */}
      {ACORNS.map((a) => (
        <g key={a.id} transform={a.t}>
          <path
            d="M-5.6 -2.2C-5.6 5 -3.2 9.6 0 11.4C3.2 9.6 5.6 5 5.6 -2.2Z"
            fill={hexA(accent, 0.18)}
            stroke={hexA(color, 0.5)}
            strokeWidth={sw * 0.8}
            strokeLinejoin="round"
          />
          <path
            d="M-6.8 -2.6C-6.8 -6.6 -3.6 -9 0 -9C3.6 -9 6.8 -6.6 6.8 -2.6Z"
            fill={hexA(accent2, 0.26)}
            stroke={hexA(color, 0.5)}
            strokeWidth={sw * 0.8}
            strokeLinejoin="round"
          />
          <path
            d="M-5.8 -5C-2.6 -6.6 2.6 -6.6 5.8 -5M-6.6 -3.4C-2.8 -5.2 2.8 -5.2 6.6 -3.4"
            fill="none"
            stroke={hexA(color, 0.34)}
            strokeWidth={hair * 0.9}
            strokeLinecap="round"
          />
          <path
            d="M0 -9C0.7 -10.6 -0.6 -11.9 0.3 -13.6"
            fill="none"
            stroke={hexA(color, 0.45)}
            strokeWidth={hair}
            strokeLinecap="round"
          />
        </g>
      ))}

      {/* --- loose grain --- */}
      {GRAINS.map((g) => (
        <circle
          key={g.id}
          cx={g.x}
          cy={g.y}
          r={g.r}
          fill={hexA(accent, 0.4)}
        />
      ))}
    </svg>
  );
}

// ─── PatrioticMotif — Bunting garland of 11 triangular pennants swagging across the top edge, Hero firework burs
function PatrioticMotif({ color, accent, accent2, scale = 300 }) {
  const c2 = accent2 || accent;
  const k = Math.max(0.78, Math.min(1.3, 300 / scale));
  const ink = (a) => hexA(color, a);
  const hot = (a) => hexA(accent, a);
  const cool = (a) => hexA(c2, a);
  const n2 = (v) => Number(v.toFixed(2));

  const starPath = (cx, cy, r) => {
    let d = "";
    for (let i = 0; i < 10; i++) {
      const t = (Math.PI / 5) * i - Math.PI / 2;
      const rr = i % 2 === 0 ? r : r * 0.42;
      d += (i === 0 ? "M" : "L") + n2(cx + Math.cos(t) * rr) + " " + n2(cy + Math.sin(t) * rr) + " ";
    }
    return d + "Z";
  };

  const burst = (id, cx, cy, n, rLong, rShort, rot, strokeCol, dotCol, sw) => {
    const out = [];
    for (let i = 0; i < n; i++) {
      const a = ((Math.PI * 2) / n) * i + rot;
      const isLong = i % 2 === 0;
      const r = isLong ? rLong : rShort;
      const ex = cx + Math.cos(a) * r;
      const ey = cy + Math.sin(a) * r;
      out.push(
        <line
          key={id + "-r" + i}
          x1={n2(cx + Math.cos(a) * r * 0.3)}
          y1={n2(cy + Math.sin(a) * r * 0.3)}
          x2={n2(ex)}
          y2={n2(ey)}
          stroke={strokeCol}
          strokeWidth={n2(sw * k)}
          strokeLinecap="round"
        />
      );
      if (isLong) {
        out.push(<circle key={id + "-d" + i} cx={n2(ex)} cy={n2(ey)} r={n2(sw * 1.05 * k)} fill={dotCol} />);
      }
    }
    out.push(<circle key={id + "-c"} cx={cx} cy={cy} r={n2(sw * 1.2 * k)} fill={dotCol} />);
    return out;
  };

  const buntX = (t) => -8 + 316 * t;
  const buntY = (t) => 12 + 100 * t - 100 * t * t;
  const buntA = (t) => (Math.atan2(100 - 200 * t, 316) * 180) / Math.PI;

  const stars = [
    [92, 84, 0],
    [176, 90, 1],
    [20, 150, 2],
    [30, 216, 0],
    [262, 172, 1],
    [284, 258, 2],
    [126, 346, 0]
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <radialGradient id="patriot-burst-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={hot(0.1)} />
          <stop offset="100%" stopColor={hot(0)} />
        </radialGradient>
      </defs>

      {/* soft halo behind the hero burst */}
      <circle cx="252" cy="88" r="38" fill="url(#patriot-burst-glow)" />

      {/* bunting garland across the top */}
      <path d="M -8 12 Q 150 62 308 12" fill="none" stroke={ink(0.42)} strokeWidth={n2(1 * k)} strokeLinecap="round" />
      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => {
        const t = (i + 0.5) / 11;
        const depth = 19 + (i % 3) * 2;
        const tone = i % 3 === 0 ? hot(0.42) : i % 3 === 1 ? cool(0.4) : ink(0.26);
        return (
          <path
            key={"patriot-pennant-" + i}
            d={"M -10.5 0 L 10.5 0 L 0 " + depth + " Z"}
            fill={tone}
            stroke={ink(0.34)}
            strokeWidth={n2(0.7 * k)}
            strokeLinejoin="round"
            transform={"translate(" + n2(buntX(t)) + "," + n2(buntY(t)) + ") rotate(" + n2(buntA(t)) + ")"}
          />
        );
      })}

      {/* hero firework burst, top right */}
      {burst("patriot-fw-a", 252, 88, 10, 30, 17, -0.18, hot(0.5), hot(0.58), 1.1)}
      {/* second burst, top left */}
      {burst("patriot-fw-b", 38, 84, 8, 22, 12, 0.4, cool(0.46), cool(0.54), 1)}
      {/* small burst, right margin */}
      {burst("patriot-fw-c", 276, 206, 6, 15, 8.5, 0.25, ink(0.4), ink(0.46), 0.9)}

      {/* scattered stars */}
      {stars.map((s, i) => (
        <path
          key={"patriot-star-" + i}
          d={starPath(s[0], s[1], 4.6 + s[2] * 1.4)}
          fill={i % 3 === 0 ? hot(0.46) : i % 3 === 1 ? cool(0.44) : ink(0.34)}
          transform={"rotate(" + ((i * 13) % 25 - 12) + " " + s[0] + " " + s[1] + ")"}
        />
      ))}

      {/* sparkler, bottom left */}
      <line x1="16" y1="388" x2="48" y2="336" stroke={ink(0.48)} strokeWidth={n2(1.8 * k)} strokeLinecap="round" />
      <line x1="17" y1="386" x2="27" y2="370" stroke={hot(0.5)} strokeWidth={n2(3.2 * k)} strokeLinecap="round" />
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const a = ((-160 + i * 44) * Math.PI) / 180;
        const len = 6 + (i % 3) * 4;
        return (
          <line
            key={"patriot-spark-" + i}
            x1={n2(48 + Math.cos(a) * 3)}
            y1={n2(334 + Math.sin(a) * 3)}
            x2={n2(48 + Math.cos(a) * len)}
            y2={n2(334 + Math.sin(a) * len)}
            stroke={i % 2 === 0 ? hot(0.55) : cool(0.5)}
            strokeWidth={n2(0.9 * k)}
            strokeLinecap="round"
          />
        );
      })}
      <circle cx="48" cy="334" r={n2(1.6 * k)} fill={hot(0.6)} />
      <circle cx="44" cy="316" r={n2(1.1 * k)} fill={hot(0.42)} />
      <circle cx="38" cy="312" r={n2(0.9 * k)} fill={cool(0.4)} />
      <circle cx="56" cy="348" r={n2(1 * k)} fill={hot(0.38)} />

      {/* pennant flag on a pole, bottom right */}
      <line x1="284" y1="396" x2="284" y2="318" stroke={ink(0.46)} strokeWidth={n2(1.4 * k)} strokeLinecap="round" />
      <path
        d="M 282 322 L 252 336 L 282 350 Z"
        fill={cool(0.34)}
        stroke={ink(0.36)}
        strokeWidth={n2(0.8 * k)}
        strokeLinejoin="round"
      />
      <line x1="276" y1="328.5" x2="261" y2="334.8" stroke={hot(0.52)} strokeWidth={n2(1.3 * k)} strokeLinecap="round" />
      <line x1="276" y1="343.5" x2="261" y2="339.2" stroke={hot(0.52)} strokeWidth={n2(1.3 * k)} strokeLinecap="round" />

      {/* rippling striped ribbon along the bottom hem */}
      {[0, 1, 2, 3, 4].map((i) => {
        const y = 362 + i * 7;
        const lift = i % 2 === 0 ? 4 : 3.2;
        const alpha = [0.46, 0.42, 0.34, 0.38, 0.3][i];
        return (
          <path
            key={"patriot-hem-" + i}
            d={"M 78 " + y + " Q 114 " + n2(y - lift) + " 150 " + y + " T 222 " + y}
            fill="none"
            stroke={i % 3 === 0 ? hot(alpha) : i % 3 === 1 ? cool(alpha) : ink(alpha)}
            strokeWidth={n2(3.4 * k)}
            strokeLinecap="round"
          />
        );
      })}
    </svg>
  );
}

// ─── EasterMotif — decorated eggs (five, three band/chevron/dot patterns), sitting bunny silhouette with ear 
function EasterMotif({ color, accent, accent2, scale = 300 }) {
  const sw = 1.15 * Math.min(1.7, 300 / Math.max(150, scale));
  const EGG =
    "M0,-14C7.2,-14 11.4,-6.4 11.4,0.4C11.4,8.6 6.4,14 0,14C-6.4,14 -11.4,8.6 -11.4,0.4C-11.4,-6.4 -7.2,-14 0,-14Z";

  const eggs = [
    { id: "eA", x: 30, y: 62, s: 1.05, r: -13, d: 1 },
    { id: "eB", x: 58, y: 42, s: 0.82, r: 14, d: 2 },
    { id: "eC", x: 22, y: 92, s: 0.72, r: 6, d: 0 },
    { id: "eD", x: 271, y: 200, s: 0.92, r: -8, d: 1 },
    { id: "eE", x: 272, y: 348, s: 0.7, r: 16, d: 2 },
  ];

  const catkins = [
    { id: "c0", x: 106, y: 25, r: -34 },
    { id: "c1", x: 121, y: 18, r: -26 },
    { id: "c2", x: 136, y: 14, r: -16 },
    { id: "c3", x: 152, y: 11.5, r: -6 },
    { id: "c4", x: 170, y: 11.5, r: 8 },
    { id: "c5", x: 188, y: 13.5, r: 20 },
  ];

  const specks = [
    { id: "s0", x: 88, y: 37, r: 1.7 },
    { id: "s1", x: 214, y: 30, r: 1.4 },
    { id: "s2", x: 46, y: 116, r: 1.5 },
    { id: "s3", x: 259, y: 126, r: 1.3 },
    { id: "s4", x: 70, y: 333, r: 1.5 },
    { id: "s5", x: 226, y: 344, r: 1.3 },
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id="easter-egg-sheen" x1="0.15" y1="0" x2="0.85" y2="1">
          <stop offset="0" stopColor={hexA(accent, 0.3)} />
          <stop offset="1" stopColor={hexA(accent2, 0.11)} />
        </linearGradient>
      </defs>

      {/* pussy-willow branch, top edge */}
      <path
        d="M96,32 C124,10 172,4 204,18"
        fill="none"
        stroke={hexA(color, 0.4)}
        strokeWidth={sw * 0.95}
        strokeLinecap="round"
      />
      {catkins.map((c) => (
        <ellipse
          key={c.id}
          cx={c.x}
          cy={c.y}
          rx="2.5"
          ry="4.4"
          transform={`rotate(${c.r} ${c.x} ${c.y})`}
          fill={hexA(accent2, 0.34)}
          stroke={hexA(color, 0.34)}
          strokeWidth={sw * 0.75}
        />
      ))}

      {/* decorated eggs */}
      {eggs.map((e) => (
        <g key={e.id} transform={`translate(${e.x} ${e.y}) rotate(${e.r}) scale(${e.s})`}>
          <path
            d={EGG}
            fill="url(#easter-egg-sheen)"
            stroke={hexA(color, 0.5)}
            strokeWidth={sw / e.s}
          />
          {e.d === 0 && (
            <React.Fragment>
              <circle cx="-5.4" cy="-5" r="1.5" fill={hexA(color, 0.32)} />
              <circle cx="0.4" cy="-8.4" r="1.5" fill={hexA(color, 0.32)} />
              <circle cx="5.6" cy="-4" r="1.5" fill={hexA(color, 0.32)} />
              <circle cx="-3.8" cy="3.2" r="1.5" fill={hexA(color, 0.32)} />
              <circle cx="3.2" cy="2" r="1.5" fill={hexA(color, 0.32)} />
              <circle cx="-0.6" cy="8.6" r="1.5" fill={hexA(color, 0.32)} />
            </React.Fragment>
          )}
          {e.d === 1 && (
            <React.Fragment>
              <path
                d="M-6.6,-8.4 Q0,-4.4 6.6,-8.4"
                fill="none"
                stroke={hexA(color, 0.36)}
                strokeWidth={sw / e.s}
                strokeLinecap="round"
              />
              <path
                d="M-10.6,-2.6 Q0,2.2 10.6,-2.6"
                fill="none"
                stroke={hexA(color, 0.36)}
                strokeWidth={sw / e.s}
                strokeLinecap="round"
              />
              <path
                d="M-9.4,4.6 Q0,9.4 9.4,4.6"
                fill="none"
                stroke={hexA(color, 0.36)}
                strokeWidth={sw / e.s}
                strokeLinecap="round"
              />
            </React.Fragment>
          )}
          {e.d === 2 && (
            <React.Fragment>
              <path
                d="M-8.2,1.6 L-4.1,-3 L0,1.6 L4.1,-3 L8.2,1.6"
                fill="none"
                stroke={hexA(color, 0.36)}
                strokeWidth={sw / e.s}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M-9.8,6.6 Q0,10.8 9.8,6.6"
                fill="none"
                stroke={hexA(color, 0.36)}
                strokeWidth={sw / e.s}
                strokeLinecap="round"
              />
            </React.Fragment>
          )}
        </g>
      ))}

      {/* sitting bunny, top-right */}
      <g transform="translate(256 88) scale(0.95)">
        <path
          d="M-7,0 C-11.5,-0.6 -13.2,-6 -12.4,-12.5 C-11.8,-17 -14.2,-19.6 -14.2,-23.4
             C-14.2,-27.4 -12.2,-30 -10,-31.2 C-10.4,-37 -9.4,-43.6 -6.8,-44.8
             C-4.4,-45.9 -3.4,-41.2 -4,-36 C-4.4,-32.8 -4.8,-31.4 -5,-30.2
             C-3.2,-32.4 -1.6,-38 0.6,-41 C2.8,-44 5.4,-42.2 4.2,-37
             C3,-31.8 1,-28.4 -0.6,-27 C3,-25.4 5.4,-22 7.4,-18
             C11,-13 15.2,-8 14,-3.2 C13.4,-0.8 12,0 10,0 Z"
          fill={hexA(accent, 0.2)}
          stroke={hexA(color, 0.52)}
          strokeWidth={sw}
          strokeLinejoin="round"
        />
        <path
          d="M-8.6,-34.4 C-8.2,-38.6 -7.6,-41.6 -6.7,-42.6"
          fill="none"
          stroke={hexA(color, 0.32)}
          strokeWidth={sw * 0.85}
          strokeLinecap="round"
        />
        <path
          d="M-1.4,-31.4 C0.4,-34.8 1.8,-38.2 2.7,-39.6"
          fill="none"
          stroke={hexA(color, 0.32)}
          strokeWidth={sw * 0.85}
          strokeLinecap="round"
        />
        <circle
          cx="13.4"
          cy="-6.2"
          r="3.4"
          fill={hexA(accent2, 0.26)}
          stroke={hexA(color, 0.4)}
          strokeWidth={sw * 0.8}
        />
        <circle cx="-10.4" cy="-24.8" r="1.2" fill={hexA(color, 0.55)} />
        <circle cx="-13.8" cy="-23.2" r="1" fill={hexA(accent2, 0.5)} />
      </g>

      {/* tulips, left margin */}
      <g transform="translate(28 262)">
        <path
          d="M0,0 C-1.2,-18 1,-38 0.6,-52"
          fill="none"
          stroke={hexA(color, 0.45)}
          strokeWidth={sw * 1.05}
          strokeLinecap="round"
        />
        <path
          d="M-0.2,-14 C-8.6,-18.4 -13.4,-30 -12.6,-42 C-6,-35.6 -1.6,-24 -0.2,-14 Z"
          fill={hexA(accent, 0.22)}
          stroke={hexA(color, 0.34)}
          strokeWidth={sw * 0.8}
        />
        <path
          d="M0.7,-21 C7.8,-25 11.6,-34.6 10.8,-44.4 C5.2,-38.6 1.7,-29 0.7,-21 Z"
          fill={hexA(accent, 0.18)}
          stroke={hexA(color, 0.3)}
          strokeWidth={sw * 0.8}
        />
        <path
          d="M-7,-67 C-7,-58 -4,-53 0.4,-52 C4.8,-53 7.4,-58 7.4,-67
             C5.4,-63 4.2,-62 3.2,-65 C2.2,-68.6 1.2,-69.6 0.2,-69.6
             C-0.8,-69.6 -1.8,-68.6 -2.8,-65 C-3.8,-62 -5,-63 -7,-67 Z"
          fill="url(#easter-egg-sheen)"
          stroke={hexA(color, 0.5)}
          strokeWidth={sw}
          strokeLinejoin="round"
        />
      </g>
      <g transform="translate(48 256) scale(0.6)">
        <path
          d="M0,0 C-1,-18 1,-36 0.6,-50"
          fill="none"
          stroke={hexA(color, 0.4)}
          strokeWidth={sw * 1.6}
          strokeLinecap="round"
        />
        <path
          d="M0.6,-16 C8.4,-20 12.6,-30 11.6,-40.6 C5.6,-34.4 1.8,-24.6 0.6,-16 Z"
          fill={hexA(accent, 0.2)}
          stroke={hexA(color, 0.3)}
          strokeWidth={sw * 1.2}
        />
        <path
          d="M-6.6,-64 C-6.6,-55.6 -4,-51 0.4,-50 C4.8,-51 7,-55.6 7,-64
             C5.2,-60.2 4,-59.4 3,-62.2 C2,-65.6 1.2,-66.6 0.2,-66.6
             C-0.8,-66.6 -1.6,-65.6 -2.6,-62.2 C-3.6,-59.4 -4.8,-60.2 -6.6,-64 Z"
          fill="url(#easter-egg-sheen)"
          stroke={hexA(color, 0.46)}
          strokeWidth={sw * 1.5}
          strokeLinejoin="round"
        />
      </g>

      {/* leaf sprigs */}
      <g transform="translate(52 82) rotate(-8)">
        <path
          d="M0,0 C0.6,-6 1.2,-12 0.8,-18"
          fill="none"
          stroke={hexA(color, 0.4)}
          strokeWidth={sw * 0.85}
          strokeLinecap="round"
        />
        <path d="M0.9,-6 C-3.4,-7.2 -6,-11 -6,-15.4 C-2.4,-14 0.2,-10.2 0.9,-6 Z" fill={hexA(accent, 0.24)} />
        <path d="M1,-11 C5,-12.4 7.4,-16 7.2,-20.2 C3.8,-18.6 1.4,-15 1,-11 Z" fill={hexA(accent, 0.2)} />
        <path d="M0.85,-15 C-2.2,-16.6 -4,-20 -3.8,-23.4 C-1.2,-21.8 0.4,-18.6 0.85,-15 Z" fill={hexA(accent, 0.24)} />
      </g>
      <g transform="translate(268 244) rotate(10)">
        <path
          d="M0,0 C0.6,-6 1.2,-12 0.8,-18"
          fill="none"
          stroke={hexA(color, 0.4)}
          strokeWidth={sw * 0.85}
          strokeLinecap="round"
        />
        <path d="M0.9,-6 C-3.4,-7.2 -6,-11 -6,-15.4 C-2.4,-14 0.2,-10.2 0.9,-6 Z" fill={hexA(accent, 0.24)} />
        <path d="M1,-11 C5,-12.4 7.4,-16 7.2,-20.2 C3.8,-18.6 1.4,-15 1,-11 Z" fill={hexA(accent, 0.2)} />
        <path d="M0.85,-15 C-2.2,-16.6 -4,-20 -3.8,-23.4 C-1.2,-21.8 0.4,-18.6 0.85,-15 Z" fill={hexA(accent, 0.24)} />
      </g>

      {/* woven basket of eggs, bottom-left */}
      <g transform="translate(46 352)">
        <path
          d="M-13.5,-4.6 C-12.5,-23.4 12.5,-23.4 13.5,-4.6"
          fill="none"
          stroke={hexA(color, 0.46)}
          strokeWidth={sw * 1.15}
          strokeLinecap="round"
        />
        <path
          d="M-16.5,-4.6 C-16.5,-11.8 -14.9,-15.8 -11,-15.8 C-7.1,-15.8 -5.5,-11.8 -5.5,-4.6 Z"
          fill={hexA(accent, 0.26)}
          stroke={hexA(color, 0.44)}
          strokeWidth={sw * 0.9}
        />
        <path
          d="M-5.6,-4.6 C-5.6,-12.6 -3.8,-17.2 0,-17.2 C3.8,-17.2 5.6,-12.6 5.6,-4.6 Z"
          fill={hexA(accent2, 0.24)}
          stroke={hexA(color, 0.44)}
          strokeWidth={sw * 0.9}
        />
        <path
          d="M5.5,-4.6 C5.5,-11.8 7.1,-15.8 11,-15.8 C14.9,-15.8 16.5,-11.8 16.5,-4.6 Z"
          fill={hexA(accent, 0.26)}
          stroke={hexA(color, 0.44)}
          strokeWidth={sw * 0.9}
        />
        <path
          d="M-3.4,-11 Q0,-8.6 3.4,-11"
          fill="none"
          stroke={hexA(color, 0.3)}
          strokeWidth={sw * 0.8}
          strokeLinecap="round"
        />
        <rect
          x="-19"
          y="-4.8"
          width="38"
          height="5.4"
          rx="2.7"
          fill={hexA(accent, 0.22)}
          stroke={hexA(color, 0.5)}
          strokeWidth={sw}
        />
        <path
          d="M-17.4,0.8 L17.4,0.8 C16.4,9.2 13.2,16 8.6,18.8 L-8.6,18.8 C-13.2,16 -16.4,9.2 -17.4,0.8 Z"
          fill={hexA(accent, 0.15)}
          stroke={hexA(color, 0.5)}
          strokeWidth={sw}
          strokeLinejoin="round"
        />
        <path d="M-15.6,6.4 Q0,9.4 15.6,6.4" fill="none" stroke={hexA(color, 0.3)} strokeWidth={sw * 0.8} />
        <path d="M-13.4,12.6 Q0,15.4 13.4,12.6" fill="none" stroke={hexA(color, 0.3)} strokeWidth={sw * 0.8} />
        <path
          d="M-8.6,1.4 C-9,7.4 -8.6,13.4 -7.6,18.4 M0,1.4 C0,7.4 0,13.4 0,18.6 M8.6,1.4 C9,7.4 8.6,13.4 7.6,18.4"
          fill="none"
          stroke={hexA(color, 0.28)}
          strokeWidth={sw * 0.8}
        />
      </g>

      {/* chick, bottom-right */}
      <g transform="translate(238 366)">
        <path
          d="M1,0 C-5,0 -9,-4 -9,-9 C-9,-14 -5.5,-17.6 -1,-18
             C-0.4,-21.6 2,-24.2 5.6,-24.2 C9,-24.2 11.6,-21.6 11.6,-18.4
             C11.6,-17.7 11.5,-17.1 11.3,-16.5 L16.2,-15 L11.1,-13.2
             C10.5,-11.4 9.5,-10 8.5,-9.2 C9.5,-5.8 8,-2 4,-0.5
             C3,-0.1 2,0 1,0 Z"
          fill={hexA(accent2, 0.24)}
          stroke={hexA(color, 0.5)}
          strokeWidth={sw}
          strokeLinejoin="round"
        />
        <path
          d="M-0.5,-11.4 C3,-12.4 5.6,-10 4.6,-6.4 C1.6,-6.6 0,-9 -0.5,-11.4"
          fill="none"
          stroke={hexA(color, 0.32)}
          strokeWidth={sw * 0.85}
          strokeLinecap="round"
        />
        <circle cx="7" cy="-19.6" r="1.15" fill={hexA(color, 0.55)} />
        <path
          d="M0.6,0 L0,4.4 M4.6,0 L5.4,4.4"
          fill="none"
          stroke={hexA(color, 0.42)}
          strokeWidth={sw * 0.8}
          strokeLinecap="round"
        />
        <path
          d="M-1.8,4.8 L1.4,4.3 M4.2,4.3 L7.2,4.9"
          fill="none"
          stroke={hexA(color, 0.42)}
          strokeWidth={sw * 0.8}
          strokeLinecap="round"
        />
      </g>

      {/* scattered specks */}
      {specks.map((p, i) => (
        <circle
          key={p.id}
          cx={p.x}
          cy={p.y}
          r={p.r}
          fill={hexA(i % 2 === 0 ? accent2 : accent, 0.4)}
        />
      ))}
    </svg>
  );
}

// ─── HanukkahMotif — Nine-branch chanukiah (menorah) with lit candles and raised shamash, top-left, Dreidel wit
function HanukkahMotif({ color, accent, accent2, scale = 300 }) {
  const ink = (a) => hexA(color, a);
  const gold = (a) => hexA(accent || color, a);
  const alt = (a) => hexA(accent2 || accent || color, a);
  const k = Math.max(0.85, Math.min(1.2, 300 / (scale || 300)));
  const w = (n) => +(n * k).toFixed(2);

  const flame = (x, y, h) =>
    "M " + x + " " + y +
    " c " + (-h * 0.4).toFixed(2) + " " + (-h * 0.34).toFixed(2) +
    " " + (-h * 0.32).toFixed(2) + " " + (-h * 0.66).toFixed(2) + " 0 " + -h +
    " c " + (h * 0.32).toFixed(2) + " " + (h * 0.34).toFixed(2) +
    " " + (h * 0.4).toFixed(2) + " " + (h * 0.66).toFixed(2) + " 0 " + h + " Z";

  const tri = (x, y, r, up) =>
    up
      ? "M " + x + " " + (y - r).toFixed(2) + " L " + (x + r * 0.866).toFixed(2) + " " + (y + r * 0.5).toFixed(2) + " L " + (x - r * 0.866).toFixed(2) + " " + (y + r * 0.5).toFixed(2) + " Z"
      : "M " + x + " " + (y + r).toFixed(2) + " L " + (x + r * 0.866).toFixed(2) + " " + (y - r * 0.5).toFixed(2) + " L " + (x - r * 0.866).toFixed(2) + " " + (y - r * 0.5).toFixed(2) + " Z";

  const cx = 92;
  const cupY = 54;
  const radii = [10, 19, 28, 37];
  const lights = [{ x: cx, y: 45, id: "shamash" }].concat(
    radii
      .map((r, i) => [{ x: cx - r, y: cupY, id: "l" + i }, { x: cx + r, y: cupY, id: "r" + i }])
      .reduce((a, b) => a.concat(b), [])
  );
  const stars = [
    { x: 176, y: 34, r: 11, id: "s0" },
    { x: 214, y: 84, r: 6.5, id: "s1" },
    { x: 32, y: 220, r: 9, id: "s2" },
    { x: 200, y: 370, r: 8, id: "s3" }
  ];
  const sparks = [
    { x: 52, y: 186, r: 1.6 }, { x: 40, y: 262, r: 1.2 },
    { x: 150, y: 96, r: 1.4 }, { x: 232, y: 112, r: 1.2 },
    { x: 140, y: 384, r: 1.5 }, { x: 278, y: 318, r: 1.3 },
    { x: 24, y: 302, r: 1.2 }, { x: 186, y: 18, r: 1.2 }
  ];
  const dx = 256;
  const dy = 48;

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <radialGradient id="hanukkah-motif-glow">
          <stop offset="0" stopColor={gold(0.24)} />
          <stop offset="1" stopColor={gold(0)} />
        </radialGradient>
      </defs>

      <g fill="none" strokeLinecap="round" strokeLinejoin="round">
        {/* ---- chanukiah: eight branches + raised shamash ---- */}
        <g stroke={ink(0.5)} strokeWidth={w(1.35)}>
          <rect x={cx - 17} y="106" width="34" height="5" rx="2.2" fill={ink(0.14)} />
          <path d={"M " + (cx - 11) + " 106 L " + (cx - 4) + " 94 L " + (cx + 4) + " 94 L " + (cx + 11) + " 106 Z"} fill={ink(0.1)} />
          <path d={"M " + cx + " 94 L " + cx + " 45"} />
          <path d={"M " + (cx - 7) + " 78 L " + (cx + 7) + " 78"} strokeWidth={w(1)} />
          {radii.map((r) => (
            <path
              key={"hanukkah-arm-" + r}
              d={
                "M " + cx + " " + (cupY + r) + " A " + r + " " + r + " 0 0 1 " + (cx - r) + " " + cupY +
                " M " + cx + " " + (cupY + r) + " A " + r + " " + r + " 0 0 0 " + (cx + r) + " " + cupY
              }
            />
          ))}
          {lights.map((p) => (
            <path
              key={"hanukkah-cup-" + p.id}
              d={"M " + (p.x - 3.4) + " " + p.y + " L " + (p.x + 3.4) + " " + p.y + " L " + (p.x + 2.2) + " " + (p.y - 3.4) + " L " + (p.x - 2.2) + " " + (p.y - 3.4) + " Z"}
              fill={ink(0.12)}
              strokeWidth={w(1)}
            />
          ))}
        </g>
        {lights.map((p, i) => {
          const ch = i === 0 ? 14 : 12;
          return (
            <rect
              key={"hanukkah-candle-" + p.id}
              x={p.x - 1.9}
              y={p.y - 3.4 - ch}
              width="3.8"
              height={ch}
              rx="1.4"
              fill={alt(0.28)}
              stroke={ink(0.42)}
              strokeWidth={w(1)}
            />
          );
        })}
        <ellipse cx={cx} cy="33.5" rx="46" ry="10" fill="url(#hanukkah-motif-glow)" />
        <circle cx={cx} cy="23" r="11" fill="url(#hanukkah-motif-glow)" />
        {lights.map((p, i) => {
          const ch = i === 0 ? 14 : 12;
          const fh = i === 0 ? 10.5 : 7.6 + (i % 3) * 0.55;
          return <path key={"hanukkah-flame-" + p.id} d={flame(p.x, p.y - 3.4 - ch, fh)} fill={gold(0.55)} />;
        })}

        {/* ---- dreidel, top right ---- */}
        <g stroke={ink(0.5)} strokeWidth={w(1.3)}>
          <path d={"M " + dx + " " + (dy - 26) + " L " + dx + " " + (dy - 18)} />
          <path d={"M " + (dx - 11) + " " + (dy - 18) + " L " + (dx + 11) + " " + (dy - 18)} />
          <path
            d={"M " + (dx - 10) + " " + (dy - 18) + " L " + (dx + 10) + " " + (dy - 18) + " L " + (dx + 10) + " " + (dy + 3) + " L " + dx + " " + (dy + 17) + " L " + (dx - 10) + " " + (dy + 3) + " Z"}
            fill={alt(0.16)}
          />
          <path
            d={"M " + (dx + 2.5) + " " + (dy - 12) + " L " + (dx - 1.5) + " " + (dy - 12) + " M " + (dx + 2.5) + " " + (dy - 12) + " L " + (dx + 2.5) + " " + (dy - 1) + " L " + (dx - 4) + " " + (dy - 1)}
            stroke={gold(0.6)}
            strokeWidth={w(1.5)}
          />
        </g>

        {/* ---- six-pointed stars ---- */}
        {stars.map((s) => (
          <g key={"hanukkah-star-" + s.id} strokeWidth={w(1.2)}>
            <path d={tri(s.x, s.y, s.r, true)} stroke={ink(0.45)} />
            <path d={tri(s.x, s.y, s.r, false)} stroke={gold(0.45)} />
          </g>
        ))}

        {/* ---- single taper in a holder, right margin ---- */}
        <g stroke={ink(0.5)} strokeWidth={w(1.3)}>
          <rect x="265" y="200" width="7" height="46" rx="3" fill={alt(0.26)} />
          <path d="M 265 214 L 272 214" stroke={ink(0.3)} strokeWidth={w(0.9)} />
          <path d="M 259 246 L 278 246 L 274 254 L 263 254 Z" fill={ink(0.12)} />
        </g>
        <circle cx="268.5" cy="194" r="10" fill="url(#hanukkah-motif-glow)" />
        <path d={flame(268.5, 200, 11)} fill={gold(0.55)} />

        {/* ---- cruse of oil, bottom left ---- */}
        <g stroke={ink(0.5)} strokeWidth={w(1.3)}>
          <path d="M 40 332 L 52 332 L 52 338 C 62 342 63 358 56 366 L 36 366 C 29 358 30 342 40 338 Z" fill={ink(0.1)} />
          <path d="M 37 332 L 55 332" />
          <path d="M 34 350 C 28 348 28 342 33 341" strokeWidth={w(1)} />
          <path d="M 58 350 C 64 348 64 342 59 341" strokeWidth={w(1)} />
          <path d="M 35 357 L 57 357" stroke={gold(0.4)} strokeWidth={w(1)} />
        </g>

        {/* ---- gelt coins ---- */}
        <g strokeWidth={w(1.25)}>
          <circle cx="88" cy="374" r="9.5" fill={gold(0.16)} stroke={gold(0.5)} />
          <circle cx="88" cy="374" r="5.6" stroke={gold(0.34)} strokeWidth={w(0.9)} />
          <circle cx="105" cy="382" r="9.5" fill={gold(0.14)} stroke={gold(0.45)} />
          <circle cx="105" cy="382" r="5.6" stroke={gold(0.3)} strokeWidth={w(0.9)} />
        </g>

        {/* ---- sufganiyah, bottom right ---- */}
        <g strokeWidth={w(1.3)}>
          <circle cx="248" cy="350" r="14" fill={ink(0.1)} stroke={ink(0.48)} />
          <path
            d="M 234.5 346.4 A 14 14 0 0 1 261.5 346.4 C 258.5 351.5 256 345.5 252 349.5 C 248.5 353 245.5 345 241.5 349 C 238.5 352 236.5 344.5 234.5 346.4 Z"
            fill={alt(0.3)}
            stroke={alt(0.52)}
            strokeWidth={w(1)}
          />
          {[[241, 341.5], [248, 339.5], [255, 342.5]].map((p) => (
            <circle key={"hanukkah-sugar-" + p[0]} cx={p[0]} cy={p[1]} r="1.05" fill={ink(0.32)} stroke="none" />
          ))}
        </g>

        {/* ---- embers ---- */}
        {sparks.map((s) => (
          <circle key={"hanukkah-spark-" + s.x + "-" + s.y} cx={s.x} cy={s.y} r={s.r} fill={gold(0.42)} stroke="none" />
        ))}
      </g>
    </svg>
  );
}

// ─── MitzvahMotif — Torah scroll with twin rollers, finials and inscribed parchment (top-left), Pair of Shabba
function MitzvahMotif({ color, accent, accent2, scale = 300 }) {
  const k = Math.max(0.7, Math.min(1.4, scale / 300));
  const sw = 1.15 / k;
  const hair = 0.72 / k;

  const ink = color;
  const foil = accent || color;
  const foil2 = accent2 || accent || color;

  // six-pointed star drawn as a single outline (crisp at small sizes)
  const starPath = (cx, cy, r) => {
    const ri = r * 0.5774;
    let d = "";
    for (let i = 0; i < 12; i++) {
      const a = (Math.PI / 180) * (-90 + i * 30);
      const rr = i % 2 === 0 ? r : ri;
      d +=
        (i === 0 ? "M" : "L") +
        (cx + rr * Math.cos(a)).toFixed(2) +
        " " +
        (cy + rr * Math.sin(a)).toFixed(2) +
        " ";
    }
    return d + "Z";
  };

  // one triangle of the interlaced hexagram
  const triPath = (cx, cy, r, up) => {
    const angles = up ? [-90, 30, 150] : [90, 210, 330];
    return (
      angles
        .map((deg, i) => {
          const a = (Math.PI / 180) * deg;
          return (
            (i === 0 ? "M" : "L") +
            (cx + r * Math.cos(a)).toFixed(2) +
            " " +
            (cy + r * Math.sin(a)).toFixed(2)
          );
        })
        .join(" ") + " Z"
    );
  };

  const smallStars = [
    { x: 96, y: 24, r: 3.4 },
    { x: 206, y: 27, r: 3.9 },
    { x: 28, y: 150, r: 5.4 },
    { x: 272, y: 172, r: 4.0 },
    { x: 25, y: 206, r: 3.2 },
    { x: 275, y: 236, r: 5.0 },
    { x: 30, y: 268, r: 4.2 },
    { x: 270, y: 292, r: 3.4 },
    { x: 104, y: 343, r: 3.6 },
    { x: 197, y: 347, r: 3.0 }
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id="mtz-parch-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={hexA(foil, 0.22)} />
          <stop offset="100%" stopColor={hexA(foil, 0.06)} />
        </linearGradient>
        <linearGradient id="mtz-foil-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={hexA(foil, 0.26)} />
          <stop offset="55%" stopColor={hexA(foil2, 0.12)} />
          <stop offset="100%" stopColor={hexA(foil, 0.22)} />
        </linearGradient>
      </defs>

      {/* ── crest: interlaced Star of David, top centre ── */}
      <g>
        <path
          d={triPath(150, 30, 13, true)}
          fill="url(#mtz-foil-grad)"
          stroke={hexA(foil, 0.55)}
          strokeWidth={sw}
          strokeLinejoin="round"
        />
        <path
          d={triPath(150, 30, 13, false)}
          fill="url(#mtz-foil-grad)"
          stroke={hexA(foil, 0.55)}
          strokeWidth={sw}
          strokeLinejoin="round"
        />
        <path
          d="M104 30 H132 M168 30 H196"
          stroke={hexA(ink, 0.3)}
          strokeWidth={hair}
          fill="none"
          strokeLinecap="round"
        />
      </g>

      {/* ── Torah scroll, top-left ── */}
      <g transform="translate(42 55)">
        <path
          d="M-10.5 -19.5 V19.5 M10.5 -19.5 V19.5"
          stroke={hexA(foil, 0.5)}
          strokeWidth={sw}
          strokeLinecap="round"
          fill="none"
        />
        <circle cx="-10.5" cy="-19.5" r="2.3" fill={hexA(foil, 0.28)} stroke={hexA(foil, 0.5)} strokeWidth={hair} />
        <circle cx="10.5" cy="-19.5" r="2.3" fill={hexA(foil, 0.28)} stroke={hexA(foil, 0.5)} strokeWidth={hair} />
        <circle cx="-10.5" cy="19.5" r="2.3" fill={hexA(foil, 0.28)} stroke={hexA(foil, 0.5)} strokeWidth={hair} />
        <circle cx="10.5" cy="19.5" r="2.3" fill={hexA(foil, 0.28)} stroke={hexA(foil, 0.5)} strokeWidth={hair} />
        <rect
          x="-6.6"
          y="-11.4"
          width="13.2"
          height="22.8"
          rx="1.2"
          fill="url(#mtz-parch-grad)"
          stroke={hexA(ink, 0.4)}
          strokeWidth={hair}
        />
        <path
          d="M-3.7 -6 H3.7 M-3.7 -1 H3.7 M-3.7 4 H2.1"
          stroke={hexA(ink, 0.38)}
          strokeWidth={hair}
          strokeLinecap="round"
          fill="none"
        />
        <rect
          x="-15"
          y="-13"
          width="9"
          height="26"
          rx="4.3"
          fill={hexA(ink, 0.1)}
          stroke={hexA(ink, 0.48)}
          strokeWidth={sw}
        />
        <rect
          x="6"
          y="-13"
          width="9"
          height="26"
          rx="4.3"
          fill={hexA(ink, 0.1)}
          stroke={hexA(ink, 0.48)}
          strokeWidth={sw}
        />
      </g>

      {/* ── pair of Shabbat candlesticks, top-right ── */}
      <g transform="translate(252 74)">
        {[-9.5, 9.5].map((dx, i) => (
          <g key={`mtz-cdl-${i}`} transform={`translate(${dx} ${i === 1 ? -1.6 : 0})`}>
            <path
              d="M-5.4 0 L5.4 0 L3.2 -2.2 L1.15 -4.4 L1.15 -10.2 L3.6 -12.6 L-3.6 -12.6 L-1.15 -10.2 L-1.15 -4.4 L-3.2 -2.2 Z"
              fill={hexA(ink, 0.12)}
              stroke={hexA(ink, 0.5)}
              strokeWidth={sw}
              strokeLinejoin="round"
            />
            <path
              d="M-1.25 -12.6 L1.25 -12.6 L1 -20.2 L-1 -20.2 Z"
              fill={hexA(ink, 0.1)}
              stroke={hexA(ink, 0.44)}
              strokeWidth={hair}
            />
            <path
              d="M0 -26.6 C2.4 -23.3 2.1 -21 0 -20.5 C-2.1 -21 -2.4 -23.3 0 -26.6 Z"
              fill={hexA(foil, 0.26)}
              stroke={hexA(foil, 0.55)}
              strokeWidth={hair}
            />
          </g>
        ))}
      </g>

      {/* ── kiddush cup, bottom-left ── */}
      <g transform="translate(44 344)">
        <path
          d="M-7.4 -14 L7.4 -14 L6 -5.6 C5.4 -2.4 3.6 -1.2 1.3 -0.8 L1.3 4.8 C3.8 5.4 5.6 6.4 6.6 8 L-6.6 8 C-5.6 6.4 -3.8 5.4 -1.3 4.8 L-1.3 -0.8 C-3.6 -1.2 -5.4 -2.4 -6 -5.6 Z"
          fill="url(#mtz-parch-grad)"
          stroke={hexA(ink, 0.5)}
          strokeWidth={sw}
          strokeLinejoin="round"
        />
        <path
          d="M-6.9 -10.7 H6.9 M-6.4 -8.3 H6.4"
          stroke={hexA(foil, 0.42)}
          strokeWidth={hair}
          fill="none"
        />
        <path d={starPath(0, -4.4, 2.9)} fill="none" stroke={hexA(foil, 0.5)} strokeWidth={hair} />
      </g>

      {/* ── pomegranate, bottom-right ── */}
      <g transform="translate(256 348)">
        <path
          d="M0 -7.6 C6.8 -7.6 10 -2.2 10 3.4 C10 9.6 5.6 13.8 0 13.8 C-5.6 13.8 -10 9.6 -10 3.4 C-10 -2.2 -6.8 -7.6 0 -7.6 Z"
          fill={hexA(foil, 0.14)}
          stroke={hexA(ink, 0.5)}
          strokeWidth={sw}
        />
        <path
          d="M-3.4 -7.4 L-5 -12.6 L-1.9 -10.2 L0 -15 L1.9 -10.2 L5 -12.6 L3.4 -7.4"
          fill="none"
          stroke={hexA(ink, 0.5)}
          strokeWidth={sw}
          strokeLinejoin="round"
        />
        <circle cx="-4.2" cy="2.6" r="1.9" fill="none" stroke={hexA(foil, 0.42)} strokeWidth={hair} />
        <circle cx="4.2" cy="2.6" r="1.9" fill="none" stroke={hexA(foil, 0.42)} strokeWidth={hair} />
        <circle cx="0" cy="6.4" r="1.9" fill="none" stroke={hexA(foil, 0.42)} strokeWidth={hair} />
      </g>

      {/* ── star scatter down the margins ── */}
      {smallStars.map((s, i) => (
        <path
          key={`mtz-s-${i}`}
          d={starPath(s.x, s.y, s.r)}
          fill={i % 3 === 0 ? hexA(foil, 0.14) : "none"}
          stroke={hexA(i % 2 === 0 ? foil : ink, 0.3 + ((i * 5) % 4) * 0.06)}
          strokeWidth={hair}
          strokeLinejoin="round"
        />
      ))}

      {/* ── footer rule with star, bottom edge ── */}
      <path
        d={starPath(150, 372, 6.2)}
        fill={hexA(foil, 0.16)}
        stroke={hexA(foil, 0.5)}
        strokeWidth={hair}
        strokeLinejoin="round"
      />
      <path
        d="M96 372 H138 M162 372 H204"
        stroke={hexA(ink, 0.28)}
        strokeWidth={hair}
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ─── QuinceMotif — tiara with gem drop and beaded band, open folding fan (abanico), rose bloom with leaves, r
function QuinceMotif({ color, accent, accent2, scale = 300 }) {
  const a1 = accent || color;
  const a2 = accent2 || accent || color;
  const k = Math.max(0.7, Math.min(1.4, 300 / (scale || 300)));
  const sw = 1.15 * k;
  const thin = 0.75 * k;

  const line = hexA(a1, 0.58);
  const soft = hexA(a1, 0.38);
  const wash = hexA(a2, 0.2);
  const gem = hexA(a2, 0.45);
  const dot = hexA(color, 0.3);

  const star = (cx, cy, s) =>
    "M" + cx + " " + (cy - s) +
    "Q" + (cx + s * 0.2) + " " + (cy - s * 0.2) + " " + (cx + s) + " " + cy +
    "Q" + (cx + s * 0.2) + " " + (cy + s * 0.2) + " " + cx + " " + (cy + s) +
    "Q" + (cx - s * 0.2) + " " + (cy + s * 0.2) + " " + (cx - s) + " " + cy +
    "Q" + (cx - s * 0.2) + " " + (cy - s * 0.2) + " " + cx + " " + (cy - s) + "Z";

  const bandDots = [[126, 63.5], [138, 61.3], [150, 60.5], [162, 61.3], [174, 63.5]];
  const ribs = [
    [25.9, 83.2, 65.5, 77.6],
    [25.3, 81.1, 60.2, 61.7],
    [23.9, 79.4, 49.6, 48.8],
    [22.0, 78.3, 35.0, 40.5],
    [19.8, 78.0, 18.4, 38.1]
  ];
  const scallops = [48, 82, 116, 150, 184, 218];
  const laceDots = [48, 82, 116, 150, 184, 218, 252];
  const sparks = [[150, 11, 4.2], [90, 44, 2.8], [212, 60, 2.4], [34, 262, 2.4], [266, 268, 2.2]];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id="quince-motif-fan-grad" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor={hexA(a2, 0.3)} />
          <stop offset="100%" stopColor={hexA(a1, 0.1)} />
        </linearGradient>
        <linearGradient id="quince-motif-band-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={hexA(a2, 0.42)} />
          <stop offset="100%" stopColor={hexA(a1, 0.16)} />
        </linearGradient>
      </defs>

      <g fill="none" stroke={line} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
        {/* tiara — top centre */}
        <path d="M116 64Q150 52 184 64L184 69Q150 57 116 69Z" fill="url(#quince-motif-band-grad)" />
        <path d="M136 59C136 41 142 29 150 25C158 29 164 41 164 59" />
        <path d="M120 62C120 47 124 39 129 35C134 40 137 48 138 59" />
        <path d="M180 62C180 47 176 39 171 35C166 40 163 48 162 59" />
        <path d="M150 17L153.4 22L150 27L146.6 22Z" fill={gem} />
        <circle cx="129" cy="32" r="1.9" fill={gem} />
        <circle cx="171" cy="32" r="1.9" fill={gem} />
        <circle cx="116" cy="65.5" r="1.7" fill={gem} />
        <circle cx="184" cy="65.5" r="1.7" fill={gem} />
        {bandDots.map((d, i) => (
          <circle key={"quince-motif-bead-" + i} cx={d[0]} cy={d[1]} r="0.9" fill={dot} stroke="none" />
        ))}

        {/* folding fan (abanico) — top left */}
        <path
          d="M65.5 77.6A46 46 0 0 0 18.4 38.1L19.4 68A16 16 0 0 1 35.8 81.8Z"
          fill="url(#quince-motif-fan-grad)"
        />
        <path d="M57.6 78.7A38 38 0 0 0 18.7 46" stroke={soft} strokeWidth={thin} />
        {ribs.map((r, i) => (
          <path key={"quince-motif-rib-" + i} d={"M" + r[0] + " " + r[1] + "L" + r[2] + " " + r[3]} strokeWidth={thin} />
        ))}
        <circle cx="20" cy="84" r="2.2" fill={gem} />

        {/* rose — top right */}
        <path d="M262 33C268 32 273 36 274 42C276 48 272 55 266 58C260 61 253 58 250 53C247 48 248 40 252 36C255 33 258 33 262 33Z" fill={wash} />
        <path d="M262 47C259 47 257 45 258 43C260 40 264 41 265 44C266 48 262 51 258 50C253 49 251 44 253 40C256 35 263 34 267 38" strokeWidth={thin} />
        <path d="M251 51C246 43 249 34 257 32" stroke={soft} strokeWidth={thin} />
        <path d="M274 42C277 50 271 58 263 58" stroke={soft} strokeWidth={thin} />
        <path d="M262 58C262 63 261 67 261 73" strokeWidth={thin} />
        <path d="M261 61C255 59 249 62 248 67C254 69 260 67 261 61Z" fill={wash} strokeWidth={thin} />
        <path d="M262 66C268 65 273 67 274 72C269 74 263 71 262 66Z" fill={wash} strokeWidth={thin} />

        {/* ribbon bow — left margin */}
        <path d="M30 196C20 181 9 185 11 194C12 202 21 201 30 196Z" fill={wash} />
        <path d="M30 196C40 181 49 185 47 194C46 202 38 201 30 196Z" fill={wash} />
        <ellipse cx="30" cy="196.5" rx="3.2" ry="2.8" fill={gem} />
        <path d="M28 200C26 212 24 224 20 238L27 235C28 224 29 212 30 202Z" fill={wash} />
        <path d="M32 200C34 212 36 224 40 238L33 235C32 224 31 212 30 202Z" fill={wash} />

        {/* rosebud sprig — right margin */}
        <path d="M270 178C267 194 271 210 268 232" stroke={soft} strokeWidth={thin} />
        <path d="M270 166C274 169 275 175 272 178C269 180 266 177 267 172C268 169 269 167 270 166Z" fill={wash} strokeWidth={thin} />
        <path d="M272 194C275 196 275 201 273 203C270 204 268 201 269 197Z" fill={wash} strokeWidth={thin} />
        <path d="M270 190C275 188 279 190 280 194C276 196 271 194 270 190Z" fill={wash} strokeWidth={thin} />
        <path d="M269 214C264 214 260 218 260 223C265 224 269 220 269 214Z" fill={wash} strokeWidth={thin} />

        {/* stiletto heel — bottom left */}
        <path d="M46 354C44 357 42 360 40 363L43.5 364C45.5 360 47.5 357 49 354Z" fill={gem} />
        <path d="M73 362C66 362 60 361 54 359C50 357 47 356 46 354C45 348 44 342 44 337C44 334 47 333 49 336C51 342 54 348 59 353C64 356 69 359 73 362Z" fill={wash} />
        <path d="M46 339C48 344 51 349 56 353" stroke={soft} strokeWidth={thin} />
        <path d="M60 356C57 353 54 354 55 357C56 359 59 358 60 356Z" strokeWidth={thin} />
        <path d="M60 356C63 353 66 354 65 357C64 359 61 358 60 356Z" strokeWidth={thin} />

        {/* rosette — bottom right */}
        <path d="M258 333C263 332 268 336 269 341C270 346 267 352 262 354C257 356 251 353 249 349C246 344 248 337 252 334C254 333 256 333 258 333Z" fill={wash} />
        <path d="M258 345C255 345 254 343 255 341C257 339 260 340 261 342C262 345 259 348 255 347C251 346 249 342 251 339C253 335 259 334 262 337" strokeWidth={thin} />
        <path d="M248 348C244 341 248 334 255 334" stroke={soft} strokeWidth={thin} />
        <path d="M250 353C245 353 241 357 241 362C246 363 250 359 250 353Z" fill={wash} strokeWidth={thin} />
        <path d="M266 352C271 353 274 357 273 361C268 361 265 357 266 352Z" fill={wash} strokeWidth={thin} />

        {/* mantilla lace scallops — bottom edge */}
        {scallops.map((x, i) => (
          <path key={"quince-motif-scallop-" + i} d={"M" + x + " 378A26 26 0 0 1 " + (x + 34) + " 378"} strokeWidth={thin} />
        ))}
        {laceDots.map((x, i) => (
          <circle key={"quince-motif-lacedot-" + i} cx={x} cy="378" r="1.1" fill={dot} stroke="none" />
        ))}

        {/* sparkles */}
        {sparks.map((s, i) => (
          <path key={"quince-motif-spark-" + i} d={star(s[0], s[1], s[2])} fill={gem} stroke="none" />
        ))}
      </g>
    </svg>
  );
}

// ─── CocktailHourMotif — martini glass with an olive on a pick, coupe glass with rising bubbles, cobbler cocktail s
function CocktailHourMotif({ color, accent, accent2, scale = 300 }) {
  const sw = Math.max(1.05, Math.min(1.7, 1.6 * (300 / scale)));
  const ink = hexA(color, 0.55);
  const inkSoft = hexA(color, 0.3);
  const inkFaint = hexA(color, 0.2);
  const fizz = hexA(accent, 0.42);
  const zest = hexA(accent2, 0.44);

  const bubbles = [
    [200, 40, 2.2], [209, 31, 1.4], [190, 29, 1.0],
    [127, 352, 2.2], [136, 344, 1.4], [119, 342, 1.0],
    [26, 198, 2.0], [31, 213, 1.3], [24, 229, 1.0], [29, 243, 1.6],
  ];
  const spokes = [0, 1, 2, 3, 4, 5];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id="ckt-hour-pour" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={hexA(accent, 0.3)} />
          <stop offset="100%" stopColor={hexA(accent, 0.08)} />
        </linearGradient>
      </defs>

      {/* martini with olive on a pick — top left */}
      <g transform="translate(46 78) scale(1.15)" fill="none" stroke={ink} strokeWidth={sw} strokeLinejoin="round">
        <path d="M -16 -30 L 16 -30 L 0 -10 Z" fill="url(#ckt-hour-pour)" />
        <path d="M -13.4 -26 L 13.4 -26" stroke={fizz} strokeWidth={sw * 0.8} />
        <path d="M 0 -10 L 0 4" strokeLinecap="round" />
        <path d="M -8.5 4.4 L 8.5 4.4" strokeLinecap="round" strokeWidth={sw * 1.15} />
        <path d="M 2 -16.5 L 13.5 -34" strokeLinecap="round" strokeWidth={sw * 0.8} />
        <circle cx="1.8" cy="-16.2" r="2.9" fill={zest} strokeWidth={sw * 0.8} />
      </g>

      {/* coupe — top right */}
      <g transform="translate(198 72)" fill="none" stroke={ink} strokeWidth={sw} strokeLinejoin="round">
        <path d="M -15 -24 A 15 13 0 0 0 15 -24 Z" fill="url(#ckt-hour-pour)" />
        <path d="M -17.5 -24 L 17.5 -24" strokeLinecap="round" />
        <path d="M -11 -19.5 A 11 8 0 0 0 11 -19.5" stroke={fizz} strokeWidth={sw * 0.75} />
        <path d="M 0 -11.5 L 0 6" strokeLinecap="round" />
        <path d="M -9 6.4 L 9 6.4" strokeLinecap="round" strokeWidth={sw * 1.15} />
      </g>

      {/* olive pick — top right corner */}
      <g transform="translate(255 37) rotate(-28)" fill="none" stroke={ink} strokeWidth={sw * 0.85} strokeLinecap="round">
        <path d="M -13 0 L 13 0" />
        <circle cx="-4.5" cy="0" r="3.1" fill={zest} />
        <circle cx="4.8" cy="0" r="3.1" fill={zest} />
        <circle cx="-4.5" cy="0" r="1" fill={inkSoft} stroke="none" />
      </g>

      {/* ice cube — top */}
      <g transform="translate(94 40)" stroke={ink} strokeWidth={sw * 0.8} strokeLinejoin="round">
        <path d="M 0 -9 L 9.5 -3.5 L 0 2 L -9.5 -3.5 Z" fill={hexA(accent, 0.1)} />
        <path d="M -9.5 -3.5 L -9.5 6 L 0 11.5 L 0 2 Z" fill={hexA(accent, 0.16)} />
        <path d="M 9.5 -3.5 L 9.5 6 L 0 11.5 L 0 2 Z" fill={hexA(accent, 0.26)} />
      </g>

      {/* citrus wheel — right margin */}
      <g transform="translate(274 100)" fill="none" stroke={ink} strokeWidth={sw * 0.85}>
        <circle cx="0" cy="0" r="11.5" fill={hexA(accent2, 0.14)} />
        <circle cx="0" cy="0" r="8.4" stroke={inkSoft} strokeWidth={sw * 0.7} />
        {spokes.map((i) => (
          <path
            key={`ckt-spoke-${i}`}
            d="M 0 -1.6 L 0 -8"
            transform={`rotate(${i * 60})`}
            stroke={inkFaint}
            strokeWidth={sw * 0.7}
            strokeLinecap="round"
          />
        ))}
      </g>

      {/* cobbler shaker — bottom right */}
      <g transform="translate(244 332) scale(1.1)" fill="none" stroke={ink} strokeWidth={sw} strokeLinejoin="round">
        <path
          d="M -10.4 11 C -9.7 1 -8 -5 -6.6 -9.4 L -7.8 -9.4 L -6.1 -15 L -4.8 -15 L -4.8 -18 C -4.8 -20.6 4.8 -20.6 4.8 -18 L 4.8 -15 L 6.1 -15 L 7.8 -9.4 L 6.6 -9.4 C 8 -5 9.7 1 10.4 11 Z"
          fill={hexA(accent, 0.15)}
        />
        <path d="M -7.2 -9.4 L 7.2 -9.4" stroke={inkFaint} strokeWidth={sw * 0.6} />
        <path d="M -5.8 -15 L 5.8 -15" stroke={inkFaint} strokeWidth={sw * 0.6} />
        <path d="M -5.9 7.5 L -4 -5" stroke={hexA(accent, 0.22)} strokeWidth={sw * 0.8} strokeLinecap="round" />
      </g>

      {/* lemon twist — bottom left */}
      <g transform="translate(48 344) scale(0.85)" strokeLinejoin="round">
        <path
          d="M -11.4 18.3 L -12.6 17.3 L -14.2 15.6 L -15.8 13.5 L -17.3 10.8 L -18.5 7.8 L -19.1 4.4 L -19.1 0.9 L -18.5 -2.7 L -17.1 -6.1 L -15 -9.3 L -12.3 -11.9 L -9.1 -13.9 L -5.5 -15 L -1.8 -15.3 L 1.8 -14.7 L 5.1 -13.3 L 7.9 -11.1 L 10 -8.3 L 11.2 -5.2 L 11.5 -1.9 L 10.9 1.2 L 9.6 3.8 L 7.6 5.9 L 5.2 7.2 L 2.7 7.7 L 0.3 7.4 L -1.7 6.5 L -3.2 5.1 L -4 3.4 L -4.1 1.7 L -3.5 1.5 L -3.3 2.8 L -2.6 4.2 L -1.4 5.4 L 0.3 6.2 L 2.2 6.4 L 4.3 5.9 L 6.2 4.8 L 7.8 3.1 L 8.9 0.9 L 9.4 -1.6 L 9.1 -4.2 L 8.1 -6.8 L 6.4 -9 L 4.2 -10.8 L 1.5 -12 L -1.5 -12.5 L -4.5 -12.2 L -7.4 -11.3 L -10 -9.7 L -12.2 -7.5 L -13.9 -5 L -15 -2.2 L -15.5 0.7 L -15.5 3.6 L -15 6.3 L -14 8.8 L -12.8 10.9 L -11.5 12.7 L -10.2 14 L -9.2 14.8 Z"
          fill={zest}
          stroke={hexA(color, 0.26)}
          strokeWidth={sw * 0.6}
        />
        <path
          d="M -11.5 12.7 L -12.8 10.9 L -14 8.8 L -15 6.3 L -15.5 3.6 L -15.5 0.7 L -15 -2.2 L -13.9 -5 L -12.2 -7.5 L -10 -9.7 L -7.4 -11.3 L -4.5 -12.2 L -1.5 -12.5 L 1.5 -12 L 4.2 -10.8 L 6.4 -9 L 8.1 -6.8 L 9.1 -4.2 L 9.4 -1.6 L 8.9 0.9 L 7.8 3.1 L 6.2 4.8 L 4.3 5.9 L 2.2 6.4"
          fill="none"
          stroke={hexA(color, 0.16)}
          strokeWidth={sw * 0.9}
        />
      </g>

      {/* second ice cube — bottom */}
      <g transform="translate(112 368) scale(0.85)" stroke={ink} strokeWidth={sw * 0.85} strokeLinejoin="round">
        <path d="M 0 -9 L 9.5 -3.5 L 0 2 L -9.5 -3.5 Z" fill={hexA(accent, 0.1)} />
        <path d="M -9.5 -3.5 L -9.5 6 L 0 11.5 L 0 2 Z" fill={hexA(accent, 0.16)} />
        <path d="M 9.5 -3.5 L 9.5 6 L 0 11.5 L 0 2 Z" fill={hexA(accent, 0.26)} />
      </g>

      {/* effervescence */}
      {bubbles.map((b, i) => (
        <circle
          key={`ckt-bub-${i}`}
          cx={b[0]}
          cy={b[1]}
          r={b[2]}
          fill="none"
          stroke={i % 3 === 0 ? fizz : inkSoft}
          strokeWidth={sw * 0.7}
        />
      ))}

      {/* hairline deco rules */}
      <g stroke={inkFaint} strokeWidth={sw * 0.7} fill={inkFaint}>
        <path d="M 120 94 L 143 94" />
        <path d="M 157 94 L 180 94" />
        <path d="M 150 90.5 L 153 94 L 150 97.5 L 147 94 Z" stroke="none" />
        <path d="M 120 326 L 143 326" />
        <path d="M 157 326 L 180 326" />
        <path d="M 150 322.5 L 153 326 L 150 329.5 L 147 326 Z" stroke="none" />
      </g>
    </svg>
  );
}

// ─── WineTastingMotif — Bordeaux wine bottle with foil capsule and label (top-left), Tall stemmed tasting glass wi
function WineTastingMotif({ color, accent, accent2, scale = 300 }) {
  const sw = scale < 220 ? 1.45 : 1.15;
  const swFine = scale < 220 ? 1.05 : 0.85;

  const ink = hexA(color, 0.5);
  const inkSoft = hexA(color, 0.32);
  const glassFill = hexA(color, 0.05);
  const vine = hexA(accent2, 0.5);
  const vineFill = hexA(accent2, 0.13);
  const fruit = hexA(accent, 0.48);
  const fruitFill = hexA(accent, 0.2);
  const wineTint = hexA(accent, 0.3);
  const corkFill = hexA(accent2, 0.14);

  const berriesA = [
    [-7.2, -0.4, 3.7], [0, -2.2, 3.6], [7.2, -0.6, 3.5],
    [-10.4, 6.2, 3.5], [-3.5, 5.6, 3.8], [3.5, 5.8, 3.7], [10.2, 6.4, 3.4],
    [-6.9, 12.6, 3.6], [0.2, 12.2, 3.7], [6.9, 12.8, 3.4],
    [-3.2, 18.8, 3.5], [3.4, 19, 3.4],
    [0, 25, 3.3]
  ];
  const berriesB = [
    [-5.6, 0, 3.1], [0.4, -1.4, 3], [5.6, 0.2, 2.9],
    [-6.2, 6, 2.7], [-2.9, 5.6, 3.1], [3.1, 5.8, 3],
    [0, 11.2, 3]
  ];

  const leafBlade = "M 0 -1.5 C -1.4 -5 -5.6 -6.2 -8.6 -8.4 C -12 -10.8 -11.6 -15.4 -7.4 -15.6 C -9 -19.2 -5.4 -22.6 -1.4 -20.8 C -0.8 -22.6 0.8 -22.6 1.4 -20.8 C 5.4 -22.6 9 -19.2 7.4 -15.6 C 11.6 -15.4 12 -10.8 8.6 -8.4 C 5.6 -6.2 1.4 -5 0 -1.5 Z";
  const leafVeins = "M 0 2.6 V -19 M 0 -13.4 L -8.2 -9.6 M 0 -13.4 L 8.2 -9.6 M 0 -17 L -6.2 -18.6 M 0 -17 L 6.2 -18.6";
  const tendril = "M 0 0 C 6.5 -1.2 11 1.6 12.6 6.4 C 14 10.6 11.2 13.8 8.6 12.6 C 6.2 11.5 6.6 8 9.2 7.9";

  const bowl = "M -12.8 -62 C -12.8 -44.5 -6.4 -32.5 0 -30.4 C 6.4 -32.5 12.8 -44.5 12.8 -62";
  const pour = "M -9.4 -46.6 C -8.2 -37.6 -4 -32.4 0 -30.4 C 4 -32.4 8.2 -37.6 9.4 -46.6 Z";

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id="wine-tst-pour" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={hexA(accent, 0.2)} />
          <stop offset="1" stopColor={hexA(accent, 0.44)} />
        </linearGradient>
      </defs>

      <g fill="none" strokeLinecap="round" strokeLinejoin="round">

        {/* ---- top-left: bottle ---- */}
        <g transform="translate(34 112)">
          <path
            d="M -10.6 -1.6 C -10.6 -0.5 -10 0 -8.8 0 H 8.8 C 10 0 10.6 -0.5 10.6 -1.6 V -46 C 10.6 -56 4.2 -58.5 3.9 -66.5 V -85 C 3.9 -87.4 4.9 -88.2 4.9 -90.2 C 4.9 -91.6 4 -92.2 2.2 -92.2 H -2.2 C -4 -92.2 -4.9 -91.6 -4.9 -90.2 C -4.9 -88.2 -3.9 -87.4 -3.9 -85 V -66.5 C -4.2 -58.5 -10.6 -56 -10.6 -46 Z"
            stroke={ink}
            strokeWidth={sw}
            fill={hexA(accent, 0.1)}
          />
          <path
            d="M -4.9 -90.2 C -4.9 -88.2 -3.9 -87.4 -3.9 -85 V -80.6 H 3.9 V -85 C 3.9 -87.4 4.9 -88.2 4.9 -90.2 C 4.9 -91.6 4 -92.2 2.2 -92.2 H -2.2 C -4 -92.2 -4.9 -91.6 -4.9 -90.2 Z"
            fill={wineTint}
            stroke={ink}
            strokeWidth={swFine}
          />
          <rect x="-8.4" y="-40" width="16.8" height="21" rx="1.6" stroke={ink} strokeWidth={swFine} fill="none" />
          <path d="M -4.6 -32.6 H 4.6 M -3 -27.4 H 3" stroke={inkSoft} strokeWidth={swFine} />
          <path d="M -7.4 -52.6 C -7 -57.4 -3.4 -59.6 -3.2 -64.4" stroke={inkSoft} strokeWidth={swFine} />
        </g>

        {/* ---- top-left: tasting glass ---- */}
        <g transform="translate(74 110)">
          <ellipse cx="0" cy="0" rx="10.6" ry="2.4" stroke={ink} strokeWidth={sw} fill="none" />
          <path d="M 0 -1.6 V -30.4" stroke={ink} strokeWidth={sw} />
          <path d={bowl} stroke={ink} strokeWidth={sw} fill={glassFill} />
          <path d={pour} fill="url(#wine-tst-pour)" />
          <ellipse cx="0" cy="-46.6" rx="9.4" ry="2" stroke={hexA(accent, 0.42)} strokeWidth={swFine} fill="none" />
          <ellipse cx="0" cy="-62" rx="12.8" ry="2.8" stroke={ink} strokeWidth={swFine} fill="none" />
        </g>

        {/* ---- top-right: grape cluster + leaf + tendril ---- */}
        <g transform="translate(254 46)">
          <path d="M 0 -6.4 C -0.8 -10.4 0.6 -13.6 3 -15.8" stroke={vine} strokeWidth={sw} />
          {berriesA.map((b, i) => (
            <circle
              key={`wine-tst-a-${i}`}
              cx={b[0]}
              cy={b[1]}
              r={b[2]}
              fill={fruitFill}
              stroke={fruit}
              strokeWidth={swFine}
            />
          ))}
        </g>
        <g transform="translate(231 36) rotate(-28)">
          <path d={leafBlade} fill={vineFill} stroke={vine} strokeWidth={sw} />
          <path d={leafVeins} stroke={hexA(accent2, 0.34)} strokeWidth={swFine} />
        </g>
        <g transform="translate(271 74) rotate(22)">
          <path d={tendril} stroke={vine} strokeWidth={swFine} />
        </g>

        {/* ---- left margin: single vine leaf ---- */}
        <g transform="translate(28 208) rotate(-16)">
          <path d={leafBlade} fill={vineFill} stroke={vine} strokeWidth={sw} />
          <path d={leafVeins} stroke={hexA(accent2, 0.34)} strokeWidth={swFine} />
        </g>

        {/* ---- right margin: tendril ---- */}
        <g transform="translate(272 230) rotate(108)">
          <path d={tendril} stroke={vine} strokeWidth={swFine} />
        </g>

        {/* ---- bottom-left: small cluster + cork ---- */}
        <g transform="translate(40 340) scale(0.95)">
          <path d="M 0 -5.2 C -0.6 -8.6 0.8 -11.4 2.8 -13.2" stroke={vine} strokeWidth={sw} />
          {berriesB.map((b, i) => (
            <circle
              key={`wine-tst-b-${i}`}
              cx={b[0]}
              cy={b[1]}
              r={b[2]}
              fill={fruitFill}
              stroke={fruit}
              strokeWidth={swFine}
            />
          ))}
        </g>
        <g transform="translate(26 384) rotate(-14)">
          <rect x="-10.5" y="-4.6" width="21" height="9.2" rx="2.6" fill={corkFill} stroke={ink} strokeWidth={sw} />
          <path d="M -3.2 -4.4 V 4.4 M 3 -4.4 V 4.4" stroke={inkSoft} strokeWidth={swFine} />
          <ellipse cx="8.2" cy="0" rx="2.3" ry="4.6" stroke={inkSoft} strokeWidth={swFine} fill="none" />
        </g>

        {/* ---- bottom-right: glass + cork + leaf ---- */}
        <g transform="translate(256 388) scale(0.85)">
          <ellipse cx="0" cy="0" rx="10.6" ry="2.4" stroke={ink} strokeWidth={sw} fill="none" />
          <path d="M 0 -1.6 V -30.4" stroke={ink} strokeWidth={sw} />
          <path d={bowl} stroke={ink} strokeWidth={sw} fill={glassFill} />
          <path d={pour} fill="url(#wine-tst-pour)" />
          <ellipse cx="0" cy="-46.6" rx="9.4" ry="2" stroke={hexA(accent, 0.42)} strokeWidth={swFine} fill="none" />
          <ellipse cx="0" cy="-62" rx="12.8" ry="2.8" stroke={ink} strokeWidth={swFine} fill="none" />
        </g>
        <g transform="translate(214 374) rotate(9)">
          <rect x="-10.5" y="-4.6" width="21" height="9.2" rx="2.6" fill={corkFill} stroke={ink} strokeWidth={sw} />
          <path d="M -3.2 -4.4 V 4.4 M 3 -4.4 V 4.4" stroke={inkSoft} strokeWidth={swFine} />
          <ellipse cx="8.2" cy="0" rx="2.3" ry="4.6" stroke={inkSoft} strokeWidth={swFine} fill="none" />
        </g>
        <g transform="translate(284 332) rotate(30)">
          <path d={leafBlade} fill={vineFill} stroke={vine} strokeWidth={sw} />
          <path d={leafVeins} stroke={hexA(accent2, 0.34)} strokeWidth={swFine} />
        </g>

      </g>
    </svg>
  );
}

// ─── DinnerBrunchMotif — crossed fork and knife, rimmed plate seen from above, teapot with lid, spout and steam, co
function DinnerBrunchMotif({ color, accent, accent2, scale = 300 }) {
  const k = Math.max(0.8, Math.min(1.3, 300 / scale));
  const w1 = 1.5 * k;
  const w2 = 1.05 * k;
  const w3 = 0.85 * k;

  const ink = hexA(color, 0.52);
  const inkSoft = hexA(color, 0.34);
  const acc = hexA(accent, 0.5);
  const accSoft = hexA(accent, 0.36);
  const acc2c = hexA(accent2, 0.46);
  const wash = hexA(accent2, 0.09);
  const washWarm = hexA(accent, 0.08);

  const leaves = [
    { y: -3.6, x: 1.2, s: 1.0 },
    { y: -7.9, x: 1.8, s: 0.94 },
    { y: -12.2, x: 1.9, s: 0.88 },
    { y: -16.4, x: 1.5, s: 0.8 },
    { y: -20.4, x: 0.8, s: 0.72 },
    { y: -24.1, x: 0.1, s: 0.62 }
  ];

  const sprigs = [
    "translate(27,212) rotate(-5)",
    "translate(273,204) scale(-1,1) rotate(-7)"
  ];

  const crumbs = [
    { x: 96, y: 361, r: 1.3 },
    { x: 118, y: 344, r: 0.95 },
    { x: 224, y: 373, r: 1.15 },
    { x: 277, y: 340, r: 0.9 },
    { x: 74, y: 28, r: 1.05 },
    { x: 205, y: 82, r: 0.9 }
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <g fill="none" stroke={ink} strokeWidth={w1} strokeLinecap="round" strokeLinejoin="round">

        {/* crossed fork + knife — top left */}
        <g transform="translate(35,47)">
          <g transform="rotate(-13) translate(-5.5,0)">
            <path d="M-4.2,-9.6 C-4.2,-5.4 -1.5,-5.2 -1.5,-1.4 L-1.5,16.6 a1.5,1.5 0 0 0 3,0 L1.5,-1.4 C1.5,-5.2 4.2,-5.4 4.2,-9.6" />
            <path d="M-4.2,-9.6 L4.2,-9.6" strokeWidth={w3} stroke={inkSoft} />
            <path d="M-4.2,-19.8 L-4.2,-9.6" strokeWidth={w2} />
            <path d="M-1.4,-20.4 L-1.4,-9.6" strokeWidth={w2} />
            <path d="M1.4,-20.4 L1.4,-9.6" strokeWidth={w2} />
            <path d="M4.2,-19.8 L4.2,-9.6" strokeWidth={w2} />
          </g>
          <g transform="rotate(13) translate(5.5,0)">
            <path d="M-1.6,-20.5 C1.6,-16.4 2.9,-9.5 2.6,-4.2 L-1.6,-4.2 Z" fill={washWarm} />
            <path d="M-1.7,-4.2 L-1.7,16.4 a1.7,1.7 0 0 0 3.4,0 L1.7,-4.2 Z" />
            <circle cx="0" cy="11.4" r="0.85" fill={inkSoft} stroke="none" />
          </g>
        </g>

        {/* plate, top down — upper right */}
        <g transform="translate(233,53)">
          <circle cx="0" cy="0" r="14.6" fill={wash} />
          <circle cx="0" cy="0" r="10.2" stroke={acc} strokeWidth={w2} />
          <path d="M-6.2,3.1 A6.9,6.9 0 0 1 4.6,-5.1" stroke={inkSoft} strokeWidth={w3} />
        </g>

        {/* coffee cup + saucer — right margin */}
        <g transform="translate(269,97)">
          <path d="M-7.2,-6 L-5.8,4.4 C-5.6,6.3 -4.3,7.2 -2.6,7.2 L2.6,7.2 C4.3,7.2 5.6,6.3 5.8,4.4 L7.2,-6 Z" fill={wash} />
          <path d="M-7.7,-6 L7.7,-6" />
          <path d="M-6.9,-3.4 L6.9,-3.4" stroke={acc} strokeWidth={w3} />
          <path d="M7,-3.2 C11.3,-3.6 11.5,3 6.2,3.4" strokeWidth={w2} />
          <path d="M-11.4,8.8 L11.4,8.8 C10.6,11.9 7.4,12.9 0,12.9 C-7.4,12.9 -10.6,11.9 -11.4,8.8 Z" />
          <path d="M-2.6,-9.2 C-4.5,-11.8 -1,-13.6 -2.7,-16.6" stroke={accSoft} strokeWidth={w2} />
          <path d="M2.8,-9.6 C1,-12.6 4.5,-14.4 2.8,-17.8" stroke={accSoft} strokeWidth={w2} />
        </g>

        {/* teapot — bottom left */}
        <g transform="translate(49,351)">
          <path d="M-11.2,1.4 C-11.2,-5.6 -6.2,-10.2 0,-10.2 C6.2,-10.2 11.2,-5.6 11.2,1.4 C11.2,6.4 6.2,9.6 0,9.6 C-6.2,9.6 -11.2,6.4 -11.2,1.4 Z" fill={wash} />
          <path d="M-5.2,-9.5 C-4.4,-12.3 4.4,-12.3 5.2,-9.5" />
          <circle cx="0" cy="-13.1" r="1.5" fill={washWarm} />
          <path d="M-10.8,-3.2 C-14.6,-4.2 -16.8,-6.7 -17.9,-9.6 L-15.3,-10.5 C-14.3,-7.9 -12.7,-6.4 -10.2,-5.8" />
          <path d="M11,-3.4 C15.9,-4 16.9,3.2 11.2,4.9" />
          <path d="M-10.5,-0.4 C-6,1.9 6,1.9 10.5,-0.4" stroke={acc} strokeWidth={w3} />
          <path d="M-6.2,9.2 C-6.2,10.9 -5.2,11.5 -3.8,11.5 L3.8,11.5 C5.2,11.5 6.2,10.9 6.2,9.2" strokeWidth={w2} />
          <path d="M-1.5,-15.8 C-3.5,-18.6 0.1,-20.4 -1.7,-23.6" stroke={accSoft} strokeWidth={w2} />
          <path d="M3.4,-16.8 C1.6,-19.4 5,-21 3.4,-24.2" stroke={accSoft} strokeWidth={w2} />
        </g>

        {/* champagne coupe — bottom centre */}
        <g transform="translate(152,365)">
          <path d="M-7.6,-8.6 C-7.6,-2.6 -4.2,1.4 0,1.4 C4.2,1.4 7.6,-2.6 7.6,-8.6 Z" fill={wash} />
          <path d="M-6.8,-5.8 C-4.2,-4.3 4.2,-4.3 6.8,-5.8" stroke={acc} strokeWidth={w3} />
          <path d="M0,1.4 L0,9.2" strokeWidth={w2} />
          <path d="M-5.6,9.7 C-3,11 3,11 5.6,9.7" strokeWidth={w2} />
          <circle cx="2.4" cy="-7.2" r="0.7" fill={acc2c} stroke="none" />
        </g>

        {/* croissant — bottom right */}
        <g transform="translate(250,356) rotate(-8)">
          <path d="M-15.2,5.2 C-16,0.6 -13.2,-4.8 -8.4,-7.6 C-3.6,-10.4 3.6,-10.4 8.4,-7.6 C13.2,-4.8 16,0.6 15.2,5.2 C14.4,6.8 12.2,6.4 11.6,4.8 C10.4,0.2 6,-3 0,-3 C-6,-3 -10.4,0.2 -11.6,4.8 C-12.2,6.4 -14.4,6.8 -15.2,5.2 Z" fill={washWarm} />
          <path d="M-8.7,-7.1 C-7.6,-5.6 -7.4,-4.4 -7.6,-2.3" stroke={inkSoft} strokeWidth={w3} />
          <path d="M0,-9.7 C0.4,-7.2 0.3,-5.4 0,-3.2" stroke={inkSoft} strokeWidth={w3} />
          <path d="M8.7,-7.1 C7.6,-5.6 7.4,-4.4 7.6,-2.3" stroke={inkSoft} strokeWidth={w3} />
        </g>

        {/* herb sprigs — left + right margins */}
        {sprigs.map((t, si) => (
          <g key={"dnbr-sprig-" + si} transform={t}>
            <path d="M0,1.5 C2.1,-7 1.7,-17 -0.7,-26.4" stroke={acc} strokeWidth={w2} />
            {leaves.map((lf, i) => (
              <path
                key={"dnbr-leaf-" + si + "-" + i}
                d="M0,0 C2.6,-2.3 6.3,-2.6 8,-1 C6.4,1.5 2.6,1.7 0,0 Z"
                transform={
                  "translate(" + lf.x + "," + lf.y + ") scale(" +
                  (i % 2 === 0 ? lf.s : -lf.s) + "," + lf.s + ") rotate(-34)"
                }
                fill={hexA(accent2, 0.12)}
                stroke={acc}
                strokeWidth={w3 * (1 / lf.s)}
              />
            ))}
          </g>
        ))}

        {/* crumbs */}
        {crumbs.map((c, i) => (
          <circle
            key={"dnbr-crumb-" + i}
            cx={c.x}
            cy={c.y}
            r={c.r}
            fill={i % 2 === 0 ? inkSoft : acc2c}
            stroke="none"
          />
        ))}
      </g>
    </svg>
  );
}

// ─── TeaPartyMotif — teacup on saucer with steam wisps (x3), bellied teapot with lid finial, curved spout and h
function TeaPartyMotif({ color, accent, accent2, scale = 300 }) {
  const k = Math.min(1.15, Math.max(0.85, scale / 300));
  const ink = hexA(color, 0.46);
  const inkSoft = hexA(color, 0.3);
  const tea = hexA(accent, 0.32);
  const cream = hexA(accent2, 0.2);
  const blush = hexA(accent, 0.2);

  const pieces = [
    { id: "pot", x: 34, y: 56, s: 1.15, r: -5 },
    { id: "cup", x: 84, y: 38, s: 1.0, r: 0 },
    { id: "cube", x: 118, y: 60, s: 0.95, r: 6 },
    { id: "mac", x: 234, y: 40, s: 1.0, r: -8 },
    { id: "rose", x: 272, y: 64, s: 1.0, r: 10 },
    { id: "cup", x: 276, y: 172, s: 0.92, r: 0 },
    { id: "pot", x: 46, y: 352, s: 0.95, r: 4 },
    { id: "mac", x: 96, y: 376, s: 0.9, r: 7 },
    { id: "cup", x: 250, y: 356, s: 1.06, r: 0 }
  ];

  const grains = [
    [22, 214, 1.3],
    [31, 237, 0.9],
    [19, 259, 1.1],
    [28, 283, 0.8],
    [286, 238, 1.0],
    [275, 264, 0.8]
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <g id="teaparty-cup">
          <ellipse cx="0" cy="9" rx="14" ry="3.4" fill={cream} />
          <ellipse cx="0" cy="8.6" rx="6.4" ry="1.5" stroke={inkSoft} />
          <path d="M-9 -5.6 C-8.6 0.4 -6.6 4.2 -5 5.4 L5 5.4 C6.6 4.2 8.6 0.4 9 -5.6" />
          <path d="M-3.4 5.4 L3.4 5.4 L3 7.3 L-3 7.3 Z" stroke={inkSoft} />
          <ellipse cx="0" cy="-5.6" rx="9" ry="2.5" fill={tea} />
          <path d="M9 -3.4 C13.8 -3.6 14.2 2.4 8.4 2.8" />
          <path d="M-3.2 -10 C-5.4 -12.6 -1.6 -14.4 -3.6 -17.4" stroke={inkSoft} />
          <path d="M3 -10.4 C1 -13 4.8 -14.8 2.8 -17.8" stroke={inkSoft} />
        </g>
        <g id="teaparty-pot">
          <path d="M-10.6 -2 C-14.8 -3.4 -17 -6.4 -17.6 -9.6 L-14.4 -10.4 C-13.8 -7.6 -12.2 -5.2 -9.2 -4" />
          <path d="M10.4 -2.6 C15.6 -3.4 16.8 3.6 10.2 5.4" />
          <path d="M-11 1.4 C-11 -4.8 -5.8 -8.4 0 -8.4 C5.8 -8.4 11 -4.8 11 1.4 C11 5.6 6 8.6 0 8.6 C-6 8.6 -11 5.6 -11 1.4 Z" fill={cream} />
          <path d="M-5.8 -8.2 C-5.2 -11.6 5.2 -11.6 5.8 -8.2 Z" fill={blush} />
          <circle cx="0" cy="-11.7" r="1.3" fill={blush} />
          <path d="M-4.6 0.8 C-2.4 -1.7 2.4 -1.7 4.6 0.8" stroke={inkSoft} />
          <circle cx="0" cy="3.4" r="1.1" fill={tea} stroke="none" />
          <path d="M-16.4 -13.2 C-18.4 -15.8 -14.7 -17.6 -16.7 -20.2" stroke={inkSoft} />
        </g>
        <g id="teaparty-mac">
          <path d="M-8 -0.9 C-8 -7.8 8 -7.8 8 -0.9 Z" fill={cream} />
          <path d="M-8 1.7 C-8 8.6 8 8.6 8 1.7 Z" fill={cream} />
          <path d="M-7.6 -0.7 L7.6 -0.7 L7.6 1.5 L-7.6 1.5 Z" fill={tea} stroke="none" />
          <path d="M-7.7 -0.7 L7.7 -0.7 M-7.7 1.5 L7.7 1.5" stroke={inkSoft} />
          <path d="M-6 -1.4 L-6 -2.7 M-2 -1.4 L-2 -3 M2 -1.4 L2 -2.8 M6 -1.4 L6 -2.4" stroke={inkSoft} />
        </g>
        <g id="teaparty-cube">
          <path d="M-4.8 -1.2 L0 -3.8 L4.8 -1.2 L0 1.4 Z" fill={cream} />
          <path d="M-4.8 -1.2 L0 1.4 L0 6.6 L-4.8 4 Z" fill={blush} />
          <path d="M4.8 -1.2 L0 1.4 L0 6.6 L4.8 4 Z" />
        </g>
        <g id="teaparty-rose">
          <path d="M0 5.4 L0.4 13.6" stroke={inkSoft} />
          <path d="M0.2 9 C-2.8 8 -5 9.4 -5.2 11.8 C-2.6 12.6 -0.4 11.4 0.2 9.5 Z" fill={cream} />
          <path d="M0.4 11.8 C3 11.4 4.8 12.8 5 15 C2.6 15.4 0.8 14.2 0.4 12.4 Z" fill={cream} />
          <path d="M-1.6 -5.6 C2.6 -7.2 6.4 -4.4 6.2 -0.6 C6 3 2.6 5.6 -0.8 5 C-4 4.4 -5.8 1.2 -4.6 -1.6" fill={blush} />
          <path d="M2.6 -1.6 C3.6 0.8 2 3 -0.4 2.8 C-2.6 2.6 -3.8 0.2 -2.2 -1.4 C-0.9 -2.7 1 -2.6 1.6 -1" stroke={inkSoft} />
          <path d="M-0.6 0.9 C0.2 0.3 0.8 -0.4 0.8 -1.1" stroke={inkSoft} />
        </g>
      </defs>

      <g fill="none" stroke={ink} strokeLinecap="round" strokeLinejoin="round">
        {pieces.map((p, i) => {
          const sc = p.s * k;
          return (
            <use
              key={p.id + "-" + i}
              href={"#teaparty-" + p.id}
              transform={
                "translate(" + p.x + " " + p.y + ") rotate(" + p.r + ") scale(" + sc.toFixed(3) + ")"
              }
              strokeWidth={(1.05 / sc).toFixed(2)}
            />
          );
        })}
        {grains.map((g, i) => (
          <circle
            key={"teaparty-grain-" + i}
            cx={g[0]}
            cy={g[1]}
            r={g[2] * k}
            fill={hexA(accent, 0.34)}
            stroke="none"
          />
        ))}
      </g>
    </svg>
  );
}

// ─── CorporateRetirementMotif — city skyline strip (bottom edge, two depth layers with lit-window dashes and two spires), 
function CorporateRetirementMotif({ color, accent, accent2, scale = 300 }) {
  const u = Math.max(0.78, Math.min(1.3, 320 / scale));
  const ink = (a) => hexA(color, a);
  const gold = (a) => hexA(accent, a);
  const sec = (a) => hexA(accent2, a);

  const towers = [
    { x: -8, w: 32, h: 44 },
    { x: 28, w: 18, h: 60, spire: 10 },
    { x: 50, w: 38, h: 34 },
    { x: 92, w: 28, h: 52 },
    { x: 124, w: 36, h: 42 },
    { x: 164, w: 20, h: 58, spire: 10 },
    { x: 188, w: 36, h: 46 },
    { x: 228, w: 30, h: 56 },
    { x: 262, w: 44, h: 40 },
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient
          id="corpret-skyfade"
          gradientUnits="userSpaceOnUse"
          x1="0"
          y1="400"
          x2="0"
          y2="330"
        >
          <stop offset="0" stopColor={color} stopOpacity="0.13" />
          <stop offset="1" stopColor={color} stopOpacity="0.01" />
        </linearGradient>
      </defs>

      {/* ---- distant skyline (bottom, behind) ---- */}
      <path
        d="M-4 400 V372 H16 V358 H34 V380 H58 V352 H80 V368 H104 V346 H128 V362 H152 V354 H178 V374 H202 V356 H226 V368 H250 V348 H272 V370 H304 V400 Z"
        fill={sec(0.1)}
        stroke={sec(0.28)}
        strokeWidth={0.9 * u}
        strokeLinejoin="round"
      />

      {/* ---- foreground skyline ---- */}
      {towers.map((t, i) => {
        const top = 400 - t.h;
        const cx = t.x + t.w / 2;
        const cols = t.w >= 34 ? [-6, 6] : [0];
        return (
          <g key={"corpret-tower-" + i}>
            <rect
              x={t.x}
              y={top}
              width={t.w}
              height={t.h + 6}
              fill="url(#corpret-skyfade)"
              stroke={ink(t.h > 48 ? 0.5 : 0.34)}
              strokeWidth={1.05 * u}
              strokeLinejoin="round"
            />
            {t.spire ? (
              <line
                x1={cx}
                y1={top}
                x2={cx}
                y2={top - t.spire}
                stroke={gold(0.5)}
                strokeWidth={1 * u}
                strokeLinecap="round"
              />
            ) : null}
            {cols.map((c, j) => (
              <line
                key={"corpret-win-" + i + "-" + j}
                x1={cx + c}
                y1={top + 8}
                x2={cx + c}
                y2={394}
                stroke={gold(0.42)}
                strokeWidth={1.6 * u}
                strokeDasharray="2 6"
                strokeLinecap="butt"
              />
            ))}
          </g>
        );
      })}

      {/* ---- briefcase, top left ---- */}
      <g transform="rotate(-5 34 44)">
        <rect
          x="11"
          y="30"
          width="46"
          height="29"
          rx="3"
          fill={ink(0.08)}
          stroke={ink(0.5)}
          strokeWidth={1.2 * u}
        />
        <path
          d="M27 30 V25 a4 4 0 0 1 4-4 h6 a4 4 0 0 1 4 4 v5"
          fill="none"
          stroke={ink(0.5)}
          strokeWidth={1.2 * u}
          strokeLinecap="round"
        />
        <line x1="11" y1="39" x2="57" y2="39" stroke={ink(0.36)} strokeWidth={0.9 * u} />
        <rect
          x="30"
          y="35"
          width="8"
          height="8"
          rx="1.5"
          fill={gold(0.22)}
          stroke={gold(0.55)}
          strokeWidth={1 * u}
        />
        <line x1="18" y1="47" x2="24" y2="47" stroke={ink(0.34)} strokeWidth={1 * u} strokeLinecap="round" />
        <line x1="44" y1="47" x2="50" y2="47" stroke={ink(0.34)} strokeWidth={1 * u} strokeLinecap="round" />
      </g>

      {/* ---- pocket watch on its chain, top right ---- */}
      <g>
        <path
          d="M262 -4 C263 10 274 12 272 20"
          fill="none"
          stroke={gold(0.45)}
          strokeWidth={1.4 * u}
          strokeDasharray="3 2.6"
          strokeLinecap="round"
        />
        <circle cx="272" cy="24" r="3.4" fill="none" stroke={gold(0.55)} strokeWidth={1.2 * u} />
        <rect x="270" y="27" width="4" height="4" rx="1" fill={gold(0.4)} />
        <circle cx="272" cy="46" r="16" fill={ink(0.06)} stroke={ink(0.5)} strokeWidth={1.3 * u} />
        <circle
          cx="272"
          cy="46"
          r="12.4"
          fill="none"
          stroke={gold(0.5)}
          strokeWidth={2.4 * u}
          strokeDasharray="1 5.49"
        />
        <line x1="272" y1="46" x2="272" y2="35" stroke={ink(0.55)} strokeWidth={1.2 * u} strokeLinecap="round" />
        <line x1="272" y1="46" x2="277.5" y2="55" stroke={ink(0.55)} strokeWidth={1.2 * u} strokeLinecap="round" />
        <circle cx="272" cy="46" r="1.5" fill={ink(0.55)} />
      </g>

      {/* ---- name badge on a lanyard, left margin ---- */}
      <g>
        <path
          d="M9 116 L24 143 L39 116"
          fill="none"
          stroke={sec(0.45)}
          strokeWidth={1.5 * u}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <rect x="20" y="142" width="8" height="6" rx="1.5" fill={ink(0.12)} stroke={ink(0.45)} strokeWidth={1 * u} />
        <rect
          x="8"
          y="148"
          width="32"
          height="44"
          rx="3"
          fill={ink(0.07)}
          stroke={ink(0.5)}
          strokeWidth={1.2 * u}
        />
        <rect x="12" y="153" width="13" height="15" rx="1.5" fill={sec(0.14)} stroke={sec(0.4)} strokeWidth={0.9 * u} />
        <circle cx="18.5" cy="158.5" r="2.4" fill="none" stroke={ink(0.42)} strokeWidth={0.9 * u} />
        <path d="M14.5 167 a4.5 4 0 0 1 8 0" fill="none" stroke={ink(0.42)} strokeWidth={0.9 * u} />
        <line x1="28" y1="156" x2="36" y2="156" stroke={gold(0.5)} strokeWidth={1.4 * u} strokeLinecap="round" />
        <line x1="28" y1="161" x2="35" y2="161" stroke={ink(0.3)} strokeWidth={1 * u} strokeLinecap="round" />
        <line x1="12" y1="177" x2="36" y2="177" stroke={ink(0.28)} strokeWidth={1 * u} strokeLinecap="round" />
        <line x1="12" y1="183" x2="29" y2="183" stroke={ink(0.22)} strokeWidth={1 * u} strokeLinecap="round" />
      </g>

      {/* ---- years-of-service medal, lower left ---- */}
      <g>
        <path
          d="M18 260 L14 280 L24 274 L34 280 L30 260"
          fill={sec(0.12)}
          stroke={sec(0.42)}
          strokeWidth={1.1 * u}
          strokeLinejoin="round"
        />
        <circle cx="24" cy="252" r="11" fill={ink(0.06)} stroke={ink(0.48)} strokeWidth={1.2 * u} />
        <path
          d="M24 246 L25.5 249.9 L29.7 250.1 L26.5 252.8 L27.5 256.9 L24 254.6 L20.5 256.9 L21.5 252.8 L18.3 250.1 L22.5 249.9 Z"
          fill={gold(0.4)}
          stroke={gold(0.55)}
          strokeWidth={0.7 * u}
          strokeLinejoin="round"
        />
      </g>

      {/* ---- champagne coupe, right margin ---- */}
      <g>
        <circle cx="272" cy="152" r="1.5" fill={gold(0.45)} />
        <circle cx="279" cy="146" r="1.1" fill={gold(0.38)} />
        <circle cx="274.5" cy="140" r="0.9" fill={gold(0.3)} />
        <path
          d="M261 159 C261 173 268 180 276 180 C284 180 291 173 291 159 Z"
          fill={sec(0.12)}
          stroke={ink(0.5)}
          strokeWidth={1.2 * u}
          strokeLinejoin="round"
        />
        <line x1="262.5" y1="164" x2="289.5" y2="164" stroke={gold(0.4)} strokeWidth={1 * u} />
        <line x1="276" y1="180" x2="276" y2="197" stroke={ink(0.5)} strokeWidth={1.2 * u} strokeLinecap="round" />
        <path d="M267 199 Q276 194 285 199" fill="none" stroke={ink(0.5)} strokeWidth={1.2 * u} strokeLinecap="round" />
      </g>

      {/* ---- fountain pen, lower right ---- */}
      <g transform="rotate(14 277 264)">
        <rect x="272.5" y="237" width="9" height="30" rx="2" fill={ink(0.1)} stroke={ink(0.48)} strokeWidth={1.2 * u} />
        <path
          d="M272.5 267 L281.5 267 L280 279.5 L274 279.5 Z"
          fill={ink(0.05)}
          stroke={ink(0.44)}
          strokeWidth={1.1 * u}
          strokeLinejoin="round"
        />
        <line x1="272.2" y1="264.5" x2="281.8" y2="264.5" stroke={gold(0.55)} strokeWidth={1.8 * u} />
        <path
          d="M274 279.5 L280 279.5 L277 293 Z"
          fill={gold(0.24)}
          stroke={gold(0.55)}
          strokeWidth={1 * u}
          strokeLinejoin="round"
        />
        <line x1="277" y1="283" x2="277" y2="291" stroke={gold(0.5)} strokeWidth={0.9 * u} strokeLinecap="round" />
        <path d="M280 240 V251" fill="none" stroke={ink(0.4)} strokeWidth={1.5 * u} strokeLinecap="round" />
      </g>
    </svg>
  );
}

// ─── GameDayMotif — bunting pennant string (7 flags) across the top edge, football with laces and end stripes,
function GameDayMotif({ color, accent, accent2, scale = 300 }) {
  const ink = color;
  const c2 = accent || color;
  const c3 = accent2 || accent || color;
  const sw = scale < 220 ? 1.85 : 1.45;
  const thin = sw * 0.72;

  const star =
    "M 0 -4 L 1.18 -1.35 L 4 -1.2 L 1.82 0.6 L 2.44 3.35 L 0 1.85 L -2.44 3.35 L -1.82 0.6 L -4 -1.2 L -1.18 -1.35 Z";

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id="gdm-sports-flagfill" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={hexA(c2, 0.3)} />
          <stop offset="100%" stopColor={hexA(c2, 0.07)} />
        </linearGradient>
        <linearGradient id="gdm-sports-cupfill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={hexA(c3, 0.28)} />
          <stop offset="100%" stopColor={hexA(c3, 0.06)} />
        </linearGradient>
      </defs>

      {/* ---- bunting string, top edge ---- */}
      <path
        d="M 6 15 Q 150 41 294 15"
        fill="none"
        stroke={hexA(ink, 0.42)}
        strokeWidth={thin}
        strokeLinecap="round"
      />
      {[0, 1, 2, 3, 4, 5, 6].map((i) => {
        const t = (i + 0.5) / 7;
        const m = 1 - t;
        const px = m * m * 6 + 2 * m * t * 150 + t * t * 294;
        const py = m * m * 15 + 2 * m * t * 41 + t * t * 15;
        const w = 8.4;
        const h = 17 + (i % 3) * 1.8;
        const cc = i % 3 === 0 ? c3 : i % 3 === 1 ? c2 : ink;
        return (
          <path
            key={"gdm-bunt-" + i}
            d={"M " + (px - w) + " " + py + " L " + (px + w) + " " + py + " L " + px + " " + (py + h) + " Z"}
            fill={hexA(cc, 0.18)}
            stroke={hexA(cc, 0.5)}
            strokeWidth={thin}
            strokeLinejoin="round"
          />
        );
      })}

      {/* ---- football, top-left ---- */}
      <g transform="translate(40 80) rotate(-16)">
        <path
          d="M -19 0 C -13 -9, -6 -12, 0 -12 C 6 -12, 13 -9, 19 0 C 13 9, 6 12, 0 12 C -6 12, -13 9, -19 0 Z"
          fill={hexA(ink, 0.13)}
          stroke={hexA(ink, 0.56)}
          strokeWidth={sw}
          strokeLinejoin="round"
        />
        <path
          d="M -13.4 -7.3 C -10.9 -3, -10.9 3, -13.4 7.3"
          fill="none"
          stroke={hexA(ink, 0.42)}
          strokeWidth={thin}
          strokeLinecap="round"
        />
        <path
          d="M 13.4 -7.3 C 10.9 -3, 10.9 3, 13.4 7.3"
          fill="none"
          stroke={hexA(ink, 0.42)}
          strokeWidth={thin}
          strokeLinecap="round"
        />
        <path
          d="M -6.5 0 L 6.5 0"
          fill="none"
          stroke={hexA(ink, 0.48)}
          strokeWidth={thin}
          strokeLinecap="round"
        />
        {[-5, -2.5, 0, 2.5, 5].map((lx, i) => (
          <path
            key={"gdm-lace-" + i}
            d={"M " + lx + " -3.1 L " + lx + " 3.1"}
            stroke={hexA(ink, 0.48)}
            strokeWidth={thin}
            strokeLinecap="round"
          />
        ))}
      </g>

      {/* ---- basketball, top-right ---- */}
      <g transform="translate(258 74)">
        <circle r="13" fill={hexA(c2, 0.12)} stroke={hexA(c2, 0.56)} strokeWidth={sw} />
        <path d="M -13 0 L 13 0" stroke={hexA(c2, 0.44)} strokeWidth={thin} />
        <path d="M 0 -13 L 0 13" stroke={hexA(c2, 0.44)} strokeWidth={thin} />
        <path
          d="M 0 -13 C -8.8 -6.5, -8.8 6.5, 0 13"
          fill="none"
          stroke={hexA(c2, 0.44)}
          strokeWidth={thin}
        />
        <path
          d="M 0 -13 C 8.8 -6.5, 8.8 6.5, 0 13"
          fill="none"
          stroke={hexA(c2, 0.44)}
          strokeWidth={thin}
        />
      </g>

      {/* ---- whistle, left margin ---- */}
      <g transform="translate(29 202) rotate(7)">
        <path
          d="M -19 -1.5 C -21.4 -1.5, -21.4 4.5, -19 4.5 L -1 5.5 L -1 -3 Z"
          fill={hexA(c3, 0.14)}
          stroke={hexA(c3, 0.52)}
          strokeWidth={thin}
          strokeLinejoin="round"
        />
        <circle cx="4" cy="2" r="9" fill={hexA(c3, 0.15)} stroke={hexA(c3, 0.54)} strokeWidth={sw} />
        <rect x="-0.5" y="-4.2" width="7" height="2.4" rx="1.2" fill={hexA(c3, 0.4)} />
        <circle cx="5" cy="4.4" r="2.4" fill="none" stroke={hexA(c3, 0.38)} strokeWidth={thin} />
        <circle cx="2" cy="-10.4" r="3.2" fill="none" stroke={hexA(c3, 0.5)} strokeWidth={thin} />
      </g>

      {/* ---- pennant on staff, right margin ---- */}
      <g transform="translate(254 210)">
        <rect x="-1.1" y="-34" width="2.2" height="68" rx="1.1" fill={hexA(ink, 0.4)} />
        <circle cx="0" cy="-36.6" r="2.3" fill={hexA(ink, 0.42)} />
        <path
          d="M 2 -31 C 16 -28, 28 -25, 38 -21.8 C 28 -18, 16 -15, 2 -12 Z"
          fill="url(#gdm-sports-flagfill)"
          stroke={hexA(c2, 0.56)}
          strokeWidth={sw}
          strokeLinejoin="round"
        />
        <path
          d="M 8 -29.2 L 8 -13.6"
          stroke={hexA(c2, 0.4)}
          strokeWidth={thin}
          strokeLinecap="round"
        />
        <circle cx="16" cy="-21.4" r="3" fill="none" stroke={hexA(c2, 0.42)} strokeWidth={thin} />
      </g>

      {/* ---- trophy, bottom-left ---- */}
      <g transform="translate(46 348)">
        <path
          d="M -11 -14 L 11 -14 C 11 -4, 7 3, 0 3 C -7 3, -11 -4, -11 -14 Z"
          fill="url(#gdm-sports-cupfill)"
          stroke={hexA(ink, 0.56)}
          strokeWidth={sw}
          strokeLinejoin="round"
        />
        <path d="M -11 -10.4 L 11 -10.4" stroke={hexA(ink, 0.4)} strokeWidth={thin} />
        <path
          d="M -11 -12 C -19 -12, -19 -2, -11 -2"
          fill="none"
          stroke={hexA(ink, 0.5)}
          strokeWidth={thin}
          strokeLinecap="round"
        />
        <path
          d="M 11 -12 C 19 -12, 19 -2, 11 -2"
          fill="none"
          stroke={hexA(ink, 0.5)}
          strokeWidth={thin}
          strokeLinecap="round"
        />
        <rect x="-2.4" y="3" width="4.8" height="6" fill={hexA(ink, 0.34)} />
        <rect x="-6.8" y="9" width="13.6" height="3" rx="1" fill={hexA(ink, 0.3)} />
        <rect x="-10" y="12.4" width="20" height="3.6" rx="1.4" fill={hexA(ink, 0.34)} />
        <path d={star} transform="translate(0 -6.2) scale(0.9)" fill={hexA(c3, 0.42)} />
      </g>

      {/* ---- baseball, bottom-right ---- */}
      <g transform="translate(256 350) rotate(12)">
        <circle r="12" fill={hexA(ink, 0.1)} stroke={hexA(ink, 0.5)} strokeWidth={sw} />
        <path
          d="M -7.4 -9.4 C -3 -3.2, -3 3.2, -7.4 9.4"
          fill="none"
          stroke={hexA(c3, 0.46)}
          strokeWidth={thin}
        />
        <path
          d="M 7.4 -9.4 C 3 -3.2, 3 3.2, 7.4 9.4"
          fill="none"
          stroke={hexA(c3, 0.46)}
          strokeWidth={thin}
        />
        {[-4, 4].map((sy, i) => (
          <g key={"gdm-stitch-" + i}>
            <path
              d={"M -7.1 " + (sy - 1.1) + " L -3.7 " + (sy + 0.6)}
              stroke={hexA(c3, 0.44)}
              strokeWidth={thin}
              strokeLinecap="round"
            />
            <path
              d={"M 7.1 " + (sy - 1.1) + " L 3.7 " + (sy + 0.6)}
              stroke={hexA(c3, 0.44)}
              strokeWidth={thin}
              strokeLinecap="round"
            />
          </g>
        ))}
      </g>

      {/* ---- yard lines, bottom edge ---- */}
      <path
        d="M 96 391 L 204 391"
        stroke={hexA(ink, 0.32)}
        strokeWidth={thin}
        strokeLinecap="round"
      />
      {[0, 1, 2, 3].map((i) => {
        const tx = 108 + i * 28;
        return (
          <path
            key={"gdm-yard-" + i}
            d={"M " + tx + " 391 L " + tx + " " + (391 - (i % 2 === 0 ? 7 : 4.5))}
            stroke={hexA(ink, 0.3)}
            strokeWidth={thin}
            strokeLinecap="round"
          />
        );
      })}

      {/* ---- award stars ---- */}
      <path d={star} transform="translate(76 104) scale(0.95)" fill={hexA(c3, 0.4)} />
      <path d={star} transform="translate(228 107) scale(0.75)" fill={hexA(c2, 0.38)} />
      <path d={star} transform="translate(80 328) scale(0.7)" fill={hexA(c2, 0.36)} />
    </svg>
  );
}

// ─── GameNightMotif — pair of dice (five-pip and three-pip, tilted), fanned trio of playing cards with spade / h
function GameNightMotif({ color, accent, accent2, scale = 300 }) {
  const a2 = accent2 || accent;
  const u = Math.max(0.7, Math.min(1.4, scale / 300));
  const sw = 1.5 / u;
  const thin = 1 / u;
  const line = hexA(color, 0.5);
  const soft = hexA(color, 0.34);
  const dot = hexA(a2, 0.52);
  const pipInk = hexA(color, 0.46);
  const wash = "url(#gmnt-tone)";
  const washB = "url(#gmnt-tone-b)";

  const HEART = "M0 4.6C-4.4 1.2 -5 -2.2 -2.8 -3.8C-1.3 -4.9 -0.3 -3.8 0 -2.8C0.3 -3.8 1.3 -4.9 2.8 -3.8C5 -2.2 4.4 1.2 0 4.6Z";
  const SPADE = "M0 -4.8C2.8 -1.8 4.8 -0.8 4.8 1.2C4.8 3 2.8 3.8 1 2.6C1.2 3.6 1.7 4.4 2.4 5L-2.4 5C-1.7 4.4 -1.2 3.6 -1 2.6C-2.8 3.8 -4.8 3 -4.8 1.2C-4.8 -0.8 -2.8 -1.8 0 -4.8Z";
  const DIAMOND = "M0 -5L3.4 0L0 5L-3.4 0Z";
  const CLUB = "M0 5C0.6 3.4 1 2.4 1.1 1.5C2.7 2.9 5 2 5 0C5 -1.8 3.2 -2.9 1.6 -2.2C2.5 -3.7 1.7 -5.3 0 -5.3C-1.7 -5.3 -2.5 -3.7 -1.6 -2.2C-3.2 -2.9 -5 -1.8 -5 0C-5 2 -2.7 2.9 -1.1 1.5C-1 2.4 -0.6 3.4 0 5Z";
  const KERNEL = "M0 -3.4C2.4 -5 4.8 -3 4 -0.6C5.4 1 4 3.8 1.6 3.4C0.2 5 -2.6 4.2 -2.8 2C-5 1.4 -5 -1.6 -3 -2.4C-2.6 -3.6 -1.4 -4 0 -3.4Z";

  const dieAPips = [[0, 0], [-7, -7], [7, -7], [-7, 7], [7, 7]];
  const dieBPips = [[-5.5, -5.5], [0, 0], [5.5, 5.5]];
  const cards = [
    { a: -22, d: SPADE },
    { a: -3, d: HEART },
    { a: 16, d: DIAMOND }
  ];
  const reelHoles = [0, 1, 2, 3, 4];
  const stripes = [-9.6, -2, 5.6];
  const kernels = [[-9, -11, 0.95], [1, -17, 1.05], [10, -10, 0.9], [-2, -23, 0.75]];
  const scatter = [
    { x: 116, y: 26, r: -12, s: 0.9, d: HEART },
    { x: 176, y: 80, r: 14, s: 0.72, d: DIAMOND },
    { x: 26, y: 268, r: -8, s: 0.85, d: SPADE },
    { x: 274, y: 262, r: 10, s: 0.78, d: CLUB },
    { x: 200, y: 326, r: -16, s: 0.8, d: DIAMOND },
    { x: 104, y: 376, r: 8, s: 0.9, d: HEART }
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id="gmnt-tone" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={hexA(accent, 0.22)} />
          <stop offset="1" stopColor={hexA(a2, 0.09)} />
        </linearGradient>
        <linearGradient id="gmnt-tone-b" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={hexA(a2, 0.2)} />
          <stop offset="1" stopColor={hexA(accent, 0.08)} />
        </linearGradient>
      </defs>

      <g opacity="0.92" strokeLinecap="round" strokeLinejoin="round">

        {/* dice pair - top left */}
        <g transform="translate(48 50) rotate(-12)">
          <rect x="-14" y="-14" width="28" height="28" rx="6.5" fill={wash} stroke={line} strokeWidth={sw} />
          {dieAPips.map((p, i) => (
            <circle key={"gmnt-da-" + i} cx={p[0]} cy={p[1]} r="2.3" fill={pipInk} />
          ))}
        </g>
        <g transform="translate(83 78) rotate(16)">
          <rect x="-11" y="-11" width="22" height="22" rx="5.5" fill={washB} stroke={line} strokeWidth={sw} />
          {dieBPips.map((p, i) => (
            <circle key={"gmnt-db-" + i} cx={p[0]} cy={p[1]} r="2" fill={dot} />
          ))}
        </g>

        {/* fanned playing cards - top right */}
        <g transform="translate(244 58)">
          {cards.map((c, i) => (
            <g key={"gmnt-card-" + i} transform={"rotate(" + c.a + ") translate(0 -15)"}>
              <rect x="-11.5" y="-17" width="23" height="34" rx="2.8" fill={i === 1 ? washB : wash} stroke={line} strokeWidth={sw} />
              <path d={c.d} transform="scale(0.85)" fill={i === 1 ? dot : pipInk} />
              <line x1="-8" y1="-13.5" x2="-4.5" y2="-13.5" stroke={soft} strokeWidth={thin} />
            </g>
          ))}
        </g>

        {/* film reel - left margin */}
        <g transform="translate(31 198)">
          <circle r="21" fill={wash} stroke={line} strokeWidth={sw} />
          <circle r="16.5" fill="none" stroke={soft} strokeWidth={thin} />
          <circle r="5.2" fill={dot} />
          {reelHoles.map((i) => {
            const ang = (i * 72 - 90) * (Math.PI / 180);
            return (
              <circle
                key={"gmnt-reel-" + i}
                cx={+(Math.cos(ang) * 11.4).toFixed(2)}
                cy={+(Math.sin(ang) * 11.4).toFixed(2)}
                r="4.4"
                fill="none"
                stroke={soft}
                strokeWidth={thin}
              />
            );
          })}
        </g>

        {/* retro microphone - right margin */}
        <g transform="translate(268 192)">
          <circle cy="-12" r="11" fill={washB} stroke={line} strokeWidth={sw} />
          <line x1="-7" y1="-16.5" x2="7" y2="-16.5" stroke={soft} strokeWidth={thin} />
          <line x1="-9.4" y1="-12" x2="9.4" y2="-12" stroke={soft} strokeWidth={thin} />
          <line x1="-7" y1="-7.5" x2="7" y2="-7.5" stroke={soft} strokeWidth={thin} />
          <rect x="-4" y="-2.5" width="8" height="4.5" rx="1.6" fill={dot} />
          <rect x="-4.6" y="2.5" width="9.2" height="25" rx="4.4" fill={wash} stroke={line} strokeWidth={sw} />
          <rect x="-6.4" y="26.5" width="12.8" height="3.6" rx="1.6" fill={pipInk} />
        </g>

        {/* clapperboard - bottom left */}
        <g transform="translate(54 346) rotate(-7)">
          <rect x="-26" y="-2" width="52" height="28" rx="2.6" fill={wash} stroke={line} strokeWidth={sw} />
          <line x1="-19" y1="8" x2="19" y2="8" stroke={soft} strokeWidth={thin} />
          <line x1="-19" y1="16" x2="12" y2="16" stroke={soft} strokeWidth={thin} />
          <g transform="translate(-26 -3) rotate(-15)">
            <rect x="0" y="-9.5" width="52" height="9.5" rx="2" fill={washB} stroke={line} strokeWidth={sw} />
            <path d="M6 -9.5L12.5 -9.5L8.5 0L2 0Z" fill={pipInk} opacity="0.75" />
            <path d="M20 -9.5L26.5 -9.5L22.5 0L16 0Z" fill={pipInk} opacity="0.75" />
            <path d="M34 -9.5L40.5 -9.5L36.5 0L30 0Z" fill={pipInk} opacity="0.75" />
          </g>
        </g>

        {/* popcorn box - bottom right */}
        <g transform="translate(250 346)">
          {kernels.map((k, i) => (
            <path
              key={"gmnt-pop-" + i}
              d={KERNEL}
              transform={"translate(" + k[0] + " " + k[1] + ") scale(" + k[2] + ") rotate(" + (i * 37 - 40) + ")"}
              fill={hexA(a2, 0.34)}
              stroke={soft}
              strokeWidth={thin}
            />
          ))}
          <path d="M-16 -4L16 -4L11.6 24L-11.6 24Z" fill={wash} stroke={line} strokeWidth={sw} />
          {stripes.map((sx, i) => (
            <path
              key={"gmnt-stripe-" + i}
              d={"M" + sx + " -4L" + (sx + 4) + " -4L" + (sx + 4 - i * 0.6) + " 24L" + (sx + 0.6 - i * 0.6) + " 24Z"}
              fill={hexA(accent, 0.26)}
            />
          ))}
          <rect x="-17.6" y="-7.4" width="35.2" height="5" rx="1.6" fill={washB} stroke={line} strokeWidth={sw} />
        </g>

        {/* game controller - bottom centre */}
        <g transform="translate(150 356)">
          <path
            d="M-26 -10C-14 -14 14 -14 26 -10C33 -7 37 4 33 11C29 17 21 15 17 9C15 6 12 5 8 5L-8 5C-12 5 -15 6 -17 9C-21 15 -29 17 -33 11C-37 4 -33 -7 -26 -10Z"
            fill={wash}
            stroke={line}
            strokeWidth={sw}
          />
          <rect x="-22.5" y="-3.4" width="13" height="4.4" rx="1.2" fill={pipInk} />
          <rect x="-18.2" y="-7.6" width="4.4" height="12.8" rx="1.2" fill={pipInk} />
          <circle cx="14.5" cy="-4.5" r="3" fill={dot} />
          <circle cx="21.5" cy="1.5" r="3" fill={dot} />
          <rect x="-3.5" y="-4.5" width="7" height="2.2" rx="1.1" fill={soft} />
        </g>

        {/* suit pip scatter */}
        {scatter.map((p, i) => (
          <path
            key={"gmnt-pip-" + i}
            d={p.d}
            transform={"translate(" + p.x + " " + p.y + ") rotate(" + p.r + ") scale(" + p.s + ")"}
            fill={i % 2 === 0 ? pipInk : dot}
          />
        ))}
      </g>
    </svg>
  );
}

// ─── LuauMotif — monstera leaf (x3), palm frond (x2), hibiscus blossom (x3), pineapple, flower lei garland,
function LuauMotif({ color, accent, accent2, scale = 300 }) {
  const leaf = color;
  const bloom = accent || color;
  const warm = accent2 || accent || color;

  const FROND = (() => {
    let d = "";
    const n = 13;
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1);
      const base = 26 - 54 * t;
      const L = 6 + 17 * Math.pow(Math.sin(Math.PI * t), 0.7);
      const y = base.toFixed(1);
      const ym = (base + 1.2).toFixed(1);
      const yt = (base - L * 0.55).toFixed(1);
      d += "M0," + y + " Q" + (-L * 0.45).toFixed(1) + "," + ym + " " + (-L * 0.8).toFixed(1) + "," + yt + " ";
      d += "M0," + y + " Q" + (L * 0.45).toFixed(1) + "," + ym + " " + (L * 0.8).toFixed(1) + "," + yt + " ";
    }
    return d;
  })();

  const MON = "M0,15 C-9,15 -16,12 -20,7 L-4,4 L-21,-1 Q-23,-4 -20,-7 L-4,-9 L-18,-14 Q-20,-17 -16,-19 L-3,-19 L-11,-25 Q-12,-27 -9,-28 L0,-32 L9,-28 Q12,-27 11,-25 L3,-19 L16,-19 Q20,-17 18,-14 L4,-9 L20,-7 Q23,-4 21,-1 L4,4 L20,7 C16,12 9,15 0,15 Z";
  const MONV = "M0,26 L0,-30 M0,10 L-15,7 M0,10 L15,7 M0,1 L-16,-3 M0,1 L16,-3 M0,-8 L-14,-15 M0,-8 L14,-15 M0,-16 L-8,-24 M0,-16 L8,-24";
  const PETAL = "M0,-1 C-10,-5 -13,-13 -8,-19 Q-4,-23 0,-19 Q4,-23 8,-19 C13,-13 10,-5 0,-1 Z";
  const PINE = "M0,-3 C-11,-3 -16,7 -16,17 C-16,29 -8,38 0,38 C8,38 16,29 16,17 C16,7 11,-3 0,-3 Z";
  const CROWN = "M0,-3 C-1,-11 -5,-17 -11,-21 C-7,-20 -3,-17 -1,-13 C-3,-21 -2,-28 0,-33 C2,-28 3,-21 1,-13 C3,-17 7,-20 11,-21 C5,-17 1,-11 0,-3 Z";
  const GRID1 = "M-26,-22 L14,18 M-26,-12 L14,28 M-26,-2 L14,38 M-26,8 L14,48 M-26,18 L14,58";
  const GRID2 = "M26,-22 L-14,18 M26,-12 L-14,28 M26,-2 L-14,38 M26,8 L-14,48 M26,18 L-14,58";

  const frond = (k, x, y, rot, sc) => (
    <g key={k} transform={"translate(" + x + " " + y + ") rotate(" + rot + ") scale(" + sc + ")"}>
      <path d={FROND} fill="none" stroke={hexA(leaf, 0.5)} strokeWidth="1.5" strokeLinecap="round" />
      <path d="M0,29 L0,-29" fill="none" stroke={hexA(leaf, 0.5)} strokeWidth="1.3" strokeLinecap="round" />
    </g>
  );

  const monstera = (k, x, y, rot, sc) => (
    <g key={k} transform={"translate(" + x + " " + y + ") rotate(" + rot + ") scale(" + sc + ")"}>
      <path d={MON} fill={hexA(leaf, 0.28)} stroke={hexA(leaf, 0.5)} strokeWidth="1" strokeLinejoin="round" />
      <path d={MONV} fill="none" stroke={hexA(leaf, 0.45)} strokeWidth="0.85" strokeLinecap="round" />
    </g>
  );

  const hibiscus = (k, x, y, sc, rot, full) => (
    <g key={k} transform={"translate(" + x + " " + y + ") rotate(" + rot + ") scale(" + sc + ")"}>
      {[0, 1, 2, 3, 4].map((i) => (
        <path
          key={k + "p" + i}
          d={PETAL}
          transform={"rotate(" + i * 72 + ")"}
          fill={hexA(bloom, 0.34)}
          stroke={hexA(bloom, 0.55)}
          strokeWidth="0.9"
          strokeLinejoin="round"
        />
      ))}
      {full ? <path d="M0,0 L4,-13" fill="none" stroke={hexA(warm, 0.55)} strokeWidth="1.1" strokeLinecap="round" /> : null}
      {full ? <circle cx="4.4" cy="-14.5" r="1.7" fill={hexA(warm, 0.5)} /> : null}
      <circle cx="0" cy="0" r="2.1" fill={hexA(warm, 0.5)} />
    </g>
  );

  const beads = [
    [271, 97], [264, 112], [261, 127], [263, 143], [269, 159], [275, 176],
    [275, 193], [269, 209], [264, 226], [264, 244], [270, 261], [271, 279]
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <clipPath id="luau-pine-clip">
          <path d={PINE} />
        </clipPath>
      </defs>

      {frond("luau-fr-a", 14, 26, 132, 1.05)}
      {monstera("luau-mn-a", 56, 24, 30, 0.92)}
      {hibiscus("luau-hb-a", 26, 84, 0.72, -12, true)}

      {monstera("luau-mn-b", 214, 20, -42, 0.8)}
      <g transform="translate(262 46) rotate(8) scale(0.8)">
        <path d={PINE} fill={hexA(warm, 0.26)} stroke={hexA(warm, 0.5)} strokeWidth="1.1" />
        <g clipPath="url(#luau-pine-clip)">
          <path d={GRID1} fill="none" stroke={hexA(warm, 0.38)} strokeWidth="0.8" />
          <path d={GRID2} fill="none" stroke={hexA(warm, 0.38)} strokeWidth="0.8" />
        </g>
        <path d={CROWN} fill={hexA(leaf, 0.3)} stroke={hexA(leaf, 0.5)} strokeWidth="0.9" strokeLinejoin="round" />
      </g>

      <path
        d="M271,97 Q256,127 264,152 Q280,178 274,200 Q262,226 264,244 Q276,266 271,279"
        fill="none"
        stroke={hexA(leaf, 0.45)}
        strokeWidth="1"
      />
      {beads.map((b, i) => (
        <circle
          key={"luau-bead-" + i}
          cx={b[0]}
          cy={b[1]}
          r={i % 3 === 0 ? 3.5 : 2.6}
          fill={hexA(i % 3 === 0 ? bloom : i % 3 === 1 ? warm : leaf, 0.5)}
        />
      ))}

      <g transform="translate(34 348) rotate(-20) scale(0.72)">
        <path
          d="M0,-1 C-8,-1 -12,4 -12,9 C-12,13 -9,14.5 -9,17 C-9,21 -13,23 -13,28 C-13,34 -7,39 0,39 C7,39 13,34 13,28 C13,23 9,21 9,17 C9,14.5 12,13 12,9 C12,4 8,-1 0,-1 Z"
          fill={hexA(warm, 0.2)}
          stroke={hexA(warm, 0.55)}
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
        <path d="M-3.2,0 L-3.2,-30 L3.2,-30 L3.2,0 Z" fill={hexA(warm, 0.18)} stroke={hexA(warm, 0.55)} strokeWidth="1.1" strokeLinejoin="round" />
        <path d="M-5,-30 L-5,-40 Q0,-43.5 5,-40 L5,-30 Z" fill={hexA(warm, 0.22)} stroke={hexA(warm, 0.55)} strokeWidth="1.1" strokeLinejoin="round" />
        <circle cx="0" cy="22" r="4.6" fill="none" stroke={hexA(warm, 0.5)} strokeWidth="1.1" />
        <path d="M-2.6,-38 L-2.6,34 M-0.9,-38 L-0.9,34 M0.9,-38 L0.9,34 M2.6,-38 L2.6,34" fill="none" stroke={hexA(leaf, 0.4)} strokeWidth="0.55" />
        <path d="M-5.5,32 L5.5,32" stroke={hexA(warm, 0.5)} strokeWidth="1.6" strokeLinecap="round" />
        <path d="M-3.2,-9 L3.2,-9 M-3.2,-17 L3.2,-17 M-3.2,-25 L3.2,-25" fill="none" stroke={hexA(warm, 0.35)} strokeWidth="0.7" />
        <circle cx="-6.6" cy="-36" r="1.5" fill={hexA(warm, 0.45)} />
        <circle cx="6.6" cy="-36" r="1.5" fill={hexA(warm, 0.45)} />
      </g>
      {hibiscus("luau-hb-b", 80, 372, 0.66, 18, true)}
      {monstera("luau-mn-c", 8, 394, -28, 0.7)}

      <g transform="translate(272 354) rotate(5) scale(0.82)">
        <path d="M-15,-27 L-11,-35 L-6,-29 L0,-37 L6,-29 L11,-35 L15,-27 Z" fill={hexA(leaf, 0.3)} stroke={hexA(leaf, 0.5)} strokeWidth="1" strokeLinejoin="round" />
        <path d="M-13,-27 L13,-27 L10,10 Q9,27 0,30 Q-9,27 -10,10 Z" fill={hexA(leaf, 0.26)} stroke={hexA(leaf, 0.55)} strokeWidth="1.1" strokeLinejoin="round" />
        <path d="M-10,-16 L0,-10 L10,-16" fill="none" stroke={hexA(warm, 0.5)} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
        <path d="M-9.5,-13 L-3,-10 L-8.5,-6 Z M9.5,-13 L3,-10 L8.5,-6 Z" fill={hexA(warm, 0.38)} stroke={hexA(warm, 0.5)} strokeWidth="0.8" strokeLinejoin="round" />
        <path d="M0,-7 L-4,4 L4,4 Z" fill="none" stroke={hexA(warm, 0.5)} strokeWidth="1" strokeLinejoin="round" />
        <path d="M-9,9 L9,9 L6.5,18 L-6.5,18 Z" fill="none" stroke={hexA(warm, 0.5)} strokeWidth="1.1" strokeLinejoin="round" />
        <path d="M-4.5,9 L-4.5,18 M0,9 L0,18 M4.5,9 L4.5,18 M-7,23 L7,23" fill="none" stroke={hexA(warm, 0.38)} strokeWidth="0.7" />
      </g>
      {frond("luau-fr-b", 232, 390, 205, 0.95)}
      {hibiscus("luau-hb-c", 248, 318, 0.5, -24, false)}

      <circle cx="44" cy="110" r="1.8" fill={hexA(bloom, 0.4)} />
      <circle cx="18" cy="308" r="2.1" fill={hexA(bloom, 0.35)} />
      <circle cx="252" cy="302" r="1.7" fill={hexA(warm, 0.4)} />
    </svg>
  );
}

// ─── PetsMotif — paw print (top edge), paw print (left margin), paw print (right margin), paw print (bottom
function PetsMotif({ color, accent, accent2, scale = 300 }) {
  const k = Math.max(0.85, Math.min(1.2, scale / 300));

  const pawPad =
    "M0,-4.9 C4.9,-4.9 7,-2 6.4,1.3 C5.8,4.6 3,6 0,6 C-3,6 -5.8,4.6 -6.4,1.3 C-7,-2 -4.9,-4.9 0,-4.9 Z";

  const toes = [
    { id: "t1", x: -6.5, y: -6, rx: 2.15, ry: 2.75, r: -34 },
    { id: "t2", x: -2.4, y: -9.4, rx: 2.2, ry: 2.95, r: -11 },
    { id: "t3", x: 2.4, y: -9.4, rx: 2.2, ry: 2.95, r: 11 },
    { id: "t4", x: 6.5, y: -6, rx: 2.15, ry: 2.75, r: 34 }
  ];

  const paws = [
    { id: "paw-top", x: 100, y: 24, s: 0.8, r: -12, o: 0.5 },
    { id: "paw-left", x: 24, y: 176, s: 0.58, r: -22, o: 0.36 },
    { id: "paw-right", x: 278, y: 236, s: 0.62, r: 22, o: 0.38 },
    { id: "paw-bottom", x: 108, y: 372, s: 0.78, r: 9, o: 0.46 }
  ];

  const collarCurve = "M -23,-7.5 C -14,6.5 14,6.5 23,-7.5";

  const tagPaw = [
    { id: "tp1", x: -1.6, y: 12.7, r: 0.5 },
    { id: "tp2", x: 0, y: 12.2, r: 0.55 },
    { id: "tp3", x: 1.6, y: 12.7, r: 0.5 }
  ];

  const kibble = [
    { id: "kb1", x: -4.2, y: -6.2, r: 1.15 },
    { id: "kb2", x: 3.6, y: -6.6, r: 1 }
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden="true"
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id="petsm-bowl-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={hexA(color, 0.95)} />
          <stop offset="100%" stopColor={hexA(color, 0.5)} />
        </linearGradient>
      </defs>

      {/* --- collar with hanging tag : top left --- */}
      <g
        transform={"translate(44 38) rotate(-6) scale(" + k * 1.05 + ")"}
        opacity={0.55}
      >
        <path
          d={collarCurve}
          fill="none"
          stroke={hexA(color, 0.95)}
          strokeWidth={5}
          strokeLinecap="round"
        />
        <path
          d={collarCurve}
          fill="none"
          stroke={hexA(accent, 0.75)}
          strokeWidth={0.7}
          strokeDasharray="2 2.6"
          strokeLinecap="round"
        />
        <g transform="translate(-13.3 0.4) rotate(24)">
          <rect
            x={-3.6}
            y={-3.6}
            width={7.2}
            height={7.2}
            rx={1.7}
            fill="none"
            stroke={hexA(color, 0.95)}
            strokeWidth={1.3}
          />
          <path
            d="M 0,-3.6 L 0,3.6"
            stroke={hexA(color, 0.95)}
            strokeWidth={1.1}
            strokeLinecap="round"
          />
        </g>
        <circle
          cx={0}
          cy={7.2}
          r={2.4}
          fill="none"
          stroke={hexA(color, 0.9)}
          strokeWidth={1.2}
        />
        <circle
          cx={0}
          cy={13.8}
          r={4.2}
          fill={hexA(accent, 0.45)}
          stroke={hexA(color, 0.9)}
          strokeWidth={1}
        />
        <ellipse cx={0} cy={14.9} rx={1.5} ry={1.2} fill={hexA(color, 0.85)} />
        {tagPaw.map((d) => (
          <circle key={d.id} cx={d.x} cy={d.y} r={d.r} fill={hexA(color, 0.85)} />
        ))}
      </g>

      {/* --- tennis ball : top right --- */}
      <g transform={"translate(204 62) scale(" + k + ")"}>
        <circle r={9.4} fill={hexA(color, 0.42)} />
        <path
          d="M -8.7,-4.5 C -3.4,-1.5 -3.4,1.5 -8.7,4.5"
          fill="none"
          stroke={hexA(accent, 0.6)}
          strokeWidth={1.4}
          strokeLinecap="round"
        />
        <path
          d="M 8.7,-4.5 C 3.4,-1.5 3.4,1.5 8.7,4.5"
          fill="none"
          stroke={hexA(accent, 0.6)}
          strokeWidth={1.4}
          strokeLinecap="round"
        />
      </g>

      {/* --- bone : top right corner --- */}
      <g
        transform={"translate(250 44) rotate(-20) scale(" + k + ")"}
        opacity={0.52}
        fill={color}
      >
        <rect x={-10.4} y={-2.8} width={20.8} height={5.6} rx={2.7} />
        <circle cx={-10.4} cy={-3} r={3.7} />
        <circle cx={-10.4} cy={3} r={3.7} />
        <circle cx={10.4} cy={-3} r={3.7} />
        <circle cx={10.4} cy={3} r={3.7} />
        <path
          d="M -6.4,-0.8 L 6.4,-0.8"
          fill="none"
          stroke={hexA(accent2, 0.55)}
          strokeWidth={0.9}
          strokeLinecap="round"
        />
      </g>

      {/* --- food bowl : bottom left --- */}
      <g
        transform={"translate(42 352) scale(" + k * 1.2 + ")"}
        opacity={0.55}
      >
        <path
          d="M -13.2,-3.2 L -8.2,7.4 Q 0,9.7 8.2,7.4 L 13.2,-3.2 Z"
          fill="url(#petsm-bowl-grad)"
        />
        <path
          d="M -11.9,1.1 L 11.9,1.1"
          fill="none"
          stroke={hexA(accent, 0.6)}
          strokeWidth={1.1}
          strokeLinecap="round"
        />
        <path
          d="M -9.4,-4.6 C -5,-8.6 5,-8.6 9.4,-4.6"
          fill={hexA(accent2, 0.5)}
        />
        {kibble.map((d) => (
          <circle
            key={d.id}
            cx={d.x}
            cy={d.y}
            r={d.r}
            fill={hexA(color, 0.55)}
          />
        ))}
        <ellipse
          cx={0}
          cy={-3.2}
          rx={13.2}
          ry={3.3}
          fill="none"
          stroke={hexA(color, 0.95)}
          strokeWidth={1.4}
        />
      </g>

      {/* --- fish : bottom right --- */}
      <g
        transform={"translate(240 366) rotate(-8) scale(" + k * 1.05 + ")"}
        opacity={0.5}
      >
        <path
          d="M -9.5,0 L -18.5,-6.6 C -15.4,-2.2 -15.4,2.2 -18.5,6.6 Z"
          fill={hexA(color, 0.8)}
        />
        <path d="M -2.6,-6.2 L 1.6,-11 L 5.4,-7.3 Z" fill={hexA(color, 0.9)} />
        <path d="M -1,6 L 0.4,9.6 L 3.6,6.5 Z" fill={hexA(color, 0.7)} />
        <path
          d="M -9.5,0 C -5.5,-6.6 5,-7.2 12,0 C 5,7.2 -5.5,6.6 -9.5,0 Z"
          fill={color}
        />
        <path
          d="M 5.2,-4.5 C 3.4,-1.6 3.4,1.6 5.2,4.5"
          fill="none"
          stroke={hexA(accent, 0.7)}
          strokeWidth={1}
          strokeLinecap="round"
        />
        <circle cx={8.3} cy={-1.4} r={1.15} fill={hexA(accent2, 0.85)} />
      </g>

      {/* --- paw prints : edge scatter --- */}
      {paws.map((p) => (
        <g
          key={p.id}
          transform={
            "translate(" +
            p.x +
            " " +
            p.y +
            ") rotate(" +
            p.r +
            ") scale(" +
            p.s * k +
            ")"
          }
          opacity={p.o}
          fill={color}
        >
          <path d={pawPad} />
          {toes.map((t) => (
            <ellipse
              key={p.id + "-" + t.id}
              cx={t.x}
              cy={t.y}
              rx={t.rx}
              ry={t.ry}
              transform={"rotate(" + t.r + " " + t.x + " " + t.y + ")"}
            />
          ))}
        </g>
      ))}
    </svg>
  );
}

// ─── MemorialGraceMotif — dove in flight carrying an olive sprig (top-left), olive branch with olives (top-right), g
function MemorialGraceMotif({ color, accent, accent2, scale = 300 }) {
  const u = Math.max(0.7, Math.min(1.5, scale / 300));
  const ink = (a) => hexA(color, a);
  const warm = (a) => hexA(accent, a);
  const sage = (a) => hexA(accent2, a);

  const LEAF = "M0 0C4.2 -3.4 8.8 -2.4 11 0C8.8 2.4 4.2 3.4 0 0Z";

  const branchLeaves = [
    [287, 19, -158],
    [279, 24, 26],
    [270, 29, -152],
    [261, 35, 32],
    [251, 42, -146],
    [241, 48, 36],
    [232, 54, -140],
  ];

  const sprigLeaves = [
    [116, 379, -150],
    [128, 374, 24],
    [140, 371, -152],
    [152, 369, 22],
    [164, 368, -154],
    [176, 368, 20],
  ];

  const driftPetals = [
    [22, 156, 24, 0.8],
    [32, 213, -38, 0.66],
    [17, 268, 14, 0.74],
    [280, 171, -26, 0.72],
    [267, 231, 34, 0.82],
    [283, 287, -12, 0.64],
  ];

  const stamens = [-54, -18, 18, 54];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <radialGradient id="mmrl-grace-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={sage(0.2)} />
          <stop offset="100%" stopColor={sage(0)} />
        </radialGradient>
        <radialGradient id="mmrl-grace-halo" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={warm(0.3)} />
          <stop offset="55%" stopColor={warm(0.11)} />
          <stop offset="100%" stopColor={warm(0)} />
        </radialGradient>
      </defs>

      {/* gentle rays breaking through, top centre */}
      <ellipse cx={150} cy={-12} rx={92} ry={62} fill="url(#mmrl-grace-glow)" />
      {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => {
        const a = ((i - 4) * 9 * Math.PI) / 180;
        const L = i % 2 === 0 ? 112 : 94;
        return (
          <line
            key={`mmrl-ray-${i}`}
            x1={150}
            y1={-46}
            x2={150 + Math.sin(a) * L}
            y2={-46 + Math.cos(a) * L}
            stroke={sage(i % 2 === 0 ? 0.3 : 0.17)}
            strokeWidth={1.1 * u}
            strokeLinecap="round"
          />
        );
      })}

      {/* dove in flight with an olive sprig — top left */}
      <g fill="none" stroke={ink(0.46)} strokeWidth={1.15 * u} strokeLinecap="round" strokeLinejoin="round">
        <path
          d="M71 42.5C66 36 57.5 35.2 50.5 39C42.5 43.4 34.5 49 27.5 56C25 58.5 25.6 61.4 29 61.8C41 63.2 53 58.4 61 50.6C66 45.8 69.6 43.6 71 42.5Z"
          fill={ink(0.1)}
        />
        <path d="M71 42.5L76.4 44.1L70.8 45.6" fill={ink(0.16)} />
        <path
          d="M47 45.5C44.5 37.5 47 28.5 53.5 23.5C51.5 32 52.5 40 57 45.5C53.5 48 50 48 47 45.5Z"
          fill={ink(0.07)}
        />
        <path d="M50 44.6C49 37.6 50.4 30.6 53.4 25.6" strokeWidth={0.7 * u} stroke={ink(0.34)} />
        <path d="M53.2 45.4C52.6 39.2 53.6 33.4 55.4 29" strokeWidth={0.7 * u} stroke={ink(0.28)} />
        <path d="M29.4 60.6L14 65.4" />
        <path d="M28.6 58.2L12.6 60.4" />
        <path d="M28.8 55.4L15 54.2" />
        <path d="M76.4 44.4C81 45.6 85.4 46.6 89 46.2" strokeWidth={0.8 * u} stroke={sage(0.42)} />
      </g>
      <circle cx={65.4} cy={41.4} r={0.9} fill={ink(0.5)} />
      <path d={LEAF} transform="translate(80 45.4) rotate(-155) scale(0.6)" fill={sage(0.34)} />
      <path d={LEAF} transform="translate(84.4 46.4) rotate(34) scale(0.6)" fill={sage(0.34)} />
      <circle cx={89.2} cy={45.8} r={1.7} fill={warm(0.32)} />

      {/* olive branch — top right */}
      <path
        d="M293 16C275 22 252 34 224 56"
        fill="none"
        stroke={ink(0.4)}
        strokeWidth={1 * u}
        strokeLinecap="round"
      />
      {branchLeaves.map((l, i) => (
        <g key={`mmrl-bl-${i}`} transform={`translate(${l[0]} ${l[1]}) rotate(${l[2]})`}>
          <path d={LEAF} fill={sage(i % 2 === 0 ? 0.3 : 0.22)} />
          <line x1={1.4} y1={0} x2={9.4} y2={0} stroke={ink(0.24)} strokeWidth={0.55 * u} />
        </g>
      ))}
      <circle cx={274} cy={31} r={2.3} fill={warm(0.3)} />
      <circle cx={255} cy={42} r={2} fill={warm(0.24)} />
      <circle cx={238} cy={53} r={2.2} fill={warm(0.28)} />

      {/* lit remembrance candle — bottom left */}
      <circle cx={35.5} cy={315} r={21} fill="url(#mmrl-grace-halo)" />
      <path
        d="M27 400V331C27 328.5 29 327 31.5 327H39.5C42 327 44 328.5 44 331V400"
        fill={ink(0.09)}
        stroke={ink(0.44)}
        strokeWidth={1.15 * u}
        strokeLinejoin="round"
      />
      <path
        d="M27.4 336C29.6 339.5 29.4 343.5 27.6 347"
        fill="none"
        stroke={ink(0.3)}
        strokeWidth={0.8 * u}
        strokeLinecap="round"
      />
      <line x1={35.5} y1={327} x2={35.5} y2={321.5} stroke={ink(0.46)} strokeWidth={1 * u} strokeLinecap="round" />
      <path
        d="M35.5 300C41 307 42.6 313 41.4 317.6C40.3 321.8 38.2 324 35.5 324C32.8 324 30.7 321.8 29.6 317.6C28.4 313 30 307 35.5 300Z"
        fill={warm(0.2)}
        stroke={warm(0.5)}
        strokeWidth={0.9 * u}
        strokeLinejoin="round"
      />
      <path
        d="M35.5 310C38 313.6 38.6 316.6 38 318.8C37.4 321 36.6 322 35.5 322C34.4 322 33.6 321 33 318.8C32.4 316.6 33 313.6 35.5 310Z"
        fill={warm(0.34)}
      />

      {/* single lily — bottom right */}
      <path
        d="M272 400C269 382 268 364 266 356"
        fill="none"
        stroke={sage(0.42)}
        strokeWidth={1.15 * u}
        strokeLinecap="round"
      />
      <path d={LEAF} transform="translate(267 375) rotate(32) scale(1.9 1.5)" fill={sage(0.24)} />
      <path
        d="M265 368C270 366 275 363 278.5 359.5"
        fill="none"
        stroke={sage(0.36)}
        strokeWidth={0.85 * u}
        strokeLinecap="round"
      />
      <path
        d="M278.5 360C282 355 283 349 280 344.5C276.8 349 276 355 278.5 360Z"
        fill={ink(0.12)}
        stroke={ink(0.4)}
        strokeWidth={0.85 * u}
        strokeLinejoin="round"
      />
      <g transform="translate(266 338)">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <path
            key={`mmrl-pt-${i}`}
            d="M0 -2C4.2 -8 5.6 -16 0 -24C-5.6 -16 -4.2 -8 0 -2Z"
            transform={`rotate(${i * 60}) scale(1 ${i % 2 === 0 ? 1 : 0.86})`}
            fill={ink(i % 2 === 0 ? 0.13 : 0.09)}
            stroke={ink(0.44)}
            strokeWidth={1 * u}
            strokeLinejoin="round"
          />
        ))}
        {stamens.map((a, i) => (
          <g key={`mmrl-st-${i}`} transform={`rotate(${a})`}>
            <line x1={0} y1={-2} x2={0} y2={-11} stroke={warm(0.42)} strokeWidth={0.7 * u} strokeLinecap="round" />
            <ellipse cx={0} cy={-12.6} rx={1.7} ry={1} fill={warm(0.44)} />
          </g>
        ))}
        <circle cx={0} cy={0} r={1.6} fill={warm(0.34)} />
      </g>

      {/* low olive sprig along the bottom edge */}
      <path
        d="M108 384C130 376 156 370 188 367"
        fill="none"
        stroke={ink(0.34)}
        strokeWidth={0.95 * u}
        strokeLinecap="round"
      />
      {sprigLeaves.map((l, i) => (
        <path
          key={`mmrl-sp-${i}`}
          d={LEAF}
          transform={`translate(${l[0]} ${l[1]}) rotate(${l[2]}) scale(0.9)`}
          fill={sage(i % 2 === 0 ? 0.28 : 0.2)}
        />
      ))}

      {/* drifting petals in the side margins */}
      {driftPetals.map((p, i) => (
        <path
          key={`mmrl-dp-${i}`}
          d={LEAF}
          transform={`translate(${p[0]} ${p[1]}) rotate(${p[2]}) scale(${p[3]} ${p[3] * 0.8})`}
          fill={i % 2 === 0 ? sage(0.26) : ink(0.2)}
        />
      ))}
    </svg>
  );
}

// ─── DiwaliMotif — diwali
function DiwaliMotif({ color, accent, accent2, scale = 300 }) {
  const flame = '#FFB84D'; // spot warm lamplight, only via hexA
  const r2 = (n) => Math.round(n * 10) / 10;

  // ---- Marigold pom-pom ----
  const marigold = (cx, cy, rad, k) => {
    const petals = [];
    const n = 11;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      petals.push(
        <circle key={`p${i}`} cx={r2(cx + Math.cos(a) * rad)} cy={r2(cy + Math.sin(a) * rad)} r={r2(rad * 0.42)} fill={hexA(accent, 0.5)} />
      );
    }
    return (
      <g key={k}>
        {petals}
        <circle cx={cx} cy={cy} r={r2(rad * 0.58)} fill={hexA(accent, 0.42)} />
        <circle cx={cx} cy={cy} r={r2(rad * 0.32)} fill={hexA(accent2, 0.55)} />
      </g>
    );
  };

  const leaf = (x, y, ang, k) => (
    <path key={k} d="M0 0 C 3 -1 3 -6 0 -8 C -3 -6 -3 -1 0 0 Z" transform={`translate(${x} ${r2(y)}) rotate(${ang})`} fill={hexA(accent2, 0.4)} />
  );

  const spark = (x, y, s, k) => (
    <path key={k} d={`M ${x} ${r2(y - s)} Q ${x} ${y} ${r2(x + s)} ${y} Q ${x} ${y} ${x} ${r2(y + s)} Q ${x} ${y} ${r2(x - s)} ${y} Q ${x} ${y} ${x} ${r2(y - s)} Z`} fill={hexA(accent, 0.5)} />
  );

  // ---- Lit diya ----
  const diya = (cx, by, w, k) => {
    const bodyD = `M ${r2(cx - w)} ${by} C ${r2(cx - w)} ${r2(by + w * 0.9)} ${r2(cx + w)} ${r2(by + w * 0.9)} ${r2(cx + w)} ${by}`;
    const rimD = `M ${r2(cx - w)} ${by} Q ${cx} ${r2(by - 3.5)} ${r2(cx + w)} ${by} Q ${cx} ${r2(by + 3.5)} ${r2(cx - w)} ${by} Z`;
    const footD = `M ${r2(cx - w * 0.4)} ${r2(by + w * 0.9)} L ${r2(cx - w * 0.24)} ${r2(by + w * 0.9 + 3)} L ${r2(cx + w * 0.24)} ${r2(by + w * 0.9 + 3)} L ${r2(cx + w * 0.4)} ${r2(by + w * 0.9)} Z`;
    const flD = `M ${cx} ${r2(by - 4)} C ${r2(cx - 4.5)} ${r2(by - 9)} ${r2(cx - 3)} ${r2(by - 16)} ${cx} ${r2(by - 21)} C ${r2(cx + 3)} ${r2(by - 16)} ${r2(cx + 4.5)} ${r2(by - 9)} ${cx} ${r2(by - 4)} Z`;
    const coreD = `M ${cx} ${r2(by - 6)} C ${r2(cx - 2.2)} ${r2(by - 10)} ${r2(cx - 1.4)} ${r2(by - 14)} ${cx} ${r2(by - 17)} C ${r2(cx + 1.4)} ${r2(by - 14)} ${r2(cx + 2.2)} ${r2(by - 10)} ${cx} ${r2(by - 6)} Z`;
    return (
      <g key={k}>
        <ellipse cx={cx} cy={r2(by - 11)} rx={r2(w * 0.75)} ry={r2(w * 1.05)} fill="url(#dw_glow)" />
        <path d={footD} fill={hexA(color, 0.45)} />
        <path d={bodyD} fill={hexA(color, 0.5)} stroke={hexA(color, 0.55)} strokeWidth="1" />
        <path d={rimD} fill={hexA(color, 0.6)} stroke={hexA(color, 0.5)} strokeWidth="0.8" />
        <path d={`M ${r2(cx - w * 0.55)} ${r2(by + w * 0.42)} Q ${cx} ${r2(by + w * 0.58)} ${r2(cx + w * 0.55)} ${r2(by + w * 0.42)}`} fill="none" stroke={hexA(color, 0.4)} strokeWidth="0.7" />
        <line x1={cx} y1={r2(by - 3)} x2={cx} y2={r2(by - 6)} stroke={hexA(color, 0.5)} strokeWidth="1" />
        <path d={flD} fill={hexA(flame, 0.6)} stroke={hexA(color, 0.38)} strokeWidth="0.7" />
        <path d={coreD} fill={hexA(accent2, 0.55)} />
      </g>
    );
  };

  // ---- Rangoli medallion (top-left) ----
  const rcx = 26, rcy = 42, R = 32;
  const rangoli = [];
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    const ca = Math.cos(a), sa = Math.sin(a);
    const ix = rcx + ca * 13, iy = rcy + sa * 13;
    const ox = rcx + ca * R, oy = rcy + sa * R;
    const px = -sa, py = ca;
    const mx = rcx + ca * 22, my = rcy + sa * 22;
    const d = `M ${r2(ix)} ${r2(iy)} Q ${r2(mx + px * 5.5)} ${r2(my + py * 5.5)} ${r2(ox)} ${r2(oy)} Q ${r2(mx - px * 5.5)} ${r2(my - py * 5.5)} ${r2(ix)} ${r2(iy)} Z`;
    rangoli.push(<path key={`rp${i}`} d={d} fill={hexA(accent, 0.16)} stroke={hexA(accent, 0.5)} strokeWidth="0.8" />);
    const da = ((i + 0.5) / 12) * Math.PI * 2;
    rangoli.push(<circle key={`rd${i}`} cx={r2(rcx + Math.cos(da) * 34.5)} cy={r2(rcy + Math.sin(da) * 34.5)} r="1.4" fill={hexA(accent, 0.45)} />);
    const ia = (i / 8) * Math.PI * 2;
    if (i < 8) rangoli.push(<circle key={`ri${i}`} cx={r2(rcx + Math.cos(ia) * 8)} cy={r2(rcy + Math.sin(ia) * 8)} r="1.5" fill={hexA(accent2, 0.5)} />);
  }
  rangoli.push(<circle key="rc1" cx={rcx} cy={rcy} r="13" fill="none" stroke={hexA(color, 0.4)} strokeWidth="0.8" />);
  rangoli.push(<circle key="rc2" cx={rcx} cy={rcy} r="3" fill={hexA(accent, 0.55)} />);

  // ---- Firework burst (top-right) ----
  const fcx = 272, fcy = 42;
  const firework = [];
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    const ca = Math.cos(a), sa = Math.sin(a);
    firework.push(<line key={`fr${i}`} x1={r2(fcx + ca * 5)} y1={r2(fcy + sa * 5)} x2={r2(fcx + ca * 20)} y2={r2(fcy + sa * 20)} stroke={hexA(accent, 0.5)} strokeWidth="1" />);
    firework.push(<circle key={`ft${i}`} cx={r2(fcx + ca * 21.5)} cy={r2(fcy + sa * 21.5)} r="1.3" fill={hexA(accent2, 0.55)} />);
  }

  // ---- Marigold garland (toran) across top ----
  const sy = (x) => 6 + 22 * Math.abs(Math.sin(Math.PI * x / 150));
  let stringD = '';
  for (let x = 0; x <= 300; x += 10) stringD += (x === 0 ? 'M ' : 'L ') + r2(x) + ' ' + r2(sy(x)) + ' ';
  const gx = [15, 40, 75, 110, 150, 190, 225, 260, 285];

  // ---- Paisley (boteh) ----
  const paisley = (tx, ty, s, rot, mir, k) => (
    <g key={k} transform={`translate(${tx} ${ty}) scale(${mir * s} ${s}) rotate(${rot})`}>
      <path d="M0 0 C 10 0 14 -10 10 -18 C 7 -24 1 -27 -4 -24 C -11 -20 -12 -9 -6 -4 C -3 -1 -1 -1 0 0 Z" fill={hexA(accent, 0.2)} stroke={hexA(color, 0.45)} strokeWidth="1.1" />
      <path d="M-2 -6 C 3 -6 5 -12 2 -17 C 0 -20 -3 -21 -5 -19" fill="none" stroke={hexA(accent2, 0.42)} strokeWidth="0.9" />
      <circle cx="-3" cy="-9" r="1.1" fill={hexA(accent, 0.5)} />
      <circle cx="-1" cy="-13" r="1" fill={hexA(accent, 0.45)} />
    </g>
  );

  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden viewBox="0 0 300 400" preserveAspectRatio="xMidYMid slice">
      <defs>
        <radialGradient id="dw_glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={hexA(flame, 0.35)} />
          <stop offset="100%" stopColor={hexA(flame, 0)} />
        </radialGradient>
      </defs>

      {/* rangoli medallion — top-left */}
      <g>{rangoli}</g>

      {/* firework burst — top-right */}
      <g>{firework}{spark(fcx, fcy, 4.5, 'fspark')}</g>

      {/* garland toran across the top */}
      <path d={stringD} fill="none" stroke={hexA(color, 0.4)} strokeWidth="1.1" />
      {gx.map((x, i) => {
        const y0 = sy(x);
        const stem = 8 + (i % 3) * 3;
        const fr = 5 + (i % 3) * 0.6;
        const fy = y0 + stem + fr;
        return (
          <g key={`mg${i}`}>
            <line x1={x} y1={r2(y0)} x2={x} y2={r2(y0 + stem)} stroke={hexA(color, 0.32)} strokeWidth="1" />
            {i % 2 === 1 && leaf(x - 3, y0 + stem * 0.5, -40, `lfa${i}`)}
            {i % 2 === 1 && leaf(x + 3, y0 + stem * 0.5, 40, `lfb${i}`)}
            {marigold(x, r2(fy), fr, `f${i}`)}
          </g>
        );
      })}

      {/* paisleys flanking the base */}
      {paisley(30, 360, 0.9, -12, 1, 'ps1')}
      {paisley(270, 360, 0.9, -12, -1, 'ps2')}

      {/* scattered sparkles */}
      {spark(150, 54, 3.2, 's1')}
      {spark(52, 100, 2.6, 's2')}
      {spark(248, 100, 2.6, 's3')}
      {spark(42, 176, 2.4, 's4')}
      {spark(258, 176, 2.4, 's5')}

      {/* lit diyas — hero, bottom */}
      {diya(96, 374, 11, 'd2')}
      {diya(204, 374, 11, 'd3')}
      {diya(150, 366, 15, 'd1')}
    </svg>
  );
}

// ─── EidMotif — eid
function EidMotif({ color, accent, accent2, scale = 300 }) {
  const k = scale / 300;
  const spark = (x, y, r) =>
    `M${x},${y - r} L${x + r * 0.28},${y - r * 0.28} L${x + r},${y} L${x + r * 0.28},${y + r * 0.28} L${x},${y + r} L${x - r * 0.28},${y + r * 0.28} L${x - r},${y} L${x - r * 0.28},${y - r * 0.28} Z`;
  const stars = [
    { x: 66, y: 20, r: 3.2 },
    { x: 128, y: 16, r: 2.4 },
    { x: 174, y: 26, r: 3.4 },
    { x: 232, y: 14, r: 2.6 },
    { x: 150, y: 50, r: 2.2 },
    { x: 30, y: 104, r: 2.8 },
    { x: 270, y: 100, r: 2.6 },
    { x: 38, y: 318, r: 2.4 },
    { x: 262, y: 322, r: 3.0 }
  ];
  const cols = [hexA(accent, 0.55), hexA(color, 0.4), hexA(accent2, 0.5)];
  const leaves = [
    "M13.25,386.28 Q11.25,381.28 6.25,379.28",
    "M13.25,386.28 Q17.25,382.28 21.25,382.28",
    "M16.99,380.51 Q14.99,375.51 9.99,373.51",
    "M16.99,380.51 Q20.99,376.51 24.99,376.51",
    "M21.23,374.70 Q19.23,369.70 14.23,367.70",
    "M21.23,374.70 Q25.23,370.70 29.23,370.70",
    "M25.96,368.84 Q23.96,363.84 18.96,361.84",
    "M25.96,368.84 Q29.96,364.84 33.96,364.84",
    "M31.19,362.94 Q29.19,357.94 24.19,355.94",
    "M31.19,362.94 Q35.19,358.94 39.19,358.94",
    "M41,353 Q39.5,349 35.5,347.5",
    "M41,353 Q44,351 47,351"
  ];
  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <radialGradient id="eidm_glow" cx="50%" cy="42%" r="60%">
          <stop offset="0%" stopColor={hexA(accent, 0.3)} />
          <stop offset="100%" stopColor={hexA(accent, 0)} />
        </radialGradient>
      </defs>

      {/* crescent moon + star (top-right hero) */}
      <g stroke={hexA(color, 0.5)} strokeWidth={1.15 * k} fill="none" strokeLinejoin="round" strokeLinecap="round">
        <ellipse cx="262" cy="40" rx="24" ry="24" fill="url(#eidm_glow)" stroke="none" />
        <path d="M254.7,23.2 A19,19 0 1 0 266.1,54.7 A17,17 0 0 1 254.7,23.2 Z" fill={hexA(accent, 0.15)} stroke={hexA(accent, 0.6)} />
        <path d="M273,33 L274.5,36.9 L278.7,37.2 L275.5,39.8 L276.5,43.9 L273,41.6 L269.5,43.9 L270.5,39.8 L267.3,37.2 L271.5,36.9 Z" fill={hexA(accent, 0.62)} stroke="none" />
      </g>

      {/* hanging fanoos lantern (top-left) */}
      <g stroke={hexA(color, 0.5)} strokeWidth={1.15 * k} fill="none" strokeLinejoin="round" strokeLinecap="round">
        <ellipse cx="28" cy="40" rx="17" ry="24" fill="url(#eidm_glow)" stroke="none" />
        <circle cx="28" cy="6" r="2.6" />
        <line x1="28" y1="8.6" x2="28" y2="17" />
        <path d="M22,24 L34,24 L31,18 L25,18 Z" fill={hexA(accent, 0.12)} />
        <path d="M22,24 L34,24 L38,32 L38,46 L32,53 L24,53 L18,46 L18,32 Z" fill="url(#eidm_glow)" />
        <line x1="18" y1="32" x2="38" y2="32" stroke={hexA(color, 0.35)} />
        <line x1="18" y1="46" x2="38" y2="46" stroke={hexA(color, 0.35)} />
        <path d="M24,48 L24,37 Q24,32 28,31 Q32,32 32,37 L32,48 Z" stroke={hexA(accent, 0.5)} />
        <path d="M24,53 L32,53 L28,60 Z" fill={hexA(accent, 0.12)} />
        <circle cx="28" cy="60.5" r="1.3" fill={hexA(accent, 0.4)} stroke="none" />
      </g>

      {/* mosque dome silhouette (bottom-center) */}
      <g stroke={hexA(color, 0.5)} strokeWidth={1.15 * k} fill={hexA(color, 0.1)} strokeLinejoin="round" strokeLinecap="round">
        <path d="M139,372 C136,363 141,357 150,354 C159,357 164,363 161,372 Z" />
        <path d="M140,372 L160,372 L160,378 L140,378 Z" />
        <path d="M128,390 L128,378 L172,378 L172,390 Z" />
        <path d="M120,390 L120,366 L124,366 L124,390 Z" />
        <path d="M119,366 C118,362 120,360 122,359 C124,360 126,362 125,366 Z" />
        <path d="M176,390 L176,366 L180,366 L180,390 Z" />
        <path d="M175,366 C174,362 176,360 178,359 C180,360 182,362 181,366 Z" />
        <line x1="120" y1="376" x2="124" y2="376" />
        <line x1="176" y1="376" x2="180" y2="376" />
        <path d="M146,390 L146,383 Q146,379 150,378.5 Q154,379 154,383 L154,390 Z" fill="none" stroke={hexA(accent, 0.45)} />
        <line x1="150" y1="354" x2="150" y2="347" />
        <circle cx="150" cy="346" r="1.5" fill={hexA(accent, 0.5)} stroke={hexA(accent, 0.6)} />
        <path d="M150.05,339.4 A3.6,3.6 0 1 0 152.91,345.12 A3.2,3.2 0 0 1 150.05,339.4 Z" fill={hexA(accent, 0.5)} stroke={hexA(accent, 0.6)} />
        <line x1="122" y1="359" x2="122" y2="356" />
        <circle cx="122" cy="355" r="1.2" fill={hexA(accent, 0.5)} stroke="none" />
        <line x1="178" y1="359" x2="178" y2="356" />
        <circle cx="178" cy="355" r="1.2" fill={hexA(accent, 0.5)} stroke="none" />
        <path d="M116,390 L184,390 L184,393 L116,393 Z" fill={hexA(accent2, 0.08)} />
        <line x1="112" y1="390" x2="188" y2="390" />
      </g>

      {/* date palm sprig (bottom-left) */}
      <g stroke={hexA(color, 0.5)} strokeWidth={1.15 * k} fill="none" strokeLinejoin="round" strokeLinecap="round">
        <path d="M10,392 Q20,373 41,353" />
        {leaves.map((d, i) => (
          <path key={`eidm-l${i}`} d={d} />
        ))}
        <ellipse cx="10.5" cy="389.5" rx="1.4" ry="2" fill={hexA(accent, 0.5)} stroke="none" />
        <ellipse cx="13.2" cy="391.4" rx="1.4" ry="2" fill={hexA(accent, 0.5)} stroke="none" />
        <ellipse cx="10.8" cy="392.8" rx="1.4" ry="2" fill={hexA(accent, 0.5)} stroke="none" />
        <ellipse cx="14" cy="393.6" rx="1.4" ry="2" fill={hexA(accent, 0.5)} stroke="none" />
      </g>

      {/* geometric arabesque (khatam) star (bottom-right) */}
      <g stroke={hexA(color, 0.5)} strokeWidth={1.15 * k} fill="none" strokeLinejoin="round" strokeLinecap="round">
        <path d="M270,345 L272.5,354 L280.6,349.4 L276,357.5 L285,360 L276,362.5 L280.6,370.6 L272.5,366 L270,375 L267.5,366 L259.4,370.6 L264,362.5 L255,360 L264,357.5 L259.4,349.4 L267.5,354 Z" fill={hexA(accent, 0.1)} />
        <path d="M270,356 L272.8,357.2 L274,360 L272.8,362.8 L270,364 L267.2,362.8 L266,360 L267.2,357.2 Z" stroke={hexA(accent, 0.5)} />
        <circle cx="270" cy="360" r="1.4" fill={hexA(accent, 0.5)} stroke="none" />
      </g>

      {/* night-sky star scatter */}
      <g stroke="none">
        {stars.map((s, i) => (
          <path key={`eidm-s${i}`} d={spark(s.x, s.y, s.r)} fill={cols[i % 3]} />
        ))}
      </g>
    </svg>
  );
}

// ─── LunarMotif — lunar
function LunarMotif({ color, accent, accent2, scale = 300 }) {
  const ink = color;

  const stroke = hexA(ink, 0.55);
  const strokeSoft = hexA(ink, 0.34);
  const petal = hexA(accent, 0.5);
  const petalSoft = hexA(accent, 0.4);
  const hub = hexA(accent2, 0.62);
  const goldCap = hexA(accent2, 0.5);

  const blossom = (key, cx, cy, r, fill) => {
    const pet = [0, 1, 2, 3, 4].map((i) => {
      const deg = i * 72 - 90;
      const a = (deg * Math.PI) / 180;
      return {
        i,
        deg,
        px: cx + Math.cos(a) * r * 0.5,
        py: cy + Math.sin(a) * r * 0.5,
        sx: cx + Math.cos(a) * r * 0.26,
        sy: cy + Math.sin(a) * r * 0.26,
      };
    });
    return (
      <g key={key}>
        {pet.map((p) => (
          <ellipse
            key={"p" + p.i}
            cx={p.px}
            cy={p.py}
            rx={r * 0.55}
            ry={r * 0.36}
            transform={`rotate(${p.deg} ${p.px} ${p.py})`}
            fill={fill}
            stroke={stroke}
            strokeWidth="0.5"
          />
        ))}
        <circle cx={cx} cy={cy} r={r * 0.3} fill={hub} stroke={stroke} strokeWidth="0.4" />
        {pet.map((p) => (
          <circle key={"s" + p.i} cx={p.sx} cy={p.sy} r={r * 0.07} fill={stroke} />
        ))}
      </g>
    );
  };

  const crackers = [
    { x: 264, y: 22, h: 17 },
    { x: 271, y: 19, h: 20 },
    { x: 278, y: 23, h: 16 },
    { x: 267, y: 41, h: 15 },
    { x: 275, y: 43, h: 16 },
  ];

  const spark = [0, 60, 120, 180, 240, 300];

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <radialGradient id="lunar-glow" cx="0.42" cy="0.36" r="0.7">
          <stop offset="0" stopColor={hexA(accent, 0.55)} />
          <stop offset="1" stopColor={hexA(accent, 0.42)} />
        </radialGradient>
        <linearGradient id="lunar-gold" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={hexA(accent2, 0.6)} />
          <stop offset="1" stopColor={hexA(accent2, 0.32)} />
        </linearGradient>
      </defs>

      {/* ---- top-left: hanging lantern ---- */}
      <g>
        <line x1="30" y1="3" x2="30" y2="17" stroke={stroke} strokeWidth="0.8" />
        <rect x="27" y="14" width="6" height="4" rx="1" fill={goldCap} stroke={stroke} strokeWidth="0.6" />
        <path d="M22,25 L24,18 L36,18 L38,25 Z" fill={goldCap} stroke={stroke} strokeWidth="0.7" />
        <path
          d="M17,39 C17,28 22,24 30,24 C38,24 43,28 43,39 C43,50 38,55 30,55 C22,55 17,50 17,39 Z"
          fill="url(#lunar-glow)"
          stroke={stroke}
          strokeWidth="0.9"
        />
        <path d="M30,25 L30,54" stroke={hexA(accent, 0.42)} strokeWidth="0.6" fill="none" />
        <path d="M23,27 C20,35 20,44 23,52" fill="none" stroke={hexA(accent, 0.42)} strokeWidth="0.6" />
        <path d="M37,27 C40,35 40,44 37,52" fill="none" stroke={hexA(accent, 0.42)} strokeWidth="0.6" />
        <path d="M22,54 L24,60 L36,60 L38,54 Z" fill={goldCap} stroke={stroke} strokeWidth="0.7" />
        <circle cx="30" cy="62" r="2.2" fill={hub} stroke={stroke} strokeWidth="0.5" />
        {[-4, -2, 0, 2, 4].map((o) => (
          <line
            key={"lt" + o}
            x1="30"
            y1="63"
            x2={30 + o}
            y2={93 - Math.abs(o)}
            stroke={hexA(accent2, 0.5)}
            strokeWidth="0.7"
          />
        ))}
      </g>

      {/* ---- top edge: plum blossom sprig ---- */}
      <g>
        <path d="M41,41 C58,31 76,22 99,16" fill="none" stroke={hexA(accent, 0.5)} strokeWidth="1.1" />
        <path d="M68,26 C73,21 80,19 87,20" fill="none" stroke={hexA(accent, 0.42)} strokeWidth="0.9" />
        {blossom("b1", 99, 15, 6.5, petal)}
        {blossom("b2", 72, 24, 5.5, petal)}
        {blossom("b3", 52, 34, 4.5, petalSoft)}
        <circle cx="88" cy="19" r="2.2" fill={petalSoft} stroke={stroke} strokeWidth="0.4" />
      </g>

      {/* ---- top-right: firecracker string ---- */}
      <g>
        <path d="M271,18 C273,12 277,9 281,6" fill="none" stroke={hexA(accent2, 0.55)} strokeWidth="0.8" />
        {spark.map((a) => {
          const rad = (a * Math.PI) / 180;
          return (
            <line
              key={"sp" + a}
              x1="281"
              y1="6"
              x2={281 + Math.cos(rad) * 4}
              y2={6 + Math.sin(rad) * 4}
              stroke={hexA(accent2, 0.6)}
              strokeWidth="0.8"
            />
          );
        })}
        <circle cx="271" cy="18" r="2.4" fill={hexA(accent, 0.5)} stroke={stroke} strokeWidth="0.6" />
        {crackers.map((c, i) => (
          <g key={"fc" + i}>
            <rect x={c.x - 3} y={c.y} width="6" height={c.h} rx="2.5" fill={hexA(accent, 0.5)} stroke={stroke} strokeWidth="0.7" />
            <line x1={c.x - 3} y1={c.y + 3} x2={c.x + 3} y2={c.y + 3} stroke={hexA(accent2, 0.55)} strokeWidth="0.7" />
            <line x1={c.x} y1={c.y + c.h} x2={c.x} y2={c.y + c.h + 2.5} stroke={hexA(accent2, 0.45)} strokeWidth="0.6" />
          </g>
        ))}
      </g>

      {/* ---- bottom-left: coin + gold ingot ---- */}
      <g>
        <circle cx="28" cy="336" r="10" fill="url(#lunar-gold)" stroke={stroke} strokeWidth="0.9" />
        <circle cx="28" cy="336" r="7.4" fill="none" stroke={hexA(accent, 0.4)} strokeWidth="0.5" />
        <rect x="24" y="332" width="8" height="8" rx="1" fill={hexA(ink, 0.18)} stroke={stroke} strokeWidth="0.7" />

        <path
          d="M15,373 C15,368 19,364 24,362 C21,360 21,356 25,356 C28,356 28,360 30,362 C32,361 38,361 40,362 C42,360 42,356 45,356 C49,356 49,360 46,362 C51,364 55,368 55,373 C55,378 46,379 35,379 C24,379 15,378 15,373 Z"
          fill="url(#lunar-gold)"
          stroke={stroke}
          strokeWidth="0.9"
        />
        <path d="M27,361 C27,356 43,356 43,361 C43,365 27,365 27,361 Z" fill={goldCap} stroke={stroke} strokeWidth="0.6" />
        <path d="M29,360 C33,358 37,358 41,360" fill="none" stroke={hexA(accent2, 0.5)} strokeWidth="0.5" />
      </g>

      {/* ---- bottom-right: lucky knot with tassel ---- */}
      <g>
        <circle cx="271" cy="331" r="2.4" fill="none" stroke={stroke} strokeWidth="0.8" />
        <line x1="271" y1="333" x2="271" y2="341" stroke={stroke} strokeWidth="0.8" />
        <rect x="260" y="344" width="22" height="12" rx="6" transform="rotate(45 271 350)" fill="none" stroke={hexA(accent, 0.55)} strokeWidth="2.4" />
        <rect x="260" y="344" width="22" height="12" rx="6" transform="rotate(-45 271 350)" fill="none" stroke={hexA(accent, 0.55)} strokeWidth="2.4" />
        <rect x="267" y="346" width="8" height="8" rx="2" transform="rotate(45 271 350)" fill={hexA(accent, 0.4)} stroke={stroke} strokeWidth="0.6" />
        <circle cx="271" cy="362" r="2.2" fill={hub} stroke={stroke} strokeWidth="0.5" />
        {[-3.5, -1.2, 1.2, 3.5].map((o) => (
          <line
            key={"kt" + o}
            x1="271"
            y1="363"
            x2={271 + o}
            y2={382 - Math.abs(o)}
            stroke={hexA(accent, 0.5)}
            strokeWidth="0.8"
          />
        ))}
      </g>

      {/* ---- side-margin blossom scatter ---- */}
      {blossom("sc-l", 27, 205, 6, petal)}
      {blossom("sc-r", 274, 210, 5.5, petalSoft)}
      <circle cx="34" cy="186" r="2.2" fill={petalSoft} stroke={hexA(accent, 0.4)} strokeWidth="0.4" />
      <circle cx="266" cy="228" r="2" fill={petalSoft} stroke={hexA(accent, 0.4)} strokeWidth="0.4" />
    </svg>
  );
}

// ─── StPatricksDayMotif — stpats
function StPatricksDayMotif({ color, accent, accent2, scale = 300 }) {
  const GOLD = "#C79A3B";
  const FOAM = "#F0E9D8";
  const leaf =
    "M0 0 C -6 -3 -11 -10 -6 -14 C -3.5 -16.5 -0.5 -14.5 0 -11 C 0.5 -14.5 3.5 -16.5 6 -14 C 11 -10 6 -3 0 0 Z";

  const clover = (k, x, y, sc, rot) => (
    <g key={k} transform={`translate(${x} ${y}) rotate(${rot}) scale(${sc})`}>
      {[0, 120, 240].map((a) => (
        <g key={a} transform={`rotate(${a})`}>
          <path d={leaf} fill={hexA(color, 0.5)} stroke={hexA(color, 0.62)} strokeWidth={1} />
          <path d="M0 -1 L0 -11.5" fill="none" stroke={hexA(GOLD, 0.5)} strokeWidth={0.9} strokeLinecap="round" />
        </g>
      ))}
      <path d="M0 3 C 2.5 8 -2 13 0.6 19" fill="none" stroke={hexA(color, 0.6)} strokeWidth={1.6} strokeLinecap="round" />
    </g>
  );

  const shoeArc = "M -9 -10 C -12.6 -6 -12.6 4 -8 9 C -4.5 12.6 4.5 12.6 8 9 C 12.6 4 12.6 -6 9 -10";
  const studs = [[0, 11], [-4.6, 9.7], [4.6, 9.7], [-9.4, 5], [9.4, 5], [-11.7, -1.4], [11.7, -1.4]];
  const horseshoe = (k, x, y, sc, rot) => (
    <g key={k} transform={`translate(${x} ${y}) rotate(${rot}) scale(${sc})`}>
      <path d={shoeArc} fill="none" stroke={hexA(color, 0.5)} strokeWidth={5} strokeLinecap="round" />
      <path d={shoeArc} fill="none" stroke={hexA(GOLD, 0.5)} strokeWidth={1.4} strokeLinecap="round" />
      <circle cx={-9} cy={-10} r={1.6} fill={hexA(GOLD, 0.72)} />
      <circle cx={9} cy={-10} r={1.6} fill={hexA(GOLD, 0.72)} />
      {studs.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r={0.95} fill={hexA(GOLD, 0.72)} />
      ))}
    </g>
  );

  const coin = (k, x, y, r) => (
    <g key={k}>
      <circle cx={x} cy={y} r={r} fill="url(#stpats_gold)" stroke={hexA(GOLD, 0.75)} strokeWidth={0.7} />
      <path
        d={`M ${x - r * 0.45} ${y - r * 0.2} A ${r * 0.6} ${r * 0.6} 0 0 1 ${x + r * 0.35} ${y - r * 0.6}`}
        fill="none"
        stroke={hexA(FOAM, 0.55)}
        strokeWidth={0.5}
      />
    </g>
  );

  const potCoins = [[0, -9], [-6, -8], [6, -8], [-3, -12.5], [3, -12.5], [0, -15], [12, -2.5], [-11, -1.5]];
  const pot = (k, x, y, sc) => (
    <g key={k} transform={`translate(${x} ${y}) scale(${sc})`}>
      <path d="M -14 -5 C -17 7 -9 16 0 16 C 9 16 17 7 14 -5 Z" fill={hexA(color, 0.4)} stroke={hexA(color, 0.6)} strokeWidth={1.2} />
      <path d="M -15.5 -6 C -15.5 -10.5 15.5 -10.5 15.5 -6 C 15.5 -1.5 -15.5 -1.5 -15.5 -6 Z" fill={hexA(color, 0.5)} stroke={hexA(color, 0.62)} strokeWidth={1} />
      <ellipse cx={0} cy={-6} rx={12} ry={3} fill={hexA(color, 0.6)} />
      {potCoins.map((c, i) => (
        <g key={i}>
          <circle cx={c[0]} cy={c[1]} r={3.2} fill="url(#stpats_gold)" stroke={hexA(GOLD, 0.75)} strokeWidth={0.7} />
          <path
            d={`M ${c[0] - 1.4} ${c[1] - 0.6} A 1.8 1.8 0 0 1 ${c[0] + 1} ${c[1] - 1.8}`}
            fill="none"
            stroke={hexA(FOAM, 0.55)}
            strokeWidth={0.5}
          />
        </g>
      ))}
    </g>
  );

  const stout = (k, x, y, sc, rot) => (
    <g key={k} transform={`translate(${x} ${y}) rotate(${rot}) scale(${sc})`}>
      <path d="M -9.4 -11.5 C -3 -9 3 -9 9.4 -11.5 L 8.2 15.6 C 8 17.6 -8 17.6 -8.2 15.6 Z" fill={hexA(color, 0.24)} />
      <path d="M -11 -20 L -8.4 16 C -8.4 18.6 8.4 18.6 8.4 16 L 11 -20" fill="none" stroke={hexA(color, 0.55)} strokeWidth={1.4} strokeLinejoin="round" />
      <path
        d="M -11 -19 C -12.5 -25 -5 -26 -2.5 -22 C 0.5 -26 7 -25 4.5 -21 C 9 -25 13 -21 11 -18.5 C 11.6 -14 10 -11 8 -11 C 2 -8.5 -2 -8.5 -8 -11 C -10 -11 -11.6 -14 -11 -19 Z"
        fill={hexA(FOAM, 0.55)}
        stroke={hexA(color, 0.5)}
        strokeWidth={1}
      />
      <path d="M -6.5 -8 C -7.5 0 -7.5 8 -6.5 14" fill="none" stroke={hexA(FOAM, 0.4)} strokeWidth={1.2} strokeLinecap="round" />
    </g>
  );

  const radii = [104, 98, 92, 86, 80];
  const bandColor = [GOLD, accent2, accent, color, GOLD];
  const ang = (64 * Math.PI) / 180;
  const sn = Math.sin(ang);
  const cs = Math.cos(ang);

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden
      viewBox="0 0 300 400"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <radialGradient id="stpats_gold" cx="0.4" cy="0.35" r="0.75">
          <stop offset="0" stopColor={hexA("#F2D98A", 0.85)} />
          <stop offset="0.55" stopColor={hexA(GOLD, 0.75)} />
          <stop offset="1" stopColor={hexA("#8A6420", 0.7)} />
        </radialGradient>
      </defs>
      {radii.map((r, i) => (
        <path
          key={r}
          d={`M ${150 - r * sn} ${120 - r * cs} A ${r} ${r} 0 0 1 ${150 + r * sn} ${120 - r * cs}`}
          fill="none"
          stroke={hexA(bandColor[i], 0.5)}
          strokeWidth={4.4}
          strokeLinecap="round"
        />
      ))}
      {clover("cl-main", 31, 54, 0.95, -12)}
      {clover("cl-small", 57, 90, 0.5, 22)}
      {horseshoe("shoe", 270, 50, 0.92, -6)}
      {coin("c-shoe", 249, 80, 3)}
      {pot("pot", 47, 354, 1.05)}
      {coin("c-p1", 75, 373, 3.4)}
      {coin("c-p2", 31, 378, 3)}
      {coin("c-p3", 62, 386, 2.7)}
      {stout("stout", 256, 346, 0.94, 6)}
      {coin("c-stout", 236, 380, 3)}
      {clover("cl-btm", 150, 379, 0.5, -6)}
    </svg>
  );
}

// ─── CincoMotif — cinco
function CincoMotif({ color, accent, accent2, scale = 300 }) {
  const P = "cinco5";
  const greenSpot = accent2;
  const redSpot = accent;

  // ---- papel picado banner (top swag) ----
  const sagY = (x) => 12 + 32 * (x / 300) * (1 - x / 300);
  const flags = Array.from({ length: 10 }, (_, i) => {
    const cx = 15 + i * 30;
    const yt = sagY(cx);
    const w = 12.5, h = 22, yb = yt + h, teeth = 4, span = (2 * w) / teeth;
    let d = `M${(cx - w).toFixed(1)},${yt.toFixed(1)} L${(cx + w).toFixed(1)},${yt.toFixed(1)} L${(cx + w).toFixed(1)},${(yb - 5).toFixed(1)}`;
    for (let k = 0; k < teeth; k++) {
      const x0 = cx + w - k * span;
      d += ` L${(x0 - span / 2).toFixed(1)},${yb.toFixed(1)} L${(x0 - span).toFixed(1)},${(yb - 5).toFixed(1)}`;
    }
    d += " Z";
    return { d, fill: i % 2 === 0 ? accent : accent2, key: i };
  });

  // ---- marigold / cempasuchil builder ----
  const marigold = (cx, cy, R, kp) => {
    const outer = Array.from({ length: 12 }, (_, i) => {
      const a = (i / 12) * Math.PI * 2;
      return { x: cx + Math.cos(a) * R * 0.6, y: cy + Math.sin(a) * R * 0.6, rot: (a * 180) / Math.PI, key: `${kp}o${i}` };
    });
    const mid = Array.from({ length: 10 }, (_, i) => {
      const a = (i / 10) * Math.PI * 2 + 0.31;
      return { x: cx + Math.cos(a) * R * 0.34, y: cy + Math.sin(a) * R * 0.34, rot: (a * 180) / Math.PI, key: `${kp}m${i}` };
    });
    return (
      <g key={kp}>
        {outer.map((p) => (
          <ellipse key={p.key} cx={p.x} cy={p.y} rx={R * 0.46} ry={R * 0.26}
            transform={`rotate(${p.rot} ${p.x} ${p.y})`}
            fill={hexA(accent, 0.5)} stroke={hexA(color, 0.22)} strokeWidth="0.5" />
        ))}
        {mid.map((p) => (
          <ellipse key={p.key} cx={p.x} cy={p.y} rx={R * 0.32} ry={R * 0.18}
            transform={`rotate(${p.rot} ${p.x} ${p.y})`}
            fill={hexA(accent, 0.62)} />
        ))}
        <circle cx={cx} cy={cy} r={R * 0.3} fill={hexA(accent2, 0.58)} />
        <circle cx={cx} cy={cy} r={R * 0.14} fill={hexA(color, 0.38)} />
      </g>
    );
  };

  // ---- maraca builder ----
  const maraca = (cx, cy, rot, kp) => (
    <g key={kp} transform={`rotate(${rot} ${cx} ${cy})`}>
      <rect x={cx - 1.8} y={cy + 8} width="3.6" height="15" rx="1.8" fill={hexA(color, 0.5)} stroke={hexA(color, 0.3)} strokeWidth="0.5" />
      <circle cx={cx} cy={cy + 24} r="2.4" fill={hexA(color, 0.55)} />
      <ellipse cx={cx} cy={cy} rx="10" ry="11" fill={hexA(accent2, 0.5)} stroke={hexA(color, 0.4)} strokeWidth="0.8" />
      <path d={`M${cx - 9},${cy + 1.5} Q${cx},${cy + 5} ${cx + 9},${cy + 1.5}`} fill="none" stroke={hexA(color, 0.32)} strokeWidth="0.8" />
      {[[-4, -3], [4, -3], [0, 3]].map((p, di) => (
        <circle key={`${kp}d${di}`} cx={cx + p[0]} cy={cy + p[1]} r="1.7" fill={hexA(accent, 0.58)} />
      ))}
    </g>
  );

  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden
      viewBox="0 0 300 400" preserveAspectRatio="xMidYMid slice">
      <defs>
        <mask id={`${P}cut`} maskContentUnits="objectBoundingBox">
          <rect x="0" y="0" width="1" height="1" fill="white" />
          <circle cx="0.5" cy="0.32" r="0.13" fill="black" />
          <circle cx="0.28" cy="0.52" r="0.08" fill="black" />
          <circle cx="0.72" cy="0.52" r="0.08" fill="black" />
          <circle cx="0.5" cy="0.63" r="0.07" fill="black" />
        </mask>
      </defs>

      {/* papel picado banner */}
      <path d="M-2,10 Q150,28 302,10" fill="none" stroke={hexA(color, 0.5)} strokeWidth="1" />
      {flags.map((f) => (
        <path key={f.key} d={f.d} fill={hexA(f.fill, 0.55)} stroke={hexA(color, 0.16)} strokeWidth="0.5" mask={`url(#${P}cut)`} />
      ))}

      {/* marigold - top left */}
      {marigold(31, 74, 14, "mgA")}

      {/* chili pepper - top right */}
      <g>
        <path d="M264,59 C259,66 261,80 269,88 C271,90 274,89 274,86 C278,77 276,65 270,59 C268,57 266,57 264,59 Z"
          fill={hexA(redSpot, 0.5)} stroke={hexA(color, 0.3)} strokeWidth="0.8" />
        <path d="M267,66 C266,73 267,80 270,85" fill="none" stroke={hexA(color, 0.22)} strokeWidth="0.7" />
        <path d="M263,59 L267,55 L271,59 Z" fill={hexA(greenSpot, 0.6)} />
        <path d="M267,57 Q266,51 270,50" fill="none" stroke={hexA(greenSpot, 0.62)} strokeWidth="2" strokeLinecap="round" />
      </g>

      {/* cactus in a pot - left edge */}
      <g>
        <rect x="20" y="186" width="8" height="62" rx="4" fill={hexA(greenSpot, 0.5)} />
        <rect x="12" y="210" width="11" height="6" rx="3" fill={hexA(greenSpot, 0.5)} />
        <rect x="12" y="199" width="6" height="14" rx="3" fill={hexA(greenSpot, 0.5)} />
        <rect x="24" y="205" width="10" height="6" rx="3" fill={hexA(greenSpot, 0.5)} />
        <rect x="30" y="192" width="6" height="16" rx="3" fill={hexA(greenSpot, 0.5)} />
        <line x1="23" y1="194" x2="23" y2="242" stroke={hexA(color, 0.2)} strokeWidth="0.7" />
        <line x1="25.5" y1="197" x2="25.5" y2="240" stroke={hexA(color, 0.16)} strokeWidth="0.7" />
        {Array.from({ length: 5 }, (_, i) => {
          const a = (i / 5) * Math.PI * 2;
          return <circle key={`cb${i}`} cx={24 + Math.cos(a) * 2.6} cy={185 + Math.sin(a) * 2.6} r="1.7" fill={hexA(accent2, 0.6)} />;
        })}
        <circle cx="24" cy="185" r="1.6" fill={hexA(accent, 0.62)} />
        <path d="M15,250 L33,250 L31,268 L17,268 Z" fill={hexA(accent, 0.5)} stroke={hexA(color, 0.35)} strokeWidth="0.8" />
        <rect x="13" y="245" width="22" height="6" rx="1.5" fill={hexA(accent, 0.6)} stroke={hexA(color, 0.35)} strokeWidth="0.7" />
      </g>

      {/* maracas - right edge */}
      {maraca(270, 196, -16, "mrA")}
      {maraca(286, 201, 15, "mrB")}

      {/* sombrero - bottom right */}
      <g>
        <ellipse cx="250" cy="363" rx="25" ry="6.5" fill={hexA(color, 0.42)} stroke={hexA(color, 0.5)} strokeWidth="0.8" />
        <path d="M226,362 Q250,372 274,362" fill="none" stroke={hexA(color, 0.45)} strokeWidth="0.8" />
        <path d="M242,362 Q242,347 250,347 Q258,347 258,362 Z" fill={hexA(accent, 0.5)} stroke={hexA(color, 0.35)} strokeWidth="0.7" />
        <rect x="242" y="357" width="16" height="3.5" rx="1" fill={hexA(accent2, 0.6)} />
        {[[238, 364], [250, 366], [262, 364]].map((p, di) => (
          <circle key={`sm${di}`} cx={p[0]} cy={p[1]} r="1.4" fill={hexA(accent, 0.55)} />
        ))}
      </g>

      {/* small marigold - bottom left */}
      {marigold(35, 360, 11, "mgB")}
    </svg>
  );
}

export const MOTIFS = {
  'diwali': DiwaliMotif,
  'eid': EidMotif,
  'lunar': LunarMotif,
  'stpats': StPatricksDayMotif,
  'cinco': CincoMotif,
  'wedding': WeddingMotif,
  'bridal': BridalShowerMotif,
  'bachelor': BachelorMotif,
  'birthday': BirthdayMotif,
  'firstbday': FirstBirthdayMotif,
  'baby': BabyShowerMotif,
  'genderreveal': GenderRevealMotif,
  'pool': PoolPartyMotif,
  'bbq': BbqCookoutMotif,
  'picnic': PicnicMotif,
  'graduation': GraduationLaurelMotif,
  'christmas': ChristmasMotif,
  'halloween': HalloweenMotif,
  'nye': NyeMidnightMotif,
  'thanksgiving': ThanksgivingHarvestMotif,
  'patriotic': PatrioticMotif,
  'easter': EasterMotif,
  'hanukkah': HanukkahMotif,
  'mitzvah': MitzvahMotif,
  'quince': QuinceMotif,
  'cocktail': CocktailHourMotif,
  'wine': WineTastingMotif,
  'dinner': DinnerBrunchMotif,
  'tea': TeaPartyMotif,
  'corporate': CorporateRetirementMotif,
  'sports': GameDayMotif,
  'gamenight': GameNightMotif,
  'luau': LuauMotif,
  'pets': PetsMotif,
  'memorial': MemorialGraceMotif,
};

export const MOTIF_KEYS = Object.keys(MOTIFS);

/** Render a named occasion motif pack, or nothing if the key is unknown. */
export default function OccasionMotif({ name, color, accent, accent2, scale = 300 }) {
  const Cmp = name && MOTIFS[name];
  if (!Cmp) return null;
  return <Cmp color={color} accent={accent} accent2={accent2} scale={scale} />;
}
